import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { enqueueSnackbar } from 'notistack';
import { Node } from 'reactflow';
import { workflowService } from '../../services/workflow.service';
import { WorkflowCredential } from '../../types/workflow.types';

type NodeData = {
  label: string;
  nodeType: string;
  parameters?: any;
  workflowId?: string;
  webhookSecret?: string;
  description?: string;
  isImported?: boolean;
  parametersSchema?: any;
  uiSchema?: any;
  importedProperties?: any[];
};

function safeJsonParse(value: string) {
  if (!value.trim()) return {};
  try {
    return JSON.parse(value);
  } catch (e: any) {
    throw new Error(e?.message || 'Invalid JSON');
  }
}

/** Read value from a plain field OR an n8n __rl resource-locator object. */
function getRlValue(param: any): string {
  if (param && typeof param === 'object' && param.__rl) return String(param.value ?? '');
  return String(param ?? '');
}

/**
 * Inline editor for the Odoo node's ``values`` parameter (used by
 * ``create`` / ``update``). Pulls the key suggestions from the cached
 * ``fields_get`` schema so users don't have to remember technical field
 * names, but still allows free-text entry for custom keys. Values are
 * kept as plain strings — the executor's renderer handles ``{{ ... }}``
 * and the ``={{ ... }}`` typed-prefix.
 */
const OdooValuesEditor: React.FC<{
  values: Record<string, any>;
  onChange: (next: Record<string, any>) => void;
  fieldOptions: Array<{ name: string; label: string; type: string }>;
  readOnly?: boolean;
}> = ({ values, onChange, fieldOptions, readOnly }) => {
  const rows = useMemo(
    () =>
      Object.entries(values || {}).map(([key, value]) => ({
        key,
        value: typeof value === 'string' ? value : JSON.stringify(value),
      })),
    [values]
  );

  const commit = (next: Array<{ key: string; value: string }>) => {
    const obj: Record<string, any> = {};
    for (const row of next) {
      const k = (row.key || '').trim();
      if (!k) continue;
      obj[k] = row.value;
    }
    onChange(obj);
  };

  const update = (idx: number, patch: Partial<{ key: string; value: string }>) => {
    const next = rows.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    commit(next);
  };

  const remove = (idx: number) => commit(rows.filter((_, i) => i !== idx));
  const add = () => commit([...rows, { key: '', value: '' }]);

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Values
      </Typography>
      <Stack spacing={1}>
        {rows.length === 0 && (
          <Typography variant="caption" color="text.secondary">
            No values yet. Click "Add value" to start.
          </Typography>
        )}
        {rows.map((row, idx) => (
          <Box key={`odoo-val-${idx}`} display="flex" gap={1} alignItems="center">
            <Autocomplete
              size="small"
              freeSolo
              options={fieldOptions}
              getOptionLabel={(opt) =>
                typeof opt === 'string' ? opt : opt.name || ''
              }
              value={row.key}
              onInputChange={(_e, v) => update(idx, { key: v })}
              renderOption={(props, opt) => (
                <li {...props} key={opt.name}>
                  <Box>
                    <Typography variant="body2">{opt.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {opt.label} · {opt.type}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(p) => <TextField {...p} label="Field" />}
              sx={{ flex: 2 }}
              disabled={readOnly}
            />
            <TextField
              label="Value"
              size="small"
              fullWidth
              value={row.value}
              onChange={(e) => update(idx, { value: e.target.value })}
              disabled={readOnly}
              sx={{ flex: 3 }}
            />
            <IconButton
              size="small"
              onClick={() => remove(idx)}
              disabled={readOnly}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        ))}
      </Stack>
      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={add}
        disabled={readOnly}
        sx={{ mt: 1 }}
      >
        Add value
      </Button>
    </Box>
  );
};

/** Write a new string value back, preserving the __rl wrapper if present. */
function setRlValue(param: any, newValue: string): any {
  if (param && typeof param === 'object' && param.__rl) {
    return { ...param, value: newValue, cachedResultName: newValue };
  }
  return newValue;
}

const SCHEDULE_MONTHS = [
  { value: '*', label: 'Every month' },
  { value: '1', label: 'January' },
  { value: '2', label: 'February' },
  { value: '3', label: 'March' },
  { value: '4', label: 'April' },
  { value: '5', label: 'May' },
  { value: '6', label: 'June' },
  { value: '7', label: 'July' },
  { value: '8', label: 'August' },
  { value: '9', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

const SCHEDULE_WEEKDAYS = [
  { value: '*', label: 'Every day' },
  { value: '0', label: 'Sunday' },
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
];

const parseCronStep = (raw: string, fallback = 1) => {
  const value = String(raw || '').trim();
  if (!value || value === '*') return 1;
  if (value.startsWith('*/')) {
    const n = Number(value.slice(2));
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : fallback;
  }
  return fallback;
};

const toCronStep = (interval: number) => {
  const n = Number(interval);
  if (!Number.isFinite(n) || n <= 1) return '*';
  return `*/${Math.floor(n)}`;
};

// --------------------------------------------------------------------------
// Webhook-trigger detection.
//
// All of these node types should expose the same runnable WerkGang webhook
// URL — `/webhook/{workflowId}/{nodeId}/{webhookSecret}/` — and the same
// HTTP-method / auth UI. New imports get rewritten to `trigger.webhook` by
// the backend, but legacy rows in the DB may still carry the original n8n
// type, so we treat them as equivalent on the frontend too.
// --------------------------------------------------------------------------
const WEBHOOK_TRIGGER_TYPES = new Set<string>([
  'trigger.webhook',
  'n8n-nodes-base.webhook',
  'n8n-nodes-base.webhookV1',
  'n8n-nodes-base.webhookV2',
  'n8n-nodes-base.webhookv1',
  'n8n-nodes-base.webhookv2',
  'n8n-nodes-base.formTrigger',
  'n8n-nodes-base.chatTrigger',
  '@n8n/n8n-nodes-langchain.chatTrigger',
]);

const isWebhookTriggerType = (t: string): boolean => WEBHOOK_TRIGGER_TYPES.has(t || '');

// --------------------------------------------------------------------------
// GenericParamsEditor
//
// Fallback parameter editor for imported nodes (n8n-nodes-base.*, @n8n/*) that
// don't yet have a dedicated UI panel in this inspector. It walks the node's
// parameter dict and renders an editable field per key:
//   * string / number / boolean  -> typed text or switch
//   * object / array             -> JSON textarea (parsed on blur)
//
// Hidden plumbing keys (those starting with "_") and the convenience tags we
// stash during import (`note`, `n8n_type`) are read-only / informational.
//
// This means a freshly-imported n8n node always shows ALL of its parameters
// as editable inputs, instead of an empty inspector. The compatibility
// executor on the backend continues to read them by their original n8n names.
// --------------------------------------------------------------------------
const GenericParamsEditor: React.FC<{
  nodeType: string;
  params: Record<string, any>;
  setParam: (key: string, value: any) => void;
  readOnly?: boolean;
}> = ({ nodeType, params, setParam, readOnly }) => {
  const [jsonText, setJsonText] = React.useState<Record<string, string>>({});
  const [newKey, setNewKey] = React.useState('');

  const isPlainPrimitive = (v: any) =>
    v === null ||
    typeof v === 'string' ||
    typeof v === 'number' ||
    typeof v === 'boolean' ||
    typeof v === 'undefined';

  const orderedKeys = React.useMemo(() => {
    const all = Object.keys(params || {});
    const visible = all.filter((k) => !k.startsWith('_'));
    visible.sort((a, b) => {
      // Push our convenience tags ("note", "n8n_type") to the bottom.
      const tagA = a === 'note' || a === 'n8n_type';
      const tagB = b === 'note' || b === 'n8n_type';
      if (tagA && !tagB) return 1;
      if (!tagA && tagB) return -1;
      return a.localeCompare(b);
    });
    return visible;
  }, [params]);

  React.useEffect(() => {
    const next: Record<string, string> = {};
    for (const k of orderedKeys) {
      const v = params[k];
      if (!isPlainPrimitive(v)) {
        next[k] = JSON.stringify(v ?? (Array.isArray(v) ? [] : {}), null, 2);
      }
    }
    setJsonText(next);
  }, [orderedKeys.join('|')]); // eslint-disable-line react-hooks/exhaustive-deps

  const headline = nodeType.startsWith('@n8n/n8n-nodes-langchain.')
    ? nodeType.replace('@n8n/n8n-nodes-langchain.', '')
    : nodeType.startsWith('n8n-nodes-base.')
      ? nodeType.replace('n8n-nodes-base.', '')
      : nodeType;

  return (
    <Box>
      <Box
        sx={{
          mb: 2,
          p: 1.25,
          borderRadius: 1,
          bgcolor: 'rgba(78, 205, 196, 0.08)',
          border: '1px solid rgba(78, 205, 196, 0.35)',
        }}
      >
        <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
          Imported n8n node — {headline}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          All original n8n parameters are editable below. Use{' '}
          <code>{'{{ $json.field }}'}</code> or <code>{'={{ expression }}'}</code> to reference
          incoming data.
        </Typography>
      </Box>

      {orderedKeys.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This node has no parameters yet. Add one below.
        </Typography>
      )}

      {orderedKeys.map((k) => {
        const v = params[k];
        const isMeta = k === 'note' || k === 'n8n_type';

        if (typeof v === 'boolean') {
          return (
            <FormControlLabel
              key={k}
              sx={{ mb: 1.5, display: 'block' }}
              control={
                <Switch
                  checked={!!v}
                  disabled={readOnly}
                  onChange={(e) => setParam(k, e.target.checked)}
                />
              }
              label={k}
            />
          );
        }

        if (typeof v === 'number') {
          return (
            <TextField
              key={k}
              label={k}
              size="small"
              type="number"
              fullWidth
              value={String(v)}
              disabled={readOnly}
              onChange={(e) => {
                const n = Number(e.target.value);
                setParam(k, Number.isFinite(n) ? n : e.target.value);
              }}
              sx={{ mb: 1.5 }}
            />
          );
        }

        if (isPlainPrimitive(v)) {
          const stringValue = String(v ?? '');
          const looksMultiline = stringValue.length > 120 || stringValue.includes('\n');
          return (
            <TextField
              key={k}
              label={k}
              size="small"
              fullWidth
              multiline={looksMultiline}
              minRows={looksMultiline ? 3 : undefined}
              value={stringValue}
              disabled={readOnly || isMeta}
              InputProps={isMeta ? { readOnly: true } : undefined}
              onChange={(e) => setParam(k, e.target.value)}
              sx={{ mb: 1.5 }}
            />
          );
        }

        return (
          <TextField
            key={k}
            label={`${k} (JSON)`}
            fullWidth
            multiline
            minRows={4}
            disabled={readOnly}
            value={jsonText[k] ?? JSON.stringify(v ?? {}, null, 2)}
            onChange={(e) => setJsonText((prev) => ({ ...prev, [k]: e.target.value }))}
            onBlur={() => {
              try {
                const parsed = JSON.parse(jsonText[k] ?? '');
                setParam(k, parsed);
              } catch (err: any) {
                enqueueSnackbar(`Invalid JSON for "${k}": ${err?.message || err}`, {
                  variant: 'error',
                });
                setJsonText((prev) => ({
                  ...prev,
                  [k]: JSON.stringify(v ?? {}, null, 2),
                }));
              }
            }}
            sx={{ mb: 1.5 }}
          />
        );
      })}

      {!readOnly && (
        <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            label="Add parameter"
            size="small"
            fullWidth
            value={newKey}
            placeholder="e.g. options.timeout"
            onChange={(e) => setNewKey(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const k = newKey.trim();
                if (k && !(k in params)) {
                  setParam(k, '');
                  setNewKey('');
                }
              }
            }}
          />
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => {
              const k = newKey.trim();
              if (k && !(k in params)) {
                setParam(k, '');
                setNewKey('');
              }
            }}
          >
            Add
          </Button>
        </Box>
      )}
    </Box>
  );
};

export const NodeInspector: React.FC<{
  node: Node<NodeData> | null;
  workflowId?: string;
  onChange: (next: { label?: string; parameters?: any }) => void;
  onDelete: () => void;
  readOnly?: boolean;
}> = ({ node, workflowId: workflowIdProp, onChange, onDelete, readOnly }) => {
  const nodeType = node?.data?.nodeType || '';
  const params = (node?.data?.parameters ?? {}) as Record<string, any>;

  // Hook order matters: declare paramsRef + sync effect at the very top of
  // the component so they run on every render (even when `node` is null and
  // we render the empty-state below). `paramsRef` lets setParam merge multi
  // updates atomically without stale-closure clobbering between consecutive
  // calls in the same handler.
  const paramsRef = useRef(params);
  useEffect(() => {
    paramsRef.current = params;
  }, [params]);

  const [unsupportedText, setUnsupportedText] = useState('');
  const [paramsText, setParamsText] = useState('');
  const [odooDomainText, setOdooDomainText] = useState('');
  const [odooFieldsText, setOdooFieldsText] = useState('');
  // Live introspection state for the Odoo node. The model list and field
  // schema are fetched from the user's Odoo instance via the credential
  // and cached per (credential_id, model) so we don't re-hit the network
  // every keystroke.
  const [odooModelOptions, setOdooModelOptions] = useState<
    Array<{ model: string; name: string }>
  >([]);
  const [odooModelsLoading, setOdooModelsLoading] = useState(false);
  const [odooModelsWarning, setOdooModelsWarning] = useState<string>('');
  const [odooModelQuery, setOdooModelQuery] = useState('');
  // How many models were returned by the most recent successful fetch.
  // Used to render the "Loaded N models from this Odoo instance." hint.
  // -1 = haven't fetched yet.
  const [odooModelsCount, setOdooModelsCount] = useState<number>(-1);
  const [odooModelsTruncated, setOdooModelsTruncated] = useState<boolean>(false);
  // Last credential we fetched models for, so we know to refetch when the
  // user picks a different Odoo credential.
  const [odooModelsLoadedFor, setOdooModelsLoadedFor] = useState<string>('');
  const [odooFieldOptions, setOdooFieldOptions] = useState<
    Array<{ name: string; label: string; type: string; required: boolean; readonly: boolean; help: string; relation: string }>
  >([]);
  const [odooFieldsLoading, setOdooFieldsLoading] = useState(false);
  const [odooFieldsWarning, setOdooFieldsWarning] = useState<string>('');
  // Power-user escape hatch: when ON, the domain builder collapses into a
  // raw-JSON textarea (used for polish-notation domains like ['|', ...]).
  const [odooDomainRawMode, setOdooDomainRawMode] = useState(false);
  const [importedJsonFields, setImportedJsonFields] = useState<Record<string, string>>({});
  const [jsonTextMap, setJsonTextMap] = useState<Record<string, string>>({});
  const [kvEditorMap, setKvEditorMap] = useState<Record<string, Array<{ key: string; value: string }>>>({});
  const [switchCases, setSwitchCases] = useState<Array<{ value: string }>>([]);
  const [sheetsValuesText, setSheetsValuesText] = useState('');
  const [smtpAttachments, setSmtpAttachments] = useState<
    Array<{ filename: string; content_type: string; content_base64: string }>
  >([]);
  const [qdrantVectorText, setQdrantVectorText] = useState('');
  const [qdrantPoints, setQdrantPoints] = useState<
    Array<{ id: string; vector: string; payload: Array<{ key: string; value: string }> }>
  >([]);
  const [driveFiles, setDriveFiles] = useState<Array<{ id: string; name: string; mimeType: string }>>([]);
  const [driveLoading, setDriveLoading] = useState(false);
  const [sheetFiles, setSheetFiles] = useState<Array<{ id: string; name: string }>>([]);
  const [sheetTabs, setSheetTabs] = useState<Array<{ title: string }>>([]);
  const [sheetsLoading, setSheetsLoading] = useState(false);
  const [calendarList, setCalendarList] = useState<Array<{ id: string; summary: string; primary: boolean }>>([]);
  const [calendarsLoading, setCalendarsLoading] = useState(false);
  const [googleError, setGoogleError] = useState('');
  const [workflowCredentials, setWorkflowCredentials] = useState<WorkflowCredential[]>([]);
  const [credentialsLoading, setCredentialsLoading] = useState(false);
  const [newParamKey, setNewParamKey] = useState('');

  const isKnownEditorType = (() => {
    const known = new Set([
      'trigger.webhook',
      'trigger.schedule',
      'trigger.manual',
      'action.http',
      'action.email',
      'action.database',
      'action.postgresql',
      'action.supabase',
      'action.gmail',
      'action.google_sheet',
      'action.google_drive',
      'action.smtp',
      'action.imap',
      'action.mqtt_publish',
      'action.qdrant',
      'action.llm.chat',
      'action.odoo',
      'action.webhook.respond',
      // SaaS integrations with a dedicated panel below.
      // Keep this list aligned with every `nodeType === 'action.<name>'` block
      // further down the file -- otherwise an imported node lands on the
      // generic JSON / imported-form fallback instead of its native UI.
      'action.shopify',
      'action.hubspot',
      'action.xero',
      'action.myob',
      'action.slack',
      'action.pipedrive',
      'action.microsoft365',
      'action.asana',
      'action.trello',
      'action.netsuite',
      'action.quickbooks',
      'action.sap',
      'action.zoom',
      'action.discord',
      'action.microsoft_teams',
      'action.jira',
      'action.confluence',
      'action.google_calendar',
      'action.notion',
      'action.airtable',
      'action.dropbox',
      'action.zoho_crm',
      'action.insightly',
      'action.cal_com',
      'action.erpnext',
      'action.mailchimp',
      'core.set',
      'core.if',
      'core.switch',
      'core.code',
      'tool.parse_email',
      'core.split',
      'core.aggregate',
      'core.constants',
      'core.delay',
      'core.unsupported',
      // Every webhook-trigger variant uses the same panel as `trigger.webhook`
      // (HTTP method, auth toggle, computed public URL) — keep them in sync
      // with WEBHOOK_TRIGGER_TYPES above.
      'n8n-nodes-base.webhook',
      'n8n-nodes-base.webhookV1',
      'n8n-nodes-base.webhookV2',
      'n8n-nodes-base.webhookv1',
      'n8n-nodes-base.webhookv2',
      'n8n-nodes-base.formTrigger',
      '@n8n/n8n-nodes-langchain.chatTrigger',
      'n8n-nodes-base.chatTrigger',
      'n8n-nodes-base.executeWorkflowTrigger',
      'n8n-nodes-base.executeWorkflow',
      'n8n-nodes-base.postgresTool',
      'n8n-nodes-base.set',
      'n8n-nodes-base.manualTrigger',
      'n8n-nodes-base.evaluationTrigger',
      'n8n-nodes-base.evaluation',
      'n8n-nodes-base.dataTable',
      '@n8n/n8n-nodes-langchain.agent',
      '@n8n/n8n-nodes-langchain.memoryBufferWindow',
      '@n8n/n8n-nodes-langchain.toolThink',
      '@n8n/n8n-nodes-langchain.vectorStorePGVector',
      '@n8n/n8n-nodes-langchain.lmChatOpenAi',
      '@n8n/n8n-nodes-langchain.embeddingsOpenAi',
    ]);
    return known.has(nodeType);
  })();

  const importedProps = useMemo(() => {
    const propsFromUiSchema = Array.isArray(node?.data?.uiSchema?.properties)
      ? (node?.data?.uiSchema?.properties as any[])
      : [];
    const propsFromParamSchema = Array.isArray(node?.data?.parametersSchema?.properties)
      ? (node?.data?.parametersSchema?.properties as any[])
      : [];
    const propsFromImported = Array.isArray(node?.data?.importedProperties)
      ? (node?.data?.importedProperties as any[])
      : [];

    // Prefer explicit n8n property definitions; then UI schema; then parameter schema.
    const rawProps =
      propsFromImported.length > 0
        ? propsFromImported
        : propsFromUiSchema.length > 0
          ? propsFromUiSchema
          : propsFromParamSchema;

    if (!Array.isArray(rawProps) || rawProps.length === 0) return [];
    const props = rawProps.filter((p: any) => p && typeof p === 'object' && p.name);
    return props.sort((a: any, b: any) => {
      const ao = Number(a.displayOrder ?? 0);
      const bo = Number(b.displayOrder ?? 0);
      return ao - bo;
    });
  }, [node?.id, node?.data?.uiSchema, node?.data?.parametersSchema, node?.data?.importedProperties]);

  useEffect(() => {
    if (!node) return;
    setUnsupportedText(JSON.stringify(params._n8n ?? params, null, 2));
    setParamsText(JSON.stringify(params ?? {}, null, 2));
    setOdooDomainText(JSON.stringify(params.domain ?? [], null, 2));
    setOdooFieldsText(JSON.stringify(params.fields ?? [], null, 2));
    // For Odoo nodes: if the saved domain uses polish-notation operators
    // ('|', '&', '!'), drop into raw-JSON mode automatically — the form
    // builder only handles simple flat triplet lists.
    if (node.data?.nodeType === 'action.odoo') {
      const parsed = parseDomainToRows(params.domain ?? []);
      setOdooDomainRawMode(!parsed.canForm);
    }
    const map: Record<string, string> = {};
    const setMap = (key: string, value: any, fallback: any) => {
      map[key] = JSON.stringify(value ?? fallback, null, 2);
    };
    setMap('supabase.filters', params.filters, {});
    setMap('supabase.values', params.values, {});
    setMap('qdrant.filter', params.filter, {});
    setMap('switch.cases', params.cases, []);
    setMap('constants.values', params.values, {});
    setJsonTextMap(map);
    const toKvList = (obj: any) => {
      if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return [];
      return Object.entries(obj).map(([k, v]) => ({ key: String(k), value: String(v ?? '') }));
    };
    setKvEditorMap({
      'http.headers': toKvList(params.headers),
      'http.body': toKvList(params.body),
      'odoo.values': toKvList(params.values),
      'supabase.filters': toKvList(params.filters),
      'supabase.values': toKvList(params.values),
      'qdrant.filter': toKvList(params.filter),
      'constants.values': toKvList(params.values),
      'set.values': toKvList(params.values),
    });
    setSwitchCases(Array.isArray(params.cases) ? params.cases.map((c: any) => ({ value: String(c?.value ?? c ?? '') })) : []);
    const rows = Array.isArray(params.values) ? params.values : [];
    const rowsText = rows
      .map((row: any) => (Array.isArray(row) ? row.map((cell) => String(cell ?? '')).join(',') : String(row ?? '')))
      .join('\n');
    setSheetsValuesText(rowsText);
    const attachments = Array.isArray(params.attachments) ? params.attachments : [];
    setSmtpAttachments(
      attachments.map((a: any) => ({
        filename: String(a?.filename ?? ''),
        content_type: String(a?.content_type ?? 'application/octet-stream'),
        content_base64: String(a?.content_base64 ?? ''),
      }))
    );
    const vector = Array.isArray(params.vector) ? params.vector : [];
    setQdrantVectorText(vector.map((v: any) => String(v)).join(','));
    const points = Array.isArray(params.points) ? params.points : [];
    setQdrantPoints(
      points.map((p: any) => ({
        id: String(p?.id ?? ''),
        vector: Array.isArray(p?.vector) ? p.vector.map((v: any) => String(v)).join(',') : String(p?.vector ?? ''),
        payload: toKvList(p?.payload),
      }))
    );
    setDriveFiles([]);
    setDriveLoading(false);
    setSheetFiles([]);
    setSheetTabs([]);
    setSheetsLoading(false);
    setCalendarList([]);
    setCalendarsLoading(false);
    setGoogleError('');
    const jsonFields: Record<string, string> = {};
    for (const p of importedProps) {
      const t = String(p?.type || '');
      if (t === 'json' || t === 'collection' || t === 'fixedCollection') {
        const current = params?.[p.name];
        jsonFields[p.name] = JSON.stringify(current ?? p?.default ?? (t === 'collection' ? [] : {}), null, 2);
      }
    }
    setImportedJsonFields(jsonFields);
  }, [node?.id, importedProps]);

  const title = useMemo(() => {
    if (!node) return 'Node';
    return nodeType || node.data?.label || 'Node';
  }, [node, nodeType]);
  const webhookUrlInfo = useMemo(() => {
    const workflowId = String(node?.data?.workflowId ?? '').trim();
    const nodeId = String(node?.id ?? '').trim();
    const webhookSecret = String(node?.data?.webhookSecret ?? '').trim();
    const isRealUuid = (v: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(v);
    const envApiUrl = String(process.env.REACT_APP_API_URL || '').trim().replace(/\/+$/, '');
    const baseOrigin = (() => {
      if (envApiUrl) {
        return envApiUrl.replace(/\/api$/i, '');
      }
      if (typeof window === 'undefined' || !window.location) return '';
      const { origin, hostname } = window.location;
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return 'http://127.0.0.1:8000';
      }
      return origin;
    })();
    const ready = isRealUuid(workflowId) && isRealUuid(nodeId) && webhookSecret.length > 8;
    return {
      ready,
      url: ready
        ? `${baseOrigin}/webhook/${workflowId}/${nodeId}/${webhookSecret}/`
        : '',
    };
  }, [node]);
  // Prefer the workflowId baked into the selected node's own data (set on
  // load + on drop), and fall back to the workflowId prop the parent editor
  // page passes down. This guarantees the credential preset dropdown can
  // always load credentials for the current workflow, even for brand-new
  // unsaved nodes that haven't round-tripped through the backend yet.
  const workflowIdForCredentials = (
    String(node?.data?.workflowId ?? '').trim() ||
    String(workflowIdProp ?? '').trim()
  );
  const postgresCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'postgres' && c.is_active),
    [workflowCredentials]
  );
  const smtpCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'smtp' && c.is_active),
    [workflowCredentials]
  );
  const supabaseCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'supabase' && c.is_active),
    [workflowCredentials]
  );
  const imapCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'imap' && c.is_active),
    [workflowCredentials]
  );
  // MQTT broker creds. First-class `mqtt` type wins; we also surface any
  // legacy `custom` credential that's clearly an MQTT broker (Node-RED
  // imports made before the dedicated type existed used `custom` + a
  // `_node_red_type: mqtt-broker` marker).
  const mqttCredentials = useMemo(
    () =>
      workflowCredentials.filter((c) => {
        if (!c.is_active) return false;
        if (c.credential_type === 'mqtt') return true;
        if (c.credential_type !== 'custom') return false;
        const data: any = c.data || {};
        return (
          data._node_red_type === 'mqtt-broker' ||
          Boolean(data.broker) ||
          Boolean(data.broker_host) ||
          String(c.name || '').toLowerCase().includes('mqtt')
        );
      }),
    [workflowCredentials]
  );
  const odooCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'odoo' && c.is_active),
    [workflowCredentials]
  );

  // ----- Odoo introspection ---------------------------------------------
  // Domain operators supported by Odoo's polish notation. Kept narrow on
  // purpose — the user can still drop into raw-JSON mode for exotic ops.
  const ODOO_DOMAIN_OPERATORS = useMemo(
    () => [
      '=', '!=', '>', '>=', '<', '<=',
      'in', 'not in',
      'like', 'ilike', 'not ilike',
      'child_of', 'parent_of',
    ],
    []
  );

  // Parse `params.domain` into a row-shaped form for the visual builder.
  // We accept two shapes losslessly:
  //   1. Flat triplets only          -> AND mode
  //   2. Leading '|' operators + N triplets (the canonical "OR of N")
  //      -> still all triplets after stripping the operators, OR mode
  // Anything with '&', '!', or mixed/nested polish notation is left to
  // the raw-JSON escape hatch (`canForm: false`) — we'd risk silently
  // corrupting the user's intent if we tried to flatten it.
  const parseDomainToRows = (
    domain: any
  ): {
    rows: Array<{ field: string; operator: string; value: string }>;
    canForm: boolean;
  } => {
    if (!Array.isArray(domain)) {
      return { rows: [], canForm: true };
    }
    if (domain.length === 0) {
      return { rows: [], canForm: true };
    }
    const allPipes = domain.every(
      (item: any) =>
        item === '|' ||
        (Array.isArray(item) &&
          item.length === 3 &&
          typeof item[0] === 'string' &&
          typeof item[1] === 'string')
    );
    if (!allPipes) {
      return { rows: [], canForm: false };
    }
    const triplets = domain.filter((item: any) => Array.isArray(item)) as any[];
    return {
      rows: triplets.map((t: any[]) => ({
        field: String(t[0] ?? ''),
        operator: String(t[1] ?? '='),
        value:
          typeof t[2] === 'string'
            ? t[2]
            : t[2] === null || t[2] === undefined
            ? ''
            : JSON.stringify(t[2]),
      })),
      canForm: true,
    };
  };

  // Coerce a string value back to a typed value when it obviously is one
  // (number, bool, null, JSON list). Keeps strings + template expressions
  // (`{{...}}` / `={{...}}`) untouched so the backend renders them at run.
  const coerceDomainValue = (raw: string): any => {
    const s = (raw ?? '').trim();
    if (s === '') return '';
    if (s === 'true') return true;
    if (s === 'false') return false;
    if (s === 'null') return null;
    if (s.includes('{{')) return raw;
    if (/^-?\d+$/.test(s)) return Number(s);
    if (/^-?\d+\.\d+$/.test(s)) return Number(s);
    if ((s.startsWith('[') && s.endsWith(']')) || (s.startsWith('{') && s.endsWith('}'))) {
      try {
        return JSON.parse(s);
      } catch {
        return raw;
      }
    }
    return raw;
  };

  // Build an Odoo domain list out of the form rows. AND is just a flat
  // list (Odoo's default). OR prepends N-1 '|' operators per Odoo polish
  // notation (which is binary, so chained OR needs `|` between every pair).
  const buildDomainFromRows = (
    rows: Array<{ field: string; operator: string; value: string }>,
    mode: 'AND' | 'OR'
  ): any[] => {
    const triplets = rows
      .filter((r) => r.field && r.field.trim())
      .map((r) => [r.field.trim(), r.operator, coerceDomainValue(r.value)]);
    if (triplets.length <= 1 || mode === 'AND') return triplets;
    const ors = Array(triplets.length - 1).fill('|');
    return [...ors, ...triplets];
  };

  // Rows are derived from `params.domain` on every render so we never
  // double-store state. The match-mode (AND/OR) is the only synthetic
  // bit we keep on the node — it can't be inferred from a flat triplet
  // list, so we stash it under `_odoo_domain_mode`.
  const odooDomainRows: Array<{ field: string; operator: string; value: string }> =
    useMemo(() => parseDomainToRows(params.domain ?? []).rows, [params.domain]);
  const odooDomainMode: 'AND' | 'OR' =
    params._odoo_domain_mode === 'OR' ? 'OR' : 'AND';

  const setOdooDomainRows = (
    nextRows: Array<{ field: string; operator: string; value: string }>,
    mode: 'AND' | 'OR' = odooDomainMode
  ) => {
    setParam('_odoo_domain_mode', mode);
    setParam('domain', buildDomainFromRows(nextRows, mode));
  };

  // Active workflow-scoped helpers for talking to the Odoo introspection
  // endpoints. Both swallow errors into a non-fatal warning string the UI
  // can render — a failing introspection should never block the user from
  // typing the model/field manually.
  // Load the full Odoo model catalogue for the selected credential in one
  // shot. We intentionally do NOT pass the user's typed input as a
  // server-side filter — the Autocomplete component already filters client
  // side, and one big fetch + local filtering feels instant compared with
  // a fresh JSON-RPC call on every keystroke.
  const fetchOdooModels = async () => {
    const cid = String(params.credential_id || '').trim();
    if (!workflowIdForCredentials || !cid) {
      setOdooModelOptions([]);
      setOdooModelsCount(-1);
      setOdooModelsTruncated(false);
      setOdooModelsLoadedFor('');
      setOdooModelsWarning(
        'Select an Odoo credential first to load model names from your instance.'
      );
      return;
    }
    setOdooModelsLoading(true);
    setOdooModelsWarning('');
    try {
      const res = await workflowService.listOdooModels(
        workflowIdForCredentials,
        cid,
        '',   // q: empty — load everything, filter locally
        0,    // limit: 0 = "use server max"
      );
      const models = res.models || [];
      setOdooModelOptions(models);
      setOdooModelsCount(models.length);
      setOdooModelsTruncated(Boolean(res.truncated));
      setOdooModelsLoadedFor(cid);
      if (res.warning) setOdooModelsWarning(res.warning);
    } catch (err: any) {
      setOdooModelOptions([]);
      setOdooModelsCount(-1);
      setOdooModelsTruncated(false);
      setOdooModelsLoadedFor('');
      setOdooModelsWarning(
        err?.response?.data?.detail || err?.message || 'Failed to load Odoo models.'
      );
    } finally {
      setOdooModelsLoading(false);
    }
  };

  const fetchOdooFields = async (modelName: string) => {
    const cid = String(params.credential_id || '').trim();
    if (!workflowIdForCredentials || !cid || !modelName) {
      setOdooFieldOptions([]);
      return;
    }
    setOdooFieldsLoading(true);
    setOdooFieldsWarning('');
    try {
      const res = await workflowService.getOdooModelFields(
        workflowIdForCredentials,
        cid,
        modelName
      );
      setOdooFieldOptions(res.fields || []);
      if (res.warning) setOdooFieldsWarning(res.warning);
    } catch (err: any) {
      setOdooFieldOptions([]);
      setOdooFieldsWarning(
        err?.response?.data?.detail || err?.message || 'Failed to load fields.'
      );
    } finally {
      setOdooFieldsLoading(false);
    }
  };

  // Auto-load the model catalogue as soon as the user picks (or changes)
  // an Odoo credential. This way the dropdown is pre-populated with the
  // full list ("Loaded N models from this Odoo instance.") before the
  // user even opens it — no first-open delay.
  useEffect(() => {
    const cid = String(params.credential_id || '').trim();
    if (nodeType !== 'action.odoo') return;
    if (!cid) {
      setOdooModelOptions([]);
      setOdooModelsCount(-1);
      setOdooModelsTruncated(false);
      setOdooModelsLoadedFor('');
      return;
    }
    if (odooModelsLoadedFor === cid || odooModelsLoading) return;
    void fetchOdooModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeType, params.credential_id]);

  // Auto-load fields when the user switches model (only if a credential
  // is selected — otherwise we have nothing to call).
  useEffect(() => {
    const cid = String(params.credential_id || '').trim();
    const model = String(params.model || '').trim();
    if (nodeType !== 'action.odoo' || !cid || !model) return;
    void fetchOdooFields(model);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeType, params.credential_id, params.model]);

  const gmailCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'gmail' && c.is_active),
    [workflowCredentials]
  );
  const shopifyCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'shopify' && c.is_active),
    [workflowCredentials]
  );
  const slackCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'slack' && c.is_active),
    [workflowCredentials]
  );
  const pipedriveCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'pipedrive' && c.is_active),
    [workflowCredentials]
  );
  const microsoft365Credentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'microsoft365' && c.is_active),
    [workflowCredentials]
  );
  const asanaCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'asana' && c.is_active),
    [workflowCredentials]
  );
  const trelloCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'trello' && c.is_active),
    [workflowCredentials]
  );
  const netsuiteCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'netsuite' && c.is_active),
    [workflowCredentials]
  );
  const quickbooksCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'quickbooks' && c.is_active),
    [workflowCredentials]
  );
  const sapCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'sap' && c.is_active),
    [workflowCredentials]
  );
  const hubspotCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'hubspot' && c.is_active),
    [workflowCredentials]
  );
  const zoomCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'zoom' && c.is_active),
    [workflowCredentials]
  );
  const jiraCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'jira' && c.is_active),
    [workflowCredentials]
  );
  const confluenceCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'confluence' && c.is_active),
    [workflowCredentials]
  );
  const discordCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'discord' && c.is_active),
    [workflowCredentials]
  );
  const microsoftTeamsCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'microsoft_teams' && c.is_active),
    [workflowCredentials]
  );
  const googleCalendarCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'google_calendar' && c.is_active),
    [workflowCredentials]
  );
  const googleSheetsCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'google_sheets' && c.is_active),
    [workflowCredentials]
  );
  const googleDriveCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'google_drive' && c.is_active),
    [workflowCredentials]
  );
  const zohoCrmCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'zoho_crm' && c.is_active),
    [workflowCredentials]
  );
  const insightlyCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'insightly' && c.is_active),
    [workflowCredentials]
  );
  const calComCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'cal_com' && c.is_active),
    [workflowCredentials]
  );
  const erpnextCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'erpnext' && c.is_active),
    [workflowCredentials]
  );
  const notionCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'notion' && c.is_active),
    [workflowCredentials]
  );
  const airtableCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'airtable' && c.is_active),
    [workflowCredentials]
  );
  const dropboxCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'dropbox' && c.is_active),
    [workflowCredentials]
  );
  const mailchimpCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'mailchimp' && c.is_active),
    [workflowCredentials]
  );
  const xeroCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'xero' && c.is_active),
    [workflowCredentials]
  );
  const myobCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'myob' && c.is_active),
    [workflowCredentials]
  );

  const loadWorkflowCredentials = async () => {
    if (!workflowIdForCredentials) {
      setWorkflowCredentials([]);
      return;
    }
    try {
      setCredentialsLoading(true);
      const items: any = await workflowService.getCredentials(workflowIdForCredentials);
      const rows = Array.isArray(items) ? items : Array.isArray(items?.results) ? items.results : [];
      setWorkflowCredentials(rows);
    } catch {
      setWorkflowCredentials([]);
    } finally {
      setCredentialsLoading(false);
    }
  };

  useEffect(() => {
    // Load workflow credentials for any action node (or any node that may need
    // a credential preset, including imported / custom nodes).
    if (!workflowIdForCredentials) return;
    if (!nodeType) return;
    const isActionNode =
      nodeType.startsWith('action.') ||
      nodeType.startsWith('integration.') ||
      nodeType.startsWith('crm.') ||
      nodeType.startsWith('erp.') ||
      nodeType.startsWith('messaging.') ||
      nodeType.startsWith('storage.') ||
      nodeType.startsWith('marketing.') ||
      nodeType.startsWith('productivity.') ||
      nodeType.startsWith('n8n-nodes-base.') ||
      nodeType.startsWith('@n8n/') ||
      // imported / custom nodes don't have a fixed prefix — always load for them
      !!node?.data?.importedProperties ||
      !!node?.data?.parametersSchema;
    if (!isActionNode) return;
    loadWorkflowCredentials();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowIdForCredentials, nodeType, node?.id]);

  // If the user opened the editor in one tab and connected an OAuth credential
  // (e.g. Google Sheets / Drive / Calendar) in another tab, refresh the
  // credential list whenever the editor tab regains focus so the new preset
  // appears in the dropdown immediately — no manual page reload required.
  useEffect(() => {
    if (!workflowIdForCredentials) return;
    const refresh = () => {
      if (document.visibilityState === 'visible') {
        loadWorkflowCredentials();
      }
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowIdForCredentials]);
  const scheduleCronParts = useMemo(() => {
    const fallback = ['0', '9', '*', '*', '*'];
    const raw = String(params.cron ?? fallback.join(' ')).trim();
    const parts = raw.split(/\s+/);
    if (parts.length < 5) return fallback;
    return [parts[0], parts[1], parts[2], parts[3], parts[4]];
  }, [params.cron]);
  const [scheduleMode, setScheduleMode] = useState<'minute' | 'hour' | 'day' | 'week' | 'month'>('day');
  const scheduleMinuteAt = useMemo(() => {
    const raw = String(scheduleCronParts[0] ?? '*');
    return raw === '*' || raw.startsWith('*/') ? '0' : raw;
  }, [scheduleCronParts]);
  const scheduleHourAt = useMemo(() => {
    const raw = String(scheduleCronParts[1] ?? '*');
    return raw === '*' || raw.startsWith('*/') ? '9' : raw;
  }, [scheduleCronParts]);
  const scheduleDayAt = useMemo(() => {
    const raw = String(scheduleCronParts[2] ?? '*');
    return raw === '*' || raw.startsWith('*/') ? '1' : raw;
  }, [scheduleCronParts]);
  const scheduleWeekdayAt = useMemo(() => {
    const raw = String(scheduleCronParts[4] ?? '*');
    return raw === '*' ? '1' : raw;
  }, [scheduleCronParts]);
  const scheduleMinuteInterval = useMemo(() => parseCronStep(scheduleCronParts[0], 1), [scheduleCronParts]);
  const scheduleHourInterval = useMemo(() => parseCronStep(scheduleCronParts[1], 1), [scheduleCronParts]);
  const scheduleDayInterval = useMemo(() => parseCronStep(scheduleCronParts[2], 1), [scheduleCronParts]);
  const scheduleMonthInterval = useMemo(() => parseCronStep(scheduleCronParts[3], 1), [scheduleCronParts]);
  const scheduleWeekInterval = useMemo(() => {
    const n = Number(params.week_interval ?? 1);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 1;
  }, [params.week_interval]);

  useEffect(() => {
    if (nodeType !== 'trigger.schedule') return;
    const [minute, hour, dayOfMonth, month, dayOfWeek] = scheduleCronParts;
    if (minute === '*' && hour === '*' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
      setScheduleMode('minute');
      return;
    }
    if (hour === '*' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
      setScheduleMode('hour');
      return;
    }
    if (dayOfMonth === '*' && month === '*' && dayOfWeek !== '*') {
      setScheduleMode('week');
      return;
    }
    if (dayOfMonth !== '*' && month === '*' && dayOfWeek === '*') {
      setScheduleMode('month');
      return;
    }
    setScheduleMode('day');
  }, [nodeType, node?.id, scheduleCronParts]);

  if (!node) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold">
          Node Settings
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Select a node to edit or delete it.
        </Typography>
      </Box>
    );
  }

  const setParam = (key: string, value: any) => {
    if (readOnly) return;
    const next = { ...paramsRef.current, [key]: value };
    paramsRef.current = next;
    onChange({ parameters: next });
  };

  // Apply several parameter updates atomically (single onChange call,
  // single React re-render). Use this whenever a UI handler needs to set
  // more than one key — e.g. file pickers that set both an id and a name.
  const setParams = (updates: Record<string, any>) => {
    if (readOnly) return;
    if (!updates || typeof updates !== 'object') return;
    const next = { ...paramsRef.current, ...updates };
    paramsRef.current = next;
    onChange({ parameters: next });
  };

  const renderJsonField = (key: string, label: string, currentValue: any, fallback: any) => {
    const textValue = jsonTextMap[key] ?? JSON.stringify(currentValue ?? fallback, null, 2);
    return (
      <TextField
        key={key}
        label={`${label} (JSON)`}
        fullWidth
        multiline
        minRows={4}
        value={textValue}
        onChange={(e) =>
          setJsonTextMap((prev) => ({
            ...prev,
            [key]: e.target.value,
          }))
        }
        onBlur={() => {
          try {
            const parsed = safeJsonParse(jsonTextMap[key] ?? '');
            const [, paramKey] = key.split('.', 2);
            if (paramKey) {
              setParam(paramKey, parsed);
            }
          } catch (err: any) {
            enqueueSnackbar(err.message || 'Invalid JSON', { variant: 'error' });
            setJsonTextMap((prev) => ({
              ...prev,
              [key]: JSON.stringify(currentValue ?? fallback, null, 2),
            }));
          }
        }}
        sx={{ mb: 2 }}
        disabled={readOnly}
      />
    );
  };

  const renderKeyValueEditor = (key: string, label: string) => {
    const items = kvEditorMap[key] || [];
    const setItems = (next: Array<{ key: string; value: string }>) => {
      setKvEditorMap((prev) => ({ ...prev, [key]: next }));
      const obj = next.reduce((acc, item) => {
        if (item.key.trim()) acc[item.key.trim()] = item.value;
        return acc;
      }, {} as Record<string, string>);
      const [, paramKey] = key.split('.', 2);
      if (paramKey) {
        setParam(paramKey, obj);
      }
    };
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {label}
        </Typography>
        <Stack spacing={1}>
          {items.map((item, idx) => (
            <Box key={`${key}-${idx}`} display="flex" gap={1} alignItems="center">
              <TextField
                label="Key"
                size="small"
                fullWidth
                value={item.key}
                onChange={(e) => {
                  const next = [...items];
                  next[idx] = { ...item, key: e.target.value };
                  setItems(next);
                }}
                disabled={readOnly}
              />
              <TextField
                label="Value"
                size="small"
                fullWidth
                value={item.value}
                onChange={(e) => {
                  const next = [...items];
                  next[idx] = { ...item, value: e.target.value };
                  setItems(next);
                }}
                disabled={readOnly}
              />
              <IconButton
                aria-label="remove"
                onClick={() => setItems(items.filter((_, i) => i !== idx))}
                disabled={readOnly}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setItems([...items, { key: '', value: '' }])}
            disabled={readOnly}
          >
            Add
          </Button>
        </Stack>
      </Box>
    );
  };

  const renderAttachmentsEditor = () => {
    const setItems = (next: Array<{ filename: string; content_type: string; content_base64: string }>) => {
      setSmtpAttachments(next);
      setParam('attachments', next.map((a) => ({ ...a })));
    };
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Attachments
        </Typography>
        <Stack spacing={1}>
          {smtpAttachments.map((att, idx) => (
            <Box key={`att-${idx}`} sx={{ border: '1px solid', borderColor: 'divider', p: 1, borderRadius: 1 }}>
              <Box display="flex" gap={1} alignItems="center">
                <TextField
                  label="Filename"
                  size="small"
                  fullWidth
                  value={att.filename}
                  onChange={(e) => {
                    const next = [...smtpAttachments];
                    next[idx] = { ...att, filename: e.target.value };
                    setItems(next);
                  }}
                  disabled={readOnly}
                />
                <IconButton
                  aria-label="remove"
                  onClick={() => setItems(smtpAttachments.filter((_, i) => i !== idx))}
                  disabled={readOnly}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
              <TextField
                label="Content Type"
                size="small"
                fullWidth
                value={att.content_type}
                onChange={(e) => {
                  const next = [...smtpAttachments];
                  next[idx] = { ...att, content_type: e.target.value };
                  setItems(next);
                }}
                sx={{ mt: 1 }}
                disabled={readOnly}
              />
              <TextField
                label="Content (base64)"
                fullWidth
                multiline
                minRows={2}
                value={att.content_base64}
                onChange={(e) => {
                  const next = [...smtpAttachments];
                  next[idx] = { ...att, content_base64: e.target.value };
                  setItems(next);
                }}
                sx={{ mt: 1 }}
                disabled={readOnly}
              />
            </Box>
          ))}
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setItems([...smtpAttachments, { filename: '', content_type: 'application/octet-stream', content_base64: '' }])}
            disabled={readOnly}
          >
            Add attachment
          </Button>
        </Stack>
      </Box>
    );
  };

  const renderQdrantPointsEditor = () => {
    const setItems = (next: Array<{ id: string; vector: string; payload: Array<{ key: string; value: string }> }>) => {
      setQdrantPoints(next);
      const pointsPayload = next.map((p) => ({
        id: p.id || undefined,
        vector: p.vector
          .split(',')
          .map((v) => v.trim())
          .filter((v) => v.length)
          .map((v) => Number(v)),
        payload: p.payload.reduce((acc, item) => {
          if (item.key.trim()) acc[item.key.trim()] = item.value;
          return acc;
        }, {} as Record<string, string>),
      }));
      setParam('points', pointsPayload);
    };
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Points
        </Typography>
        <Stack spacing={1}>
          {qdrantPoints.map((p, idx) => (
            <Box key={`pt-${idx}`} sx={{ border: '1px solid', borderColor: 'divider', p: 1, borderRadius: 1 }}>
              <Box display="flex" gap={1} alignItems="center">
                <TextField
                  label="ID"
                  size="small"
                  fullWidth
                  value={p.id}
                  onChange={(e) => {
                    const next = [...qdrantPoints];
                    next[idx] = { ...p, id: e.target.value };
                    setItems(next);
                  }}
                  disabled={readOnly}
                />
                <IconButton
                  aria-label="remove"
                  onClick={() => setItems(qdrantPoints.filter((_, i) => i !== idx))}
                  disabled={readOnly}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
              <TextField
                label="Vector (comma-separated)"
                size="small"
                fullWidth
                value={p.vector}
                onChange={(e) => {
                  const next = [...qdrantPoints];
                  next[idx] = { ...p, vector: e.target.value };
                  setItems(next);
                }}
                sx={{ mt: 1 }}
                disabled={readOnly}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Payload
              </Typography>
              <Stack spacing={1} sx={{ mt: 1 }}>
                {(p.payload || []).map((item, pIdx) => (
                  <Box key={`pt-${idx}-kv-${pIdx}`} display="flex" gap={1} alignItems="center">
                    <TextField
                      label="Key"
                      size="small"
                      fullWidth
                      value={item.key}
                      onChange={(e) => {
                        const next = [...qdrantPoints];
                        const payload = [...(next[idx].payload || [])];
                        payload[pIdx] = { ...payload[pIdx], key: e.target.value };
                        next[idx] = { ...next[idx], payload };
                        setItems(next);
                      }}
                      disabled={readOnly}
                    />
                    <TextField
                      label="Value"
                      size="small"
                      fullWidth
                      value={item.value}
                      onChange={(e) => {
                        const next = [...qdrantPoints];
                        const payload = [...(next[idx].payload || [])];
                        payload[pIdx] = { ...payload[pIdx], value: e.target.value };
                        next[idx] = { ...next[idx], payload };
                        setItems(next);
                      }}
                      disabled={readOnly}
                    />
                    <IconButton
                      aria-label="remove"
                      onClick={() => {
                        const next = [...qdrantPoints];
                        const payload = (next[idx].payload || []).filter((_, i) => i !== pIdx);
                        next[idx] = { ...next[idx], payload };
                        setItems(next);
                      }}
                      disabled={readOnly}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    const next = [...qdrantPoints];
                    const payload = [...(next[idx].payload || []), { key: '', value: '' }];
                    next[idx] = { ...next[idx], payload };
                    setItems(next);
                  }}
                  disabled={readOnly}
                >
                  Add payload field
                </Button>
              </Stack>
            </Box>
          ))}
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setItems([...qdrantPoints, { id: '', vector: '', payload: [] }])}
            disabled={readOnly}
          >
            Add point
          </Button>
        </Stack>
      </Box>
    );
  };

  // Resolve a Google access token for the editor "Load …" helpers.
  //
  // Preference order:
  //   1. The user-typed inline ``access_token`` parameter (if present).
  //   2. The selected Workflow Credential preset (``credential_id``) — this
  //      hits a backend endpoint that auto-refreshes the OAuth token if it
  //      has expired, so the user never has to paste a raw token.
  //
  // Returns an empty string AND shows an error toast if neither is available,
  // so callers can simply ``return`` on a falsy result.
  const resolveGoogleAccessToken = async (): Promise<string> => {
    const inline = String(params.access_token || '').trim();
    if (inline) return inline;

    const credentialId = String(params.credential_id || '').trim();
    if (!credentialId) {
      enqueueSnackbar(
        'Select a Google credential preset (or paste an Access Token) first.',
        { variant: 'error' }
      );
      return '';
    }
    if (!workflowIdForCredentials) {
      enqueueSnackbar('Workflow not ready yet — try again in a moment.', { variant: 'error' });
      return '';
    }
    try {
      const resp: any = await workflowService.getCredentialAccessToken(
        workflowIdForCredentials,
        credentialId
      );
      const token = String(resp?.access_token || '').trim();
      if (!token) {
        enqueueSnackbar(
          'The selected credential has no usable access token. Re-connect it from the Workflow Credentials page.',
          { variant: 'error' }
        );
      }
      return token;
    } catch (err: any) {
      const msg = err?.message || 'Could not load access token from the selected credential.';
      setGoogleError(msg);
      enqueueSnackbar(msg, { variant: 'error' });
      return '';
    }
  };

  const fetchDriveFiles = async () => {
    setDriveLoading(true);
    setGoogleError('');
    try {
      const token = await resolveGoogleAccessToken();
      if (!token) return;
      const q = params.query ? String(params.query) : '';
      const qs = new URLSearchParams({
        pageSize: '100',
        fields: 'files(id,name,mimeType)',
      });
      if (q) qs.set('q', q);
      const resp = await fetch(`https://www.googleapis.com/drive/v3/files?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data?.error?.message || 'Failed to load files');
      }
      const files = Array.isArray(data?.files) ? data.files : [];
      setDriveFiles(files.map((f: any) => ({ id: String(f.id), name: String(f.name), mimeType: String(f.mimeType || '') })));
    } catch (err: any) {
      const msg = err?.message || 'Failed to load files';
      setGoogleError(msg);
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setDriveLoading(false);
    }
  };

  const fetchSheetFiles = async () => {
    setSheetsLoading(true);
    setGoogleError('');
    try {
      const token = await resolveGoogleAccessToken();
      if (!token) return;
      const qs = new URLSearchParams({
        q: "mimeType='application/vnd.google-apps.spreadsheet' and trashed=false",
        pageSize: '100',
        fields: 'files(id,name)',
      });
      const resp = await fetch(`https://www.googleapis.com/drive/v3/files?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data?.error?.message || 'Failed to load spreadsheets');
      }
      const files = Array.isArray(data?.files) ? data.files : [];
      setSheetFiles(files.map((f: any) => ({ id: String(f.id), name: String(f.name) })));
    } catch (err: any) {
      const msg = err?.message || 'Failed to load spreadsheets';
      setGoogleError(msg);
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSheetsLoading(false);
    }
  };

  const fetchSheetTabs = async () => {
    const spreadsheetId = String(params.spreadsheet_id || '').trim();
    if (!spreadsheetId) {
      enqueueSnackbar('Pick a spreadsheet first (or paste a Spreadsheet ID).', { variant: 'error' });
      return;
    }
    setSheetsLoading(true);
    setGoogleError('');
    try {
      const token = await resolveGoogleAccessToken();
      if (!token) return;
      const resp = await fetch(`https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}?fields=sheets(properties(title))`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data?.error?.message || 'Failed to load sheet tabs');
      }
      const sheets = Array.isArray(data?.sheets) ? data.sheets : [];
      setSheetTabs(sheets.map((s: any) => ({ title: String(s?.properties?.title || '') })).filter((s: any) => s.title));
    } catch (err: any) {
      const msg = err?.message || 'Failed to load sheet tabs';
      setGoogleError(msg);
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSheetsLoading(false);
    }
  };

  // Google Calendar — list of accessible calendars for the dropdown.
  const fetchCalendarList = async () => {
    setCalendarsLoading(true);
    setGoogleError('');
    try {
      const token = await resolveGoogleAccessToken();
      if (!token) return;
      const resp = await fetch(
        'https://www.googleapis.com/calendar/v3/users/me/calendarList?fields=items(id,summary,primary)',
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data?.error?.message || 'Failed to load calendars');
      }
      const items = Array.isArray(data?.items) ? data.items : [];
      setCalendarList(
        items.map((c: any) => ({
          id: String(c.id),
          summary: String(c.summary || c.id),
          primary: !!c.primary,
        }))
      );
    } catch (err: any) {
      const msg = err?.message || 'Failed to load calendars';
      setGoogleError(msg);
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setCalendarsLoading(false);
    }
  };

  const shouldShowImportedProp = (p: any) => {
    const opts = p?.displayOptions;
    if (!opts) return true;
    const matches = (conditions: any) => {
      if (!conditions || typeof conditions !== 'object') return true;
      return Object.entries(conditions).every(([key, values]) => {
        const current = params?.[key];
        if (Array.isArray(values)) {
          return values.includes(current);
        }
        return current === values;
      });
    };
    if (opts.show && !matches(opts.show)) return false;
    if (opts.hide && matches(opts.hide)) return false;
    return true;
  };

  const getPathValue = (obj: any, path: Array<string | number>) => {
    let cur = obj;
    for (const part of path) {
      if (cur == null) return undefined;
      cur = cur[part as any];
    }
    return cur;
  };

  const setPathValue = (obj: any, path: Array<string | number>, value: any) => {
    const root = obj ?? {};
    if (path.length === 0) return value;

    const clone = Array.isArray(root) ? [...root] : { ...root };
    let cur: any = clone;

    for (let i = 0; i < path.length - 1; i++) {
      const key = path[i];
      const nextKey = path[i + 1];
      const existing = cur?.[key as any];
      const nextContainer =
        existing != null
          ? (Array.isArray(existing) ? [...existing] : { ...existing })
          : (typeof nextKey === 'number' ? [] : {});
      cur[key as any] = nextContainer;
      cur = nextContainer;
    }

    cur[path[path.length - 1] as any] = value;
    return clone;
  };

  const updateImportedParamPath = (path: Array<string | number>, value: any) => {
    const next = setPathValue(params ?? {}, path, value);
    onChange({ parameters: next });
  };

  const renderImportedFieldByPath = (p: any, path: Array<string | number>, keyPrefix: string) => {
    if (!p || !p.name) return null;
    const pType = String(p?.type || 'string');
    const label = p?.displayName || String(path[path.length - 1] ?? p.name);
    const description = p?.description || '';
    const defaultValue = p?.default;
    const typeOptions = p?.typeOptions || {};
    const currentValue = getPathValue(params, path);
    const value = currentValue ?? defaultValue;
    const key = `${keyPrefix}.${String(path[path.length - 1])}`;

    if (pType === 'boolean') {
      return (
        <FormControlLabel
          key={key}
          control={
            <Switch
              checked={Boolean(value)}
              onChange={(e) => updateImportedParamPath(path, e.target.checked)}
              disabled={readOnly}
            />
          }
          label={label}
          sx={{ mb: 2 }}
        />
      );
    }

    if (pType === 'options' || pType === 'multiOptions' || Array.isArray(p?.options)) {
      const options = Array.isArray(p?.options) ? p.options : [];
      const multiple = pType === 'multiOptions';
      const selectValue = value ?? (multiple ? [] : '');
      return (
        <FormControl key={key} fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel id={`${key}-label`}>{label}</InputLabel>
          <Select
            labelId={`${key}-label`}
            label={label}
            multiple={multiple}
            value={selectValue}
            onChange={(e) => updateImportedParamPath(path, e.target.value)}
            disabled={readOnly}
          >
            {options.map((opt: any) => (
              <MenuItem key={String(opt?.value)} value={opt?.value}>
                {opt?.name || String(opt?.value)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (pType === 'number') {
      return (
        <TextField
          key={key}
          label={label}
          size="small"
          fullWidth
          type="number"
          value={value ?? ''}
          onChange={(e) => updateImportedParamPath(path, e.target.value === '' ? '' : Number(e.target.value))}
          sx={{ mb: 2 }}
          helperText={description || undefined}
          disabled={readOnly}
        />
      );
    }

    if (pType === 'collection') {
      const nested = Array.isArray(p?.options) ? p.options : [];
      return (
        <Box key={key} sx={{ mb: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {label}
          </Typography>
          {description && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {description}
            </Typography>
          )}
          {nested.map((child: any) =>
            renderImportedFieldByPath(child, [...path, child.name], `${key}.${String(child.name)}`)
          )}
        </Box>
      );
    }

    if (pType === 'fixedCollection') {
      const groups = Array.isArray(p?.options) ? p.options : [];
      return (
        <Box key={key} sx={{ mb: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {label}
          </Typography>
          {description && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {description}
            </Typography>
          )}
          {groups.map((group: any) => {
            const groupName = String(group?.name || '');
            if (!groupName) return null;
            const groupPath = [...path, groupName];
            const valuesDef = Array.isArray(group?.values) ? group.values : [];
            const rawGroupValue = getPathValue(params, groupPath);
            const isMulti = Boolean(group?.multipleValues) || Array.isArray(rawGroupValue);
            const rows = isMulti
              ? (Array.isArray(rawGroupValue) && rawGroupValue.length > 0 ? rawGroupValue : [{}])
              : [rawGroupValue && typeof rawGroupValue === 'object' ? rawGroupValue : {}];

            return (
              <Box key={`${key}.${groupName}`} sx={{ mb: 1.5 }}>
                <Typography variant="body2" fontWeight="medium" sx={{ mb: 0.5 }}>
                  {group?.displayName || groupName}
                </Typography>
                {rows.map((_: any, idx: number) => (
                  <Box
                    key={`${key}.${groupName}.${idx}`}
                    sx={{ mb: 1, p: 1, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}
                  >
                    {valuesDef.map((child: any) =>
                      renderImportedFieldByPath(
                        child,
                        isMulti
                          ? [...groupPath, idx, child.name]
                          : [...groupPath, child.name],
                        `${key}.${groupName}.${idx}.${String(child?.name || '')}`
                      )
                    )}
                    {isMulti && (
                      <Button
                        size="small"
                        color="error"
                        onClick={() => {
                          const cur = Array.isArray(getPathValue(params, groupPath))
                            ? [...getPathValue(params, groupPath)]
                            : [];
                          cur.splice(idx, 1);
                          updateImportedParamPath(groupPath, cur.length > 0 ? cur : []);
                        }}
                        disabled={readOnly}
                      >
                        Remove Row
                      </Button>
                    )}
                  </Box>
                ))}
                {isMulti && (
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const cur = Array.isArray(getPathValue(params, groupPath))
                        ? [...getPathValue(params, groupPath)]
                        : [];
                      cur.push({});
                      updateImportedParamPath(groupPath, cur);
                    }}
                    disabled={readOnly}
                  >
                    Add Row
                  </Button>
                )}
              </Box>
            );
          })}
        </Box>
      );
    }

    if (pType === 'json') {
      const textValue = importedJsonFields[key] ?? JSON.stringify(value ?? defaultValue ?? {}, null, 2);
      return (
        <TextField
          key={key}
          label={`${label} (JSON)`}
          fullWidth
          multiline
          minRows={4}
          value={textValue}
          onChange={(e) =>
            setImportedJsonFields((prev) => ({
              ...prev,
              [key]: e.target.value,
            }))
          }
          onBlur={() => {
            try {
              const parsed = safeJsonParse(importedJsonFields[key] ?? '');
              updateImportedParamPath(path, parsed);
            } catch (err: any) {
              enqueueSnackbar(err.message || 'Invalid JSON', { variant: 'error' });
              setImportedJsonFields((prev) => ({
                ...prev,
                [key]: JSON.stringify(value ?? defaultValue ?? {}, null, 2),
              }));
            }
          }}
          sx={{ mb: 2 }}
          helperText={description || undefined}
          disabled={readOnly}
        />
      );
    }

    return (
      <TextField
        key={key}
        label={label}
        size="small"
        fullWidth
        type={typeOptions?.password ? 'password' : 'text'}
        value={getRlValue(value)}
        onChange={(e) => updateImportedParamPath(path, setRlValue(currentValue, e.target.value))}
        sx={{ mb: 2 }}
        helperText={description || undefined}
        disabled={readOnly}
      />
    );
  };

  const renderImportedField = (p: any) => {
    const key = p?.name;
    if (!key) return null;
    if (!shouldShowImportedProp(p)) return null;
    return renderImportedFieldByPath(p, [key], key);
  };

  const dynamicImportedParamKeys =
    !node?.data?.isImported || !params || typeof params !== 'object'
      ? []
      : Object.keys(params).filter((k) => !k.startsWith('_'));

  const renderDynamicParamByPath = (
    path: Array<string | number>,
    value: any,
    keyPrefix: string,
    label: string
  ): React.ReactNode => {
    const key = `${keyPrefix}.${String(path[path.length - 1] ?? 'root')}`;
    const current = getPathValue(params, path);

    if (typeof value === 'boolean') {
      return (
        <FormControlLabel
          key={key}
          control={
            <Switch
              checked={Boolean(current)}
              onChange={(e) => updateImportedParamPath(path, e.target.checked)}
              disabled={readOnly}
            />
          }
          label={label}
          sx={{ mb: 2 }}
        />
      );
    }

    if (typeof value === 'number') {
      return (
        <TextField
          key={key}
          label={label}
          size="small"
          fullWidth
          type="number"
          value={current ?? ''}
          onChange={(e) => updateImportedParamPath(path, e.target.value === '' ? '' : Number(e.target.value))}
          sx={{ mb: 2 }}
          disabled={readOnly}
        />
      );
    }

    if (typeof value === 'string' || value == null) {
      return (
        <TextField
          key={key}
          label={label}
          size="small"
          fullWidth
          value={current ?? ''}
          onChange={(e) => updateImportedParamPath(path, e.target.value)}
          sx={{ mb: 2 }}
          disabled={readOnly}
        />
      );
    }

    if (Array.isArray(value)) {
      const primitiveArray = value.every((v) => ['string', 'number', 'boolean'].includes(typeof v));
      if (primitiveArray) {
        return (
          <TextField
            key={key}
            label={`${label} (comma separated)`}
            size="small"
            fullWidth
            value={(current || []).map((v: any) => String(v)).join(', ')}
            onChange={(e) =>
              updateImportedParamPath(
                path,
                e.target.value
                  .split(',')
                  .map((x) => x.trim())
                  .filter(Boolean)
              )
            }
            sx={{ mb: 2 }}
            disabled={readOnly}
          />
        );
      }
      // Complex arrays fallback to JSON editor for now.
      return (
        <TextField
          key={key}
          label={`${label} (JSON)`}
          fullWidth
          multiline
          minRows={4}
          value={importedJsonFields[key] ?? JSON.stringify(current ?? value, null, 2)}
          onChange={(e) =>
            setImportedJsonFields((prev) => ({
              ...prev,
              [key]: e.target.value,
            }))
          }
          onBlur={() => {
            try {
              const parsed = safeJsonParse(importedJsonFields[key] ?? '');
              updateImportedParamPath(path, parsed);
            } catch (err: any) {
              enqueueSnackbar(err.message || 'Invalid JSON', { variant: 'error' });
            }
          }}
          sx={{ mb: 2 }}
          disabled={readOnly}
        />
      );
    }

    if (typeof value === 'object') {
      const entries = Object.entries(value);
      return (
        <Box key={key} sx={{ mb: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {label}
          </Typography>
          {entries.length === 0 ? (
            <Typography variant="caption" color="text.secondary">
              Empty object
            </Typography>
          ) : (
            entries.map(([childKey, childValue]) =>
              renderDynamicParamByPath(
                [...path, childKey],
                childValue,
                `${key}.${childKey}`,
                childKey
              )
            )
          )}
        </Box>
      );
    }

    return null;
  };

  // Imported nodes normally render a generic "imported" form, but if the node
  // type matches one of our native panels (Xero, MYOB, Slack, HubSpot, every
  // webhook trigger variant, ...) we MUST fall through to that bespoke UI so
  // the user gets the credential picker, operation/resource dropdowns, etc.
  // Only nodes whose type is genuinely unknown should land on the raw
  // key/value editor.
  const useGenericImportedForm =
    Boolean(node?.data?.isImported) &&
    !isKnownEditorType &&
    !isWebhookTriggerType(nodeType);

  if (useGenericImportedForm) {
    const hasSchemaFields = importedProps.length > 0;
    const hasDynamicFields = dynamicImportedParamKeys.length > 0;
    const isEmpty = !hasSchemaFields && !hasDynamicFields;

    const handleAddParam = () => {
      const key = newParamKey.trim();
      if (!key) return;
      onChange({ parameters: { ...(params ?? {}), [key]: '' } });
      setNewParamKey('');
    };

    const handleDeleteParam = (key: string) => {
      const next = { ...(params ?? {}) };
      delete next[key];
      onChange({ parameters: next });
    };

    return (
      <Box
        className="nowheel nodrag"
        onWheelCapture={(e) => e.stopPropagation()}
        sx={{
          p: 2,
          width: 320,
          maxHeight: '75vh',
          overflowY: 'scroll',
          overflowX: 'hidden',
          overscrollBehavior: 'contain',
          scrollbarGutter: 'stable',
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': { width: 10 },
          '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(120,120,120,0.7)', borderRadius: 8 },
          '&::-webkit-scrollbar-track': { backgroundColor: 'rgba(0,0,0,0.08)', borderRadius: 8 },
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight="bold" noWrap>
              {title}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {nodeType}
            </Typography>
          </Box>
          <Button
            color="error"
            variant="outlined"
            size="small"
            startIcon={<DeleteIcon />}
            onClick={onDelete}
            disabled={readOnly}
          >
            Delete
          </Button>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ pointerEvents: readOnly ? 'none' : 'auto', opacity: readOnly ? 0.6 : 1 }}>
          <TextField
            label="Name"
            size="small"
            fullWidth
            value={node.data?.label || ''}
            onChange={(e) => {
              if (readOnly) return;
              onChange({ label: e.target.value });
            }}
            sx={{ mb: 2 }}
            disabled={readOnly}
          />

          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Imported node — parameters are passed directly to the executor.
          </Typography>

          {/* Schema-driven fields (when the import has a full n8n property list) */}
          {hasSchemaFields && importedProps.map((p: any) => renderImportedField(p))}

          {/* Dynamic fields from existing params (node was already configured) */}
          {!hasSchemaFields && hasDynamicFields && (
            <>
              {dynamicImportedParamKeys.map((k) => (
                <Box key={k} display="flex" alignItems="flex-start" gap={0.5} sx={{ mb: 0 }}>
                  <Box sx={{ flex: 1 }}>
                    {renderDynamicParamByPath([k], (params as any)?.[k], `dynamic.${k}`, k)}
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteParam(k)}
                    disabled={readOnly}
                    sx={{ mt: 0.5, color: 'error.main' }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </>
          )}

          {/* Empty state — no schema and no params yet */}
          {isEmpty && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
              No parameters yet. Add fields below to configure this node.
            </Typography>
          )}

          {/* Add Parameter row */}
          {!hasSchemaFields && !readOnly && (
            <Box display="flex" gap={1} alignItems="center" sx={{ mt: isEmpty ? 0 : 1 }}>
              <TextField
                label="New field name"
                size="small"
                value={newParamKey}
                onChange={(e) => setNewParamKey(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleAddParam(); }}
                sx={{ flex: 1 }}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddParam}
                disabled={!newParamKey.trim()}
              >
                Add
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      className="nowheel nodrag"
      onWheelCapture={(e) => e.stopPropagation()}
      sx={{
        p: 2,
        width: 320,
        maxHeight: '75vh',
        overflowY: 'scroll',
        overflowX: 'hidden',
        overscrollBehavior: 'contain',
        scrollbarGutter: 'stable',
        scrollbarWidth: 'thin',
        '&::-webkit-scrollbar': {
          width: 10,
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(120, 120, 120, 0.7)',
          borderRadius: 8,
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'rgba(0, 0, 0, 0.08)',
          borderRadius: 8,
        },
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight="bold" noWrap>
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {node.id}
          </Typography>
        </Box>
        <Button
          color="error"
          variant="outlined"
          size="small"
          startIcon={<DeleteIcon />}
          onClick={onDelete}
          disabled={readOnly}
        >
          Delete
        </Button>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ pointerEvents: readOnly ? 'none' : 'auto', opacity: readOnly ? 0.6 : 1 }}>
      <TextField
        label="Name"
        size="small"
        fullWidth
        value={node.data?.label || ''}
        onChange={(e) => {
          if (readOnly) return;
          onChange({ label: e.target.value });
        }}
        sx={{ mb: 2 }}
        disabled={readOnly}
      />

      {/* Generic editor for truly unknown non-imported node types */}
      {!isKnownEditorType && !node?.data?.isImported && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Unknown node type. Edit parameters as JSON.
          </Typography>
          <TextField
            label="Parameters (JSON)"
            fullWidth
            multiline
            minRows={10}
            value={paramsText}
            onChange={(e) => setParamsText(e.target.value)}
            onBlur={() => {
              try {
                const parsed = safeJsonParse(paramsText);
                onChange({ parameters: parsed });
              } catch (err: any) {
                enqueueSnackbar(err.message || 'Invalid parameters JSON', { variant: 'error' });
                setParamsText(JSON.stringify(params ?? {}, null, 2));
              }
            }}
            sx={{ mb: 2 }}
          />
        </>
      )}

      {/* Trigger: Webhook (native + every n8n webhook variant) */}
      {isWebhookTriggerType(nodeType) && (() => {
        // Normalise whatever the import / DB has put in `method` (or the
        // legacy n8n `httpMethod`). Older n8n imports stored the multi-method
        // form as a list — and an even older import bug saved it as the
        // literal string "['POST']". We accept all three (string / array /
        // bracketed string) and fall back to POST when nothing matches.
        const ALLOWED = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'];
        const pickMethod = (raw: any): string => {
          if (Array.isArray(raw)) return pickMethod(raw[0]);
          const s = String(raw ?? '').trim();
          if (!s) return 'POST';
          const m = s.toUpperCase().match(/[A-Z]+/g) || [];
          for (const candidate of m) {
            if (ALLOWED.includes(candidate)) return candidate;
          }
          return 'POST';
        };
        const currentMethod = pickMethod(params.method ?? params.httpMethod);
        return (
          <>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel id="webhook-method-label">Method</InputLabel>
              <Select
                labelId="webhook-method-label"
                label="Method"
                value={currentMethod}
                onChange={(e) => setParam('method', String(e.target.value).toUpperCase())}
              >
                {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((m) => (
                  <MenuItem key={m} value={m}>
                    {m}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(params.auth_required)}
                  onChange={(e) => setParam('auth_required', e.target.checked)}
                  disabled={readOnly}
                />
              }
              label="Require auth token"
              sx={{ mb: 1 }}
            />
            {Boolean(params.auth_required) && (
              <>
                <TextField
                  label="Webhook auth token"
                  size="small"
                  fullWidth
                  type="password"
                  value={params.auth_token ?? ''}
                  onChange={(e) => setParam('auth_token', e.target.value)}
                  sx={{ mb: 1 }}
                  disabled={readOnly}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                  Send this token in header <code>X-Webhook-Token</code> when calling the webhook.
                </Typography>
              </>
            )}
            {webhookUrlInfo.ready ? (
              <>
                <TextField
                  label="Webhook URL"
                  size="small"
                  fullWidth
                  value={webhookUrlInfo.url}
                  InputProps={{ readOnly: true }}
                  sx={{ mb: 1 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                  Call this URL from external systems. It is unique per workflow node and secret.
                </Typography>
              </>
            ) : (
              <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
                Save this workflow first to generate a unique webhook URL.
              </Typography>
            )}
          </>
        );
      })()}

      {/* Trigger: Schedule */}
      {nodeType === 'trigger.schedule' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="schedule-mode-label">Run every</InputLabel>
            <Select
              labelId="schedule-mode-label"
              label="Run every"
              value={scheduleMode}
              onChange={(e) => {
                const mode = String(e.target.value) as 'minute' | 'hour' | 'day' | 'week' | 'month';
                setScheduleMode(mode);
                if (mode === 'minute') setParam('cron', `${toCronStep(scheduleMinuteInterval)} * * * *`);
                if (mode === 'hour') setParam('cron', `${scheduleMinuteAt} ${toCronStep(scheduleHourInterval)} * * *`);
                if (mode === 'day') setParam('cron', `${scheduleMinuteAt} ${scheduleHourAt} ${toCronStep(scheduleDayInterval)} * *`);
                if (mode === 'week') setParam('cron', `${scheduleMinuteAt} ${scheduleHourAt} * * ${scheduleWeekdayAt}`);
                if (mode === 'month') setParam('cron', `${scheduleMinuteAt} ${scheduleHourAt} ${scheduleDayAt} ${toCronStep(scheduleMonthInterval)} *`);
              }}
              disabled={readOnly}
            >
              <MenuItem value="minute">Minute</MenuItem>
              <MenuItem value="hour">Hour</MenuItem>
              <MenuItem value="day">Day</MenuItem>
              <MenuItem value="week">Week</MenuItem>
              <MenuItem value="month">Month</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="schedule-interval-label">Interval</InputLabel>
            <Select
              labelId="schedule-interval-label"
              label="Interval"
              value={
                scheduleMode === 'minute' ? String(scheduleMinuteInterval)
                : scheduleMode === 'hour' ? String(scheduleHourInterval)
                : scheduleMode === 'day' ? String(scheduleDayInterval)
                : scheduleMode === 'week' ? String(scheduleWeekInterval)
                : String(scheduleMonthInterval)
              }
              onChange={(e) => {
                const interval = Math.max(1, Number(e.target.value));
                if (scheduleMode === 'minute') setParam('cron', `${toCronStep(interval)} * * * *`);
                if (scheduleMode === 'hour') setParam('cron', `${scheduleMinuteAt} ${toCronStep(interval)} * * *`);
                if (scheduleMode === 'day') setParam('cron', `${scheduleMinuteAt} ${scheduleHourAt} ${toCronStep(interval)} * *`);
                if (scheduleMode === 'week') {
                  setParam('week_interval', interval);
                  setParam('cron', `${scheduleMinuteAt} ${scheduleHourAt} * * ${scheduleWeekdayAt}`);
                }
                if (scheduleMode === 'month') setParam('cron', `${scheduleMinuteAt} ${scheduleHourAt} ${scheduleDayAt} ${toCronStep(interval)} *`);
              }}
              disabled={readOnly}
            >
              {Array.from(
                {
                  length:
                    scheduleMode === 'minute' ? 60
                    : scheduleMode === 'hour' ? 24
                    : scheduleMode === 'day' ? 31
                    : scheduleMode === 'week' ? 12
                    : 12,
                },
                (_, i) => i + 1
              ).map((n) => (
                <MenuItem key={`int-${n}`} value={String(n)}>
                  {`Every ${n} ${scheduleMode}${n > 1 ? 's' : ''}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {scheduleMode !== 'minute' && (
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="schedule-minute-label">Minute</InputLabel>
            <Select
              labelId="schedule-minute-label"
              label="Minute"
              value={scheduleMinuteAt}
              onChange={(e) => {
                const m = String(e.target.value);
                if (scheduleMode === 'hour') setParam('cron', `${m} ${toCronStep(scheduleHourInterval)} * * *`);
                if (scheduleMode === 'day') setParam('cron', `${m} ${scheduleHourAt} ${toCronStep(scheduleDayInterval)} * *`);
                if (scheduleMode === 'week') setParam('cron', `${m} ${scheduleHourAt} * * ${scheduleWeekdayAt}`);
                if (scheduleMode === 'month') setParam('cron', `${m} ${scheduleHourAt} ${scheduleDayAt} ${toCronStep(scheduleMonthInterval)} *`);
              }}
              disabled={readOnly}
            >
              {Array.from({ length: 60 }, (_, i) => (
                <MenuItem key={`min-${i}`} value={String(i)}>
                  {i.toString().padStart(2, '0')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          )}

          {(scheduleMode === 'day' || scheduleMode === 'week' || scheduleMode === 'month') && (
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="schedule-hour-label">Hour</InputLabel>
            <Select
              labelId="schedule-hour-label"
              label="Hour"
              value={scheduleHourAt}
              onChange={(e) => {
                const h = String(e.target.value);
                if (scheduleMode === 'day') setParam('cron', `${scheduleMinuteAt} ${h} ${toCronStep(scheduleDayInterval)} * *`);
                if (scheduleMode === 'week') setParam('cron', `${scheduleMinuteAt} ${h} * * ${scheduleWeekdayAt}`);
                if (scheduleMode === 'month') setParam('cron', `${scheduleMinuteAt} ${h} ${scheduleDayAt} ${toCronStep(scheduleMonthInterval)} *`);
              }}
              disabled={readOnly}
            >
              {Array.from({ length: 24 }, (_, i) => (
                <MenuItem key={`hour-${i}`} value={String(i)}>
                  {i.toString().padStart(2, '0')}:00
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          )}

          {scheduleMode === 'month' && (
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="schedule-day-label">Day of month</InputLabel>
            <Select
              labelId="schedule-day-label"
              label="Day of month"
              value={scheduleDayAt}
              onChange={(e) => {
                const day = String(e.target.value);
                setParam('cron', `${scheduleMinuteAt} ${scheduleHourAt} ${day} ${toCronStep(scheduleMonthInterval)} *`);
              }}
              disabled={readOnly}
            >
              {Array.from({ length: 31 }, (_, i) => (
                <MenuItem key={`day-${i + 1}`} value={String(i + 1)}>
                  {i + 1}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          )}

          {scheduleMode === 'week' && (
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="schedule-weekday-label">Day of week</InputLabel>
            <Select
              labelId="schedule-weekday-label"
              label="Day of week"
              value={scheduleWeekdayAt}
              onChange={(e) => {
                setParam('cron', `${scheduleMinuteAt} ${scheduleHourAt} * * ${String(e.target.value)}`);
              }}
              disabled={readOnly}
            >
              {SCHEDULE_WEEKDAYS.map((d) => (
                <MenuItem key={`weekday-${d.value}`} value={d.value}>
                  {d.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          )}

          <TextField
            label="Generated cron"
            size="small"
            fullWidth
            value={scheduleCronParts.join(' ')}
            InputProps={{ readOnly: true }}
            sx={{ mb: 1 }}
          />
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="schedule-timezone-label">Timezone</InputLabel>
            <Select
              labelId="schedule-timezone-label"
              label="Timezone"
              value={params.timezone ?? 'UTC'}
              onChange={(e) => setParam('timezone', e.target.value)}
              disabled={readOnly}
            >
              {['UTC', 'Asia/Manila', 'Asia/Singapore', 'Asia/Jakarta', 'Europe/London', 'America/New_York'].map((tz) => (
                <MenuItem key={tz} value={tz}>
                  {tz}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Choose interval and timing fields. Weekly interval is stored for UI clarity.
          </Typography>
        </>
      )}

      {/* Trigger: n8n Chat */}
      {nodeType === 'n8n-nodes-base.chatTrigger' && (
        <>
          <TextField
            label="Session Key"
            size="small"
            fullWidth
            value={params.sessionKey ?? '{{sessionId}}'}
            onChange={(e) => setParam('sessionKey', e.target.value)}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="n8n-session-id-type">Session ID Type</InputLabel>
            <Select
              labelId="n8n-session-id-type"
              label="Session ID Type"
              value={params.sessionIdType ?? 'customKey'}
              onChange={(e) => setParam('sessionIdType', e.target.value)}
            >
              <MenuItem value="customKey">customKey</MenuItem>
              <MenuItem value="auto">auto</MenuItem>
            </Select>
          </FormControl>
        </>
      )}

      {/* Execute Workflow Trigger (Start / input definitions) */}
      {nodeType === 'n8n-nodes-base.executeWorkflowTrigger' && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Define the input fields this workflow accepts when called by another workflow.
          </Typography>
          {(() => {
            const inputs: Array<{ name: string }> = Array.isArray(params.workflowInputs?.values)
              ? params.workflowInputs.values.map((v: any) => ({ name: String(v?.name ?? '') }))
              : [];
            const setInputs = (next: Array<{ name: string }>) => {
              setParam('workflowInputs', { ...params.workflowInputs, values: next });
            };
            return (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Input Fields</Typography>
                <Stack spacing={1} sx={{ mb: 1 }}>
                  {inputs.map((inp, idx) => (
                    <Box key={`wft-${idx}`} display="flex" gap={1} alignItems="center">
                      <TextField
                        label="Field name"
                        size="small"
                        fullWidth
                        value={inp.name}
                        onChange={(e) => {
                          const next = [...inputs];
                          next[idx] = { name: e.target.value };
                          setInputs(next);
                        }}
                        disabled={readOnly}
                      />
                      <IconButton
                        aria-label="remove"
                        onClick={() => setInputs(inputs.filter((_, i) => i !== idx))}
                        disabled={readOnly}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}
                </Stack>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setInputs([...inputs, { name: '' }])}
                  disabled={readOnly}
                >
                  Add field
                </Button>
              </Box>
            );
          })()}
        </>
      )}

      {/* Execute Workflow (Call another workflow) */}
      {nodeType === 'n8n-nodes-base.executeWorkflow' && (
        <>
          <TextField
            label="Workflow ID"
            size="small"
            fullWidth
            value={getRlValue(params.workflowId)}
            onChange={(e) => setParam('workflowId', setRlValue(params.workflowId, e.target.value))}
            sx={{ mb: 2 }}
            helperText={params.workflowId?.cachedResultName ? `Name: ${params.workflowId.cachedResultName}` : 'Enter the UUID or n8n workflow ID'}
            disabled={readOnly}
          />
          {(() => {
            const inputMap: Record<string, string> =
              params.workflowInputs?.value && typeof params.workflowInputs.value === 'object'
                ? params.workflowInputs.value
                : {};
            const entries = Object.entries(inputMap);
            const setEntry = (key: string, value: string) => {
              setParam('workflowInputs', {
                ...params.workflowInputs,
                value: { ...inputMap, [key]: value },
              });
            };
            const removeEntry = (key: string) => {
              const next = { ...inputMap };
              delete next[key];
              setParam('workflowInputs', { ...params.workflowInputs, value: next });
            };
            const addEntry = () => setEntry('', '');
            return (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Input Mappings</Typography>
                <Stack spacing={1}>
                  {entries.map(([k, v], idx) => (
                    <Box key={`ewf-${idx}`} display="flex" gap={1} alignItems="center">
                      <TextField
                        label="Field"
                        size="small"
                        fullWidth
                        value={k}
                        onChange={(e) => {
                          removeEntry(k);
                          setEntry(e.target.value, v);
                        }}
                        disabled={readOnly}
                      />
                      <TextField
                        label="Value / Expression"
                        size="small"
                        fullWidth
                        value={v}
                        onChange={(e) => setEntry(k, e.target.value)}
                        disabled={readOnly}
                      />
                      <IconButton aria-label="remove" onClick={() => removeEntry(k)} disabled={readOnly}>
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}
                  <Button size="small" startIcon={<AddIcon />} onClick={addEntry} disabled={readOnly}>
                    Add mapping
                  </Button>
                </Stack>
              </Box>
            );
          })()}
        </>
      )}

      {/* n8n Agent */}
      {nodeType === '@n8n/n8n-nodes-langchain.agent' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="agent-prompt-type-label">Prompt Source</InputLabel>
            <Select
              labelId="agent-prompt-type-label"
              label="Prompt Source"
              value={params.promptType ?? 'auto'}
              onChange={(e) => setParam('promptType', e.target.value)}
              disabled={readOnly}
            >
              <MenuItem value="auto">Auto (from connected input)</MenuItem>
              <MenuItem value="define">Define manually</MenuItem>
            </Select>
          </FormControl>
          {(params.promptType === 'define' || !params.promptType) && (
            <TextField
              label="User Prompt"
              size="small"
              fullWidth
              multiline
              minRows={3}
              value={params.text ?? '={{ $json.chatInput }}'}
              onChange={(e) => setParam('text', e.target.value)}
              sx={{ mb: 2 }}
              helperText="Supports n8n expressions like ={{ $json.chatInput }}"
              disabled={readOnly}
            />
          )}
          <TextField
            label="System Message"
            size="small"
            fullWidth
            multiline
            minRows={4}
            value={params.options?.systemMessage ?? params.systemMessage ?? ''}
            onChange={(e) => {
              setParam('options', { ...(params.options ?? {}), systemMessage: e.target.value });
            }}
            sx={{ mb: 2 }}
            helperText="Defines the agent's role and behaviour"
            disabled={readOnly}
          />
          <TextField
            label="Max Iterations"
            size="small"
            fullWidth
            type="number"
            value={params.maxIterations ?? params.options?.maxIterations ?? 10}
            onChange={(e) => setParam('maxIterations', Number(e.target.value))}
            sx={{ mb: 2 }}
            disabled={readOnly}
          />
        </>
      )}

      {/* n8n Memory Buffer Window */}
      {nodeType === '@n8n/n8n-nodes-langchain.memoryBufferWindow' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="mem-session-id-type-label">Session ID Type</InputLabel>
            <Select
              labelId="mem-session-id-type-label"
              label="Session ID Type"
              value={params.sessionIdType ?? 'customKey'}
              onChange={(e) => setParam('sessionIdType', e.target.value)}
              disabled={readOnly}
            >
              <MenuItem value="customKey">Custom Key</MenuItem>
              <MenuItem value="auto">Auto (from input)</MenuItem>
            </Select>
          </FormControl>
          {(params.sessionIdType === 'customKey' || !params.sessionIdType || params.sessionIdType === 'customKey') && (
            <TextField
              label="Session Key"
              size="small"
              fullWidth
              value={params.sessionKey ?? '={{ $json.sessionId }}'}
              onChange={(e) => setParam('sessionKey', e.target.value)}
              sx={{ mb: 2 }}
              helperText="Expression that resolves to the session identifier"
              disabled={readOnly}
            />
          )}
          <TextField
            label="Window Size (messages)"
            size="small"
            fullWidth
            type="number"
            value={params.windowSize ?? 10}
            onChange={(e) => setParam('windowSize', Number(e.target.value))}
            sx={{ mb: 2 }}
            helperText="Number of past messages to keep in memory"
            disabled={readOnly}
          />
        </>
      )}

      {/* n8n Postgres Tool */}
      {nodeType === 'n8n-nodes-base.postgresTool' && (
        <>
          <TextField
            label="Tool Description"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={params.toolDescription ?? ''}
            onChange={(e) => setParam('toolDescription', e.target.value)}
            sx={{ mb: 2 }}
            helperText="Describe what this tool does so the AI agent can decide when to use it"
            disabled={readOnly}
          />
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="pg-tool-op-label">Operation</InputLabel>
            <Select
              labelId="pg-tool-op-label"
              label="Operation"
              value={params.operation ?? 'executeQuery'}
              onChange={(e) => setParam('operation', e.target.value)}
              disabled={readOnly}
            >
              <MenuItem value="executeQuery">Execute Query</MenuItem>
              <MenuItem value="select">Select</MenuItem>
              <MenuItem value="insert">Insert</MenuItem>
              <MenuItem value="update">Update</MenuItem>
              <MenuItem value="delete">Delete</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="SQL Query"
            fullWidth
            multiline
            minRows={5}
            value={params.query ?? ''}
            onChange={(e) => setParam('query', e.target.value)}
            sx={{ mb: 2 }}
            helperText="Use $fromAI('param', 'desc') to let the AI fill values, e.g. {{ $fromAI('sql_query', 'SQL query') }}"
            disabled={readOnly}
          />
        </>
      )}

      {/* n8n PGVector Store */}
      {nodeType === '@n8n/n8n-nodes-langchain.vectorStorePGVector' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="pgvec-mode-label">Mode</InputLabel>
            <Select
              labelId="pgvec-mode-label"
              label="Mode"
              value={params.mode ?? 'retrieve-as-tool'}
              onChange={(e) => setParam('mode', e.target.value)}
              disabled={readOnly}
            >
              <MenuItem value="retrieve-as-tool">Retrieve as Tool (AI agent)</MenuItem>
              <MenuItem value="load">Load (insert documents)</MenuItem>
              <MenuItem value="retrieve">Retrieve (query)</MenuItem>
              <MenuItem value="update">Update</MenuItem>
              <MenuItem value="delete">Delete</MenuItem>
            </Select>
          </FormControl>
          {(params.mode === 'retrieve-as-tool' || !params.mode) && (
            <TextField
              label="Tool Description"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={params.toolDescription ?? ''}
              onChange={(e) => setParam('toolDescription', e.target.value)}
              sx={{ mb: 2 }}
              helperText="Describe what knowledge this vector store contains"
              disabled={readOnly}
            />
          )}
          <TextField
            label="Table Name"
            size="small"
            fullWidth
            value={params.tableName ?? 'n8n_knowledges'}
            onChange={(e) => setParam('tableName', e.target.value)}
            sx={{ mb: 2 }}
            disabled={readOnly}
          />
          <TextField
            label="Top K (results)"
            size="small"
            fullWidth
            type="number"
            value={params.topK ?? 10}
            onChange={(e) => setParam('topK', Number(e.target.value))}
            sx={{ mb: 2 }}
            helperText="Number of similar documents to return"
            disabled={readOnly}
          />
        </>
      )}

      {/* n8n OpenAI Chat Model */}
      {nodeType === '@n8n/n8n-nodes-langchain.lmChatOpenAi' && (
        <>
          <TextField
            label="Model"
            size="small"
            fullWidth
            value={getRlValue(params.model) || 'gpt-4o-mini'}
            onChange={(e) => setParam('model', setRlValue(params.model, e.target.value))}
            sx={{ mb: 2 }}
            helperText="e.g. gpt-4o, gpt-4o-mini, gpt-4-turbo, gpt-3.5-turbo"
            disabled={readOnly}
          />
          <TextField
            label="Temperature"
            size="small"
            fullWidth
            type="number"
            value={params.options?.temperature ?? params.temperature ?? ''}
            onChange={(e) =>
              setParam('options', { ...(params.options ?? {}), temperature: e.target.value === '' ? undefined : Number(e.target.value) })
            }
            sx={{ mb: 2 }}
            helperText="0 = deterministic, 1 = creative (leave blank for default)"
            disabled={readOnly}
          />
          <TextField
            label="Max Tokens"
            size="small"
            fullWidth
            type="number"
            value={params.options?.maxTokens ?? params.maxTokens ?? ''}
            onChange={(e) =>
              setParam('options', { ...(params.options ?? {}), maxTokens: e.target.value === '' ? undefined : Number(e.target.value) })
            }
            sx={{ mb: 2 }}
            helperText="Maximum tokens in the response (leave blank for default)"
            disabled={readOnly}
          />
        </>
      )}

      {/* n8n OpenAI Embeddings */}
      {nodeType === '@n8n/n8n-nodes-langchain.embeddingsOpenAi' && (
        <>
          <TextField
            label="Model"
            size="small"
            fullWidth
            value={getRlValue(params.model) || 'text-embedding-3-small'}
            onChange={(e) => setParam('model', setRlValue(params.model, e.target.value))}
            sx={{ mb: 2 }}
            helperText="e.g. text-embedding-3-small, text-embedding-3-large, text-embedding-ada-002"
            disabled={readOnly}
          />
          <TextField
            label="Dimensions"
            size="small"
            fullWidth
            type="number"
            value={params.options?.dimensions ?? ''}
            onChange={(e) =>
              setParam('options', { ...(params.options ?? {}), dimensions: e.target.value === '' ? undefined : Number(e.target.value) })
            }
            sx={{ mb: 2 }}
            helperText="Output embedding dimensions (e.g. 1024, 1536 — leave blank for model default)"
            disabled={readOnly}
          />
        </>
      )}

      {/* Action: HTTP */}
      {nodeType === 'action.http' && (
        <>
          <TextField
            label="URL"
            size="small"
            fullWidth
            value={params.url ?? ''}
            onChange={(e) => setParam('url', e.target.value)}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="http-method-label">Method</InputLabel>
            <Select
              labelId="http-method-label"
              label="Method"
              value={params.method ?? 'GET'}
              onChange={(e) => setParam('method', e.target.value)}
            >
              {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((m) => (
                <MenuItem key={m} value={m}>
                  {m}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {renderKeyValueEditor('http.headers', 'Headers')}
          {renderKeyValueEditor('http.body', 'Body')}
        </>
      )}

      {/* Action: Email */}
      {nodeType === 'action.email' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="email-credential-label">Credential preset</InputLabel>
            <Select
              labelId="email-credential-label"
              label="Credential preset"
              value={params.credential_id ?? ''}
              onChange={(e) => setParam('credential_id', e.target.value)}
              onOpen={() => loadWorkflowCredentials()} disabled={readOnly || credentialsLoading}
            >
              <MenuItem value="">None (use default/system email)</MenuItem>
              {smtpCredentials.map((cred) => (
                <MenuItem key={cred.id} value={cred.id}>
                  {cred.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Select SMTP credential preset to send via saved account.
          </Typography>
          <TextField
            label="From (optional)"
            size="small"
            fullWidth
            value={params.from_email ?? ''}
            onChange={(e) => setParam('from_email', e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="To"
            size="small"
            fullWidth
            value={params.to_email ?? ''}
            onChange={(e) => setParam('to_email', e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Subject"
            size="small"
            fullWidth
            value={params.subject ?? ''}
            onChange={(e) => setParam('subject', e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Body"
            fullWidth
            multiline
            minRows={4}
            value={params.body ?? ''}
            onChange={(e) => setParam('body', e.target.value)}
            sx={{ mb: 2 }}
          />
        </>
      )}

      {/* Action: Odoo */}
      {nodeType === 'action.odoo' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="odoo-credential-label">Credential preset</InputLabel>
            <Select
              labelId="odoo-credential-label"
              label="Credential preset"
              value={params.credential_id ?? ''}
              onChange={(e) => {
                setParam('credential_id', e.target.value);
                // Reset cached model/field options so the dropdowns
                // re-fetch against the newly selected credential.
                setOdooModelOptions([]);
                setOdooFieldOptions([]);
                setOdooModelsWarning('');
                setOdooFieldsWarning('');
              }}
              onOpen={() => loadWorkflowCredentials()}
              disabled={readOnly || credentialsLoading}
            >
              <MenuItem value="">None (use inline fields)</MenuItem>
              {odooCredentials.map((cred) => (
                <MenuItem key={cred.id} value={cred.id}>
                  {cred.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Create/manage credentials in the Workflow Credentials page (uses
            username + password). Selecting a credential here lets us load
            your Odoo models and fields automatically.
          </Typography>

          {/* Model: searchable Autocomplete + manual fallback */}
          <Autocomplete
            size="small"
            freeSolo
            options={odooModelOptions}
            getOptionLabel={(opt) =>
              typeof opt === 'string' ? opt : opt.model || ''
            }
            isOptionEqualToValue={(opt, val) =>
              (typeof opt === 'string' ? opt : opt.model) ===
              (typeof val === 'string' ? val : val.model)
            }
            value={params.model ?? ''}
            onInputChange={(_evt, newInput) => {
              setOdooModelQuery(newInput);
              setParam('model', newInput);
            }}
            onChange={(_evt, picked) => {
              const next = typeof picked === 'string' ? picked : picked?.model || '';
              setParam('model', next);
              if (next) void fetchOdooFields(next);
            }}
            onOpen={() => {
              if (!odooModelsLoading && odooModelOptions.length === 0) {
                void fetchOdooModels();
              }
            }}
            loading={odooModelsLoading}
            disabled={readOnly}
            renderOption={(props, opt) => (
              <li {...props} key={opt.model}>
                <Box>
                  <Typography variant="body2">{opt.model}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {opt.name}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={(p) => (
              <TextField
                {...p}
                label="Model"
                helperText={
                  odooModelsWarning
                    ? odooModelsWarning
                    : odooModelsLoading
                    ? 'Loading models from this Odoo instance…'
                    : odooModelsCount > 0
                    ? `Loaded ${odooModelsCount} model${
                        odooModelsCount === 1 ? '' : 's'
                      } from this Odoo instance.${
                        odooModelsTruncated
                          ? ' (More exist — type to narrow the list further.)'
                          : ''
                      }`
                    : 'Type to search (e.g. partner, sale.order). Picks from your live Odoo instance once a credential is selected.'
                }
                InputProps={{
                  ...p.InputProps,
                  endAdornment: (
                    <>
                      {odooModelsLoading ? <CircularProgress size={16} /> : null}
                      <Tooltip title="Reload model list">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => void fetchOdooModels()}
                            disabled={readOnly || !params.credential_id}
                          >
                            <RefreshIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {p.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            sx={{ mb: 2 }}
          />

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="odoo-operation-label">Operation</InputLabel>
            <Select
              labelId="odoo-operation-label"
              label="Operation"
              value={params.operation ?? 'search_read'}
              onChange={(e) => setParam('operation', e.target.value)}
            >
              {['search_read', 'search', 'get', 'create', 'update', 'delete', 'count'].map((op) => (
                <MenuItem key={op} value={op}>
                  {op}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Record IDs"
            size="small"
            fullWidth
            value={params.record_ids ?? params.record_id ?? ''}
            onChange={(e) => setParam('record_ids', e.target.value)}
            sx={{ mb: 2 }}
            helperText="Single id, comma list, or JSON list. Templates work: {{ body.id }}"
          />

          {/* Domain: form-based builder with raw-JSON escape hatch */}
          <Box sx={{ mb: 2 }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 1 }}
            >
              <Typography variant="subtitle2">Domain (filter)</Typography>
              <Box display="flex" alignItems="center" gap={1}>
                {!odooDomainRawMode && (
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={odooDomainMode}
                    onChange={(_e, v) => {
                      if (v === 'AND' || v === 'OR') {
                        setOdooDomainRows(odooDomainRows, v);
                      }
                    }}
                  >
                    <ToggleButton value="AND">Match ALL (AND)</ToggleButton>
                    <ToggleButton value="OR">Match ANY (OR)</ToggleButton>
                  </ToggleButtonGroup>
                )}
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={odooDomainRawMode}
                      onChange={(_e, checked) => setOdooDomainRawMode(checked)}
                      disabled={readOnly}
                    />
                  }
                  label="Raw JSON"
                />
              </Box>
            </Box>

            {odooDomainRawMode ? (
              <TextField
                fullWidth
                multiline
                minRows={3}
                value={odooDomainText}
                onChange={(e) => setOdooDomainText(e.target.value)}
                onBlur={() => {
                  try {
                    if (!odooDomainText.trim()) {
                      setParam('domain', []);
                      setOdooDomainText('[]');
                      return;
                    }
                    const parsed = safeJsonParse(odooDomainText);
                    if (!Array.isArray(parsed)) {
                      throw new Error('Domain must be an array');
                    }
                    setParam('domain', parsed);
                  } catch (err: any) {
                    enqueueSnackbar(err.message || 'Invalid domain JSON', { variant: 'error' });
                    setOdooDomainText(JSON.stringify(params.domain ?? [], null, 2));
                  }
                }}
                helperText="Power-user mode: enter the raw Odoo domain list, including '|' / '&' polish-notation operators."
              />
            ) : (
              <>
                <Stack spacing={1}>
                  {odooDomainRows.map((row, idx) => (
                    <Box
                      key={`odoo-domain-${idx}`}
                      display="flex"
                      gap={1}
                      alignItems="center"
                    >
                      <Autocomplete
                        size="small"
                        freeSolo
                        options={odooFieldOptions}
                        getOptionLabel={(opt) =>
                          typeof opt === 'string' ? opt : opt.name || ''
                        }
                        value={row.field}
                        onInputChange={(_e, v) => {
                          const next = [...odooDomainRows];
                          next[idx] = { ...row, field: v };
                          setOdooDomainRows(next);
                        }}
                        loading={odooFieldsLoading}
                        sx={{ flex: 2 }}
                        renderOption={(props, opt) => (
                          <li {...props} key={opt.name}>
                            <Box>
                              <Typography variant="body2">{opt.name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {opt.label} · {opt.type}
                              </Typography>
                            </Box>
                          </li>
                        )}
                        renderInput={(p) => <TextField {...p} label="Field" />}
                        disabled={readOnly}
                      />
                      <FormControl size="small" sx={{ minWidth: 110 }}>
                        <Select
                          value={row.operator || '='}
                          onChange={(e) => {
                            const next = [...odooDomainRows];
                            next[idx] = { ...row, operator: String(e.target.value) };
                            setOdooDomainRows(next);
                          }}
                          disabled={readOnly}
                        >
                          {ODOO_DOMAIN_OPERATORS.map((op) => (
                            <MenuItem key={op} value={op}>
                              {op}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <TextField
                        size="small"
                        label="Value"
                        sx={{ flex: 2 }}
                        value={row.value}
                        onChange={(e) => {
                          const next = [...odooDomainRows];
                          next[idx] = { ...row, value: e.target.value };
                          setOdooDomainRows(next);
                        }}
                        disabled={readOnly}
                        placeholder="{{ body.id }} or literal value"
                      />
                      <IconButton
                        size="small"
                        onClick={() => {
                          const next = odooDomainRows.filter((_, i) => i !== idx);
                          setOdooDomainRows(next);
                        }}
                        disabled={readOnly}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}
                </Stack>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() =>
                    setOdooDomainRows([
                      ...odooDomainRows,
                      { field: '', operator: '=', value: '' },
                    ])
                  }
                  disabled={readOnly}
                  sx={{ mt: 1 }}
                >
                  Add condition
                </Button>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 1 }}
                >
                  Values are auto-coerced: numbers / true / false / null /
                  [1,2,3] become typed; everything else is a string. Templates
                  like <code>{'{{ body.customer_id }}'}</code> are kept verbatim
                  and rendered at run-time.
                </Typography>
              </>
            )}
          </Box>

          {/* Fields: multi-select chips sourced from fields_get */}
          <Autocomplete
            multiple
            freeSolo
            size="small"
            options={odooFieldOptions}
            getOptionLabel={(opt) =>
              typeof opt === 'string' ? opt : opt.name || ''
            }
            value={Array.isArray(params.fields) ? params.fields : []}
            onChange={(_e, picked) => {
              const next = (picked || []).map((p: any) =>
                typeof p === 'string' ? p : p.name
              );
              setParam('fields', next);
              setOdooFieldsText(JSON.stringify(next, null, 2));
            }}
            renderOption={(props, opt) => (
              <li {...props} key={opt.name}>
                <Box>
                  <Typography variant="body2">{opt.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {opt.label} · {opt.type}
                  </Typography>
                </Box>
              </li>
            )}
            renderTags={(value: readonly string[], getTagProps) =>
              value.map((option: string, index: number) => (
                <Chip
                  variant="outlined"
                  label={option}
                  size="small"
                  {...getTagProps({ index })}
                  key={`${option}-${index}`}
                />
              ))
            }
            loading={odooFieldsLoading}
            disabled={readOnly}
            renderInput={(p) => (
              <TextField
                {...p}
                label="Fields to return"
                helperText={
                  odooFieldsWarning ||
                  'Pick fields from your model, or type custom names + Enter. Leave empty to get the default set.'
                }
                InputProps={{
                  ...p.InputProps,
                  endAdornment: (
                    <>
                      {odooFieldsLoading ? <CircularProgress size={16} /> : null}
                      <Tooltip title="Reload field schema">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() =>
                              void fetchOdooFields(String(params.model || ''))
                            }
                            disabled={readOnly || !params.model}
                          >
                            <RefreshIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {p.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            sx={{ mb: 2 }}
          />

          {/* Values: KV editor with field-name autocomplete on the key */}
          <OdooValuesEditor
            values={
              params.values && typeof params.values === 'object'
                ? params.values
                : {}
            }
            onChange={(next) => setParam('values', next)}
            fieldOptions={odooFieldOptions}
            readOnly={readOnly}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: -1, mb: 2 }}
          >
            Used by create / update. Values support templates:{' '}
            <code>{'{{ body.name }}'}</code>,{' '}
            <code>{'={{ body.amount }}'}</code> (typed number).
          </Typography>
          <Box display="flex" gap={2}>
            <TextField
              label="Limit"
              size="small"
              type="number"
              fullWidth
              value={params.limit ?? 0}
              onChange={(e) => setParam('limit', e.target.value)}
            />
            <TextField
              label="Offset"
              size="small"
              type="number"
              fullWidth
              value={params.offset ?? 0}
              onChange={(e) => setParam('offset', e.target.value)}
            />
          </Box>
        </>
      )}

      {/* Action: LLM Chat */}
      {nodeType === 'action.llm.chat' && (
        <>
          <TextField
            label="Prompt"
            fullWidth
            multiline
            minRows={5}
            value={params.prompt ?? ''}
            onChange={(e) => setParam('prompt', e.target.value)}
            sx={{ mb: 2 }}
            helperText="You can reference trigger input like {{body.question}}"
          />
          <TextField
            label="System prompt (optional)"
            fullWidth
            multiline
            minRows={3}
            value={params.system_prompt ?? ''}
            onChange={(e) => setParam('system_prompt', e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Model override (optional)"
            size="small"
            fullWidth
            value={params.model ?? ''}
            onChange={(e) => setParam('model', e.target.value)}
            helperText="Leave blank to use Settings → LLM default model."
          />
        </>
      )}

      {/* Action: PostgreSQL */}
      {nodeType === 'action.postgresql' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="pg-credential-label">Credential preset</InputLabel>
            <Select
              labelId="pg-credential-label"
              label="Credential preset"
              value={params.credential_id ?? ''}
              onChange={(e) => setParam('credential_id', e.target.value)}
              onOpen={() => loadWorkflowCredentials()} disabled={readOnly || credentialsLoading}
            >
              <MenuItem value="">None (use inline fields)</MenuItem>
              {postgresCredentials.map((cred) => (
                <MenuItem key={cred.id} value={cred.id}>
                  {cred.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Create/manage credentials in Workflow Credentials page, then select it here.
          </Typography>
          <FormControl fullWidth size="small" sx={{ mt: 2, mb: 2 }}>
            <InputLabel id="pg-query-type-label">Query type</InputLabel>
            <Select
              labelId="pg-query-type-label"
              label="Query type"
              value={params.query_type ?? 'select'}
              onChange={(e) => setParam('query_type', e.target.value)}
            >
              {['select', 'insert', 'update', 'delete', 'execute'].map((op) => (
                <MenuItem key={op} value={op}>
                  {op}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Query"
            fullWidth
            multiline
            minRows={4}
            value={params.query ?? ''}
            onChange={(e) => setParam('query', e.target.value)}
            sx={{ mb: 2 }}
          />
        </>
      )}

      {/* Action: Supabase */}
      {nodeType === 'action.supabase' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="supabase-credential-label">Credential preset</InputLabel>
            <Select
              labelId="supabase-credential-label"
              label="Credential preset"
              value={params.credential_id ?? ''}
              onChange={(e) => setParam('credential_id', e.target.value)}
              onOpen={() => loadWorkflowCredentials()} disabled={readOnly || credentialsLoading}
            >
              <MenuItem value="">None (use inline fields)</MenuItem>
              {supabaseCredentials.map((cred) => (
                <MenuItem key={cred.id} value={cred.id}>
                  {cred.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Create/manage credentials in Workflow Credentials page, then select it here.
          </Typography>
          <TextField
            label="Table"
            size="small"
            fullWidth
            value={params.table ?? ''}
            onChange={(e) => setParam('table', e.target.value)}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="supabase-operation-label">Operation</InputLabel>
            <Select
              labelId="supabase-operation-label"
              label="Operation"
              value={params.operation ?? 'select'}
              onChange={(e) => setParam('operation', e.target.value)}
            >
              {['select', 'insert', 'update', 'delete', 'rpc'].map((op) => (
                <MenuItem key={op} value={op}>
                  {op}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {params.operation === 'rpc' && (
            <TextField
              label="RPC Name"
              size="small"
              fullWidth
              value={params.rpc_name ?? ''}
              onChange={(e) => setParam('rpc_name', e.target.value)}
              sx={{ mb: 2 }}
            />
          )}
          <TextField
            label="Select"
            size="small"
            fullWidth
            value={params.select ?? '*'}
            onChange={(e) => setParam('select', e.target.value)}
            sx={{ mb: 2 }}
          />
          {renderKeyValueEditor('supabase.filters', 'Filters')}
          {renderKeyValueEditor('supabase.values', 'Values')}
          <Box display="flex" gap={2}>
            <TextField
              label="Limit"
              size="small"
              type="number"
              fullWidth
              value={params.limit ?? 10}
              onChange={(e) => setParam('limit', Number(e.target.value))}
            />
            <TextField
              label="Order"
              size="small"
              fullWidth
              value={params.order ?? ''}
              onChange={(e) => setParam('order', e.target.value)}
            />
          </Box>
        </>
      )}

      {/* Action: Gmail */}
      {nodeType === 'action.gmail' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="gmail-credential-label">Credential preset</InputLabel>
            <Select
              labelId="gmail-credential-label"
              label="Credential preset"
              value={params.credential_id ?? ''}
              onChange={(e) => setParam('credential_id', e.target.value)}
              onOpen={() => loadWorkflowCredentials()} disabled={readOnly || credentialsLoading}
            >
              <MenuItem value="">None (use inline fields)</MenuItem>
              {gmailCredentials.map((cred) => (
                <MenuItem key={cred.id} value={cred.id}>
                  {cred.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Create/manage credentials in Workflow Credentials page, then select it here.
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="gmail-operation-label">Operation</InputLabel>
            <Select
              labelId="gmail-operation-label"
              label="Operation"
              value={params.operation ?? 'list'}
              onChange={(e) => setParam('operation', e.target.value)}
            >
              {[
                { value: 'list',                  label: 'List messages' },
                { value: 'get',                   label: 'Get message' },
                { value: 'send',                  label: 'Send' },
                { value: 'reply',                 label: 'Reply (same thread)' },
                { value: 'send_and_wait',         label: 'Send & wait for reply' },
                { value: 'list_attachments',      label: 'List attachments (metadata only)' },
                { value: 'download_attachments',  label: 'Download attachment(s)' },
              ].map((op) => (
                <MenuItem key={op.value} value={op.value}>
                  {op.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* list / get inputs */}
          {(params.operation ?? 'list') === 'list' && (
            <TextField
              label="Query (Gmail search syntax, e.g. is:unread)"
              size="small"
              fullWidth
              value={params.query ?? ''}
              onChange={(e) => setParam('query', e.target.value)}
              sx={{ mb: 2 }}
            />
          )}
          {params.operation === 'get' && (
            <TextField
              label="Message ID"
              size="small"
              fullWidth
              value={params.message_id ?? ''}
              onChange={(e) => setParam('message_id', e.target.value)}
              sx={{ mb: 2 }}
            />
          )}

          {/* list_attachments: metadata-only listing, no bytes */}
          {params.operation === 'list_attachments' && (
            <>
              <TextField
                label="Message ID"
                size="small"
                fullWidth
                value={params.message_id ?? ''}
                onChange={(e) => setParam('message_id', e.target.value)}
                placeholder="19a1b2c3d4e5 • or full Gmail URL • or <...@mail.gmail.com>"
                helperText='Gmail API id (hex), Gmail URL, or RFC <...@mail.gmail.com> header — all are accepted. Templates work: {{body.message_id}} from an upstream Gmail list/get node.'
                sx={{ mb: 2 }}
                disabled={readOnly}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Returns &#123;message_id, count, attachments: [&#123;filename,
                content_type, size, attachment_id, disposition&#125;]&#125; —
                no bytes are downloaded. Use this to decide what to pull
                before calling <em>Download attachment(s)</em>.
              </Typography>
            </>
          )}

          {/* download_attachments: single or bulk fetch with safety caps */}
          {params.operation === 'download_attachments' && (
            <>
              <TextField
                label="Message ID (required)"
                size="small"
                fullWidth
                value={params.message_id ?? ''}
                onChange={(e) => setParam('message_id', e.target.value)}
                placeholder="19a1b2c3d4e5 • or full Gmail URL • or <...@mail.gmail.com>"
                helperText='Gmail API id (hex), Gmail URL, or RFC <...@mail.gmail.com> header — all are accepted. Templates also work: {{body.message_id}}.'
                sx={{ mb: 2 }}
                disabled={readOnly}
              />
              <TextField
                label="Attachment ID (optional — leave blank to pull all)"
                size="small"
                fullWidth
                value={params.attachment_id ?? ''}
                onChange={(e) => setParam('attachment_id', e.target.value)}
                helperText="Paste a specific id for a single file, or leave blank to auto-pull every attachment on the message."
                sx={{ mb: 2 }}
                disabled={readOnly}
              />
              <TextField
                label="Filename filter (glob, optional)"
                size="small"
                fullWidth
                value={params.filename_filter ?? ''}
                onChange={(e) => setParam('filename_filter', e.target.value)}
                helperText='fnmatch-style glob, e.g. "*.pdf" or "invoice-*.xlsx". Case-insensitive. Ignored when an Attachment ID is set.'
                sx={{ mb: 2 }}
                disabled={readOnly}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(params.include_inline)}
                    onChange={(e) => setParam('include_inline', e.target.checked)}
                    disabled={readOnly}
                  />
                }
                label="Include inline images (off by default)"
                sx={{ mb: 1 }}
              />
              <TextField
                label="Max total bytes (safety cap)"
                size="small"
                type="number"
                fullWidth
                value={params.max_total_bytes ?? ''}
                onChange={(e) => setParam('max_total_bytes', e.target.value)}
                helperText="Total cumulative download size. Defaults to 52428800 (50 MiB). Anything over the cap is reported under output.skipped instead of being fetched."
                sx={{ mb: 2 }}
                disabled={readOnly}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Returns &#123;count, total_bytes, attachments: [&#123;filename,
                content_type, size, attachment_id, disposition, data_base64&#125;],
                skipped: [...]&#125;. Pipe <code>data_base64</code> straight to{' '}
                <code>core.file.write</code>, an HTTP upload, or Google Drive
                — no separate HTTP node required.
              </Typography>
            </>
          )}

          {/* Compose fields shared by send / reply / send_and_wait */}
          {(params.operation === 'send' ||
            params.operation === 'reply' ||
            params.operation === 'send_and_wait') && (
            <>
              {/* For reply, message_id is required. For send_and_wait it's
                  optional and turns the initial send into a reply on the
                  given thread. */}
              {(params.operation === 'reply' || params.operation === 'send_and_wait') && (
                <TextField
                  label={
                    params.operation === 'reply'
                      ? 'Message ID to reply to (required)'
                      : 'Message ID (optional — sends as reply if set)'
                  }
                  size="small"
                  fullWidth
                  value={params.message_id ?? ''}
                  onChange={(e) => setParam('message_id', e.target.value)}
                  sx={{ mb: 2 }}
                />
              )}

              <TextField
                label={
                  params.operation === 'reply'
                    ? 'To (optional — defaults to original sender)'
                    : 'To'
                }
                size="small"
                fullWidth
                value={params.to_email ?? ''}
                onChange={(e) => setParam('to_email', e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                label="Cc"
                size="small"
                fullWidth
                value={params.cc ?? ''}
                onChange={(e) => setParam('cc', e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                label="Bcc"
                size="small"
                fullWidth
                value={params.bcc ?? ''}
                onChange={(e) => setParam('bcc', e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                label="From (optional — overrides default sender)"
                size="small"
                fullWidth
                value={params.from_email ?? ''}
                onChange={(e) => setParam('from_email', e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                label={
                  params.operation === 'reply'
                    ? 'Subject (optional — auto-prefixed with Re:)'
                    : 'Subject'
                }
                size="small"
                fullWidth
                value={params.subject ?? ''}
                onChange={(e) => setParam('subject', e.target.value)}
                sx={{ mb: 2 }}
              />
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel id="gmail-bodytype-label">Body type</InputLabel>
                <Select
                  labelId="gmail-bodytype-label"
                  label="Body type"
                  value={params.body_type ?? 'text'}
                  onChange={(e) => setParam('body_type', e.target.value)}
                  disabled={readOnly}
                >
                  <MenuItem value="text">Plain text</MenuItem>
                  <MenuItem value="html">HTML</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label={
                  (params.body_type ?? 'text') === 'html'
                    ? 'HTML body source'
                    : 'Body'
                }
                fullWidth
                multiline
                minRows={(params.body_type ?? 'text') === 'html' ? 8 : 4}
                value={params.body ?? ''}
                onChange={(e) => setParam('body', e.target.value)}
                helperText={
                  (params.body_type ?? 'text') === 'html'
                    ? 'Supports {{templates}} from upstream data — e.g. <p>Hello {{payload.name}}</p>'
                    : 'Supports {{templates}} from upstream data — e.g. Hi {{payload.name}}'
                }
                InputProps={
                  (params.body_type ?? 'text') === 'html'
                    ? { sx: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: 13 } }
                    : undefined
                }
                sx={{ mb: 2 }}
                disabled={readOnly}
              />
              {(params.body_type ?? 'text') === 'html' && (
                <Box sx={{ mb: 2 }}>
                  <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                    <Typography variant="subtitle2">HTML preview</Typography>
                    <Typography variant="caption" color="text.secondary">
                      (rendered locally, sandboxed — no scripts run)
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      overflow: 'hidden',
                      bgcolor: 'background.paper',
                    }}
                  >
                    <iframe
                      title="Gmail HTML preview"
                      sandbox=""
                      srcDoc={String(params.body ?? '')}
                      style={{
                        width: '100%',
                        height: 220,
                        border: 0,
                        backgroundColor: '#ffffff',
                      }}
                    />
                  </Box>
                </Box>
              )}
              {params.operation === 'send' && (
                <TextField
                  label="Raw Message (base64url) — overrides To/Subject/Body"
                  fullWidth
                  multiline
                  minRows={3}
                  value={params.raw_base64 ?? ''}
                  onChange={(e) => setParam('raw_base64', e.target.value)}
                  sx={{ mb: 2 }}
                  disabled={readOnly}
                />
              )}

              {/* send_and_wait knobs */}
              {params.operation === 'send_and_wait' && (
                <>
                  <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel id="gmail-matchby-label">Match incoming reply by</InputLabel>
                    <Select
                      labelId="gmail-matchby-label"
                      label="Match incoming reply by"
                      value={params.match_by ?? 'thread'}
                      onChange={(e) => setParam('match_by', e.target.value)}
                    >
                      <MenuItem value="thread">Thread (recommended)</MenuItem>
                      <MenuItem value="sender">Sender (from:&lt;to_email&gt;)</MenuItem>
                      <MenuItem value="subject">Subject ("&lt;subject&gt;")</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label="Wait seconds (default 600, max 1800)"
                    size="small"
                    type="number"
                    fullWidth
                    value={params.wait_seconds ?? ''}
                    onChange={(e) => setParam('wait_seconds', e.target.value)}
                    sx={{ mb: 2 }}
                    inputProps={{ min: 1, max: 1800 }}
                  />
                  <TextField
                    label="Poll interval seconds (default 30, min 2)"
                    size="small"
                    type="number"
                    fullWidth
                    value={params.poll_interval_seconds ?? ''}
                    onChange={(e) => setParam('poll_interval_seconds', e.target.value)}
                    sx={{ mb: 2 }}
                    inputProps={{ min: 2 }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    Returns &#123;status: reply_received | timed_out, sent_message_id, thread_id, reply, attempts&#125;.
                  </Typography>
                </>
              )}
            </>
          )}
        </>
      )}

      {/* Action: Google Sheet */}
      {nodeType === 'action.google_sheet' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="gsheets-credential-label">Credential preset</InputLabel>
            <Select
              labelId="gsheets-credential-label"
              label="Credential preset"
              value={params.credential_id ?? ''}
              onChange={(e) => setParam('credential_id', e.target.value)}
              onOpen={() => loadWorkflowCredentials()} disabled={readOnly || credentialsLoading}
            >
              <MenuItem value="">None (use inline access token)</MenuItem>
              {googleSheetsCredentials.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Connect via "Connect with Google" → Google Sheets in the Workflow Credentials page,
            then select the preset here. The OAuth token auto-refreshes — no need to paste an access
            token below.
          </Typography>
          <TextField
            label="Access Token (optional, overrides preset)"
            size="small"
            type="password"
            fullWidth
            value={params.access_token ?? ''}
            onChange={(e) => setParam('access_token', e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Spreadsheet ID"
            size="small"
            fullWidth
            value={params.spreadsheet_id ?? ''}
            onChange={(e) => setParam('spreadsheet_id', e.target.value)}
            sx={{ mb: 2 }}
          />
          <Box display="flex" gap={1} sx={{ mb: 2 }}>
            <Button size="small" variant="outlined" onClick={fetchSheetFiles} disabled={sheetsLoading}>
              {sheetsLoading ? <CircularProgress size={16} /> : 'Load spreadsheets'}
            </Button>
            {sheetFiles.length > 0 && (
              <FormControl fullWidth size="small">
                <InputLabel id="sheet-file-label">Select spreadsheet</InputLabel>
                <Select
                  labelId="sheet-file-label"
                  label="Select spreadsheet"
                  value={params.spreadsheet_id ?? ''}
                  onChange={(e) => {
                    const id = String(e.target.value);
                    const file = sheetFiles.find((f) => f.id === id);
                    const currentRange = String(paramsRef.current.range || '');
                    if (file && !currentRange.includes('!')) {
                      setParams({ spreadsheet_id: id, range: `${file.name}!A1:Z` });
                    } else {
                      setParams({ spreadsheet_id: id });
                    }
                  }}
                >
                  {sheetFiles.map((f) => (
                    <MenuItem key={f.id} value={f.id}>
                      {f.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
          <TextField
            label="Range"
            size="small"
            fullWidth
            value={params.range ?? ''}
            onChange={(e) => setParam('range', e.target.value)}
            sx={{ mb: 2 }}
          />
          <Box display="flex" gap={1} sx={{ mb: 2 }}>
            <Button size="small" variant="outlined" onClick={fetchSheetTabs} disabled={sheetsLoading}>
              {sheetsLoading ? <CircularProgress size={16} /> : 'Load sheet tabs'}
            </Button>
            {sheetTabs.length > 0 && (
              <FormControl fullWidth size="small">
                <InputLabel id="sheet-tab-label">Select sheet</InputLabel>
                <Select
                  labelId="sheet-tab-label"
                  label="Select sheet"
                  value={(params.range || '').split('!')[0] || ''}
                  onChange={(e) => {
                    const name = String(e.target.value);
                    setParam('range', `${name}!A1:Z`);
                  }}
                >
                  {sheetTabs.map((s) => (
                    <MenuItem key={s.title} value={s.title}>
                      {s.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="sheets-operation-label">Operation</InputLabel>
            <Select
              labelId="sheets-operation-label"
              label="Operation"
              value={params.operation ?? 'get'}
              onChange={(e) => setParam('operation', e.target.value)}
            >
              {['get', 'append', 'update'].map((op) => (
                <MenuItem key={op} value={op}>
                  {op}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Values (CSV rows)"
            fullWidth
            multiline
            minRows={4}
            value={sheetsValuesText}
            onChange={(e) => setSheetsValuesText(e.target.value)}
            onBlur={() => {
              const rows = (sheetsValuesText || '')
                .split('\n')
                .filter((r) => r.trim().length)
                .map((r) => r.split(',').map((c) => c.trim()));
              setParam('values', rows);
            }}
            sx={{ mb: 2 }}
            helperText="Each line is a row, comma-separated."
          />
          <TextField
            label="Value Input Option"
            size="small"
            fullWidth
            value={params.value_input_option ?? 'USER_ENTERED'}
            onChange={(e) => setParam('value_input_option', e.target.value)}
            sx={{ mb: 2 }}
          />
        </>
      )}

      {/* Action: Google Drive */}
      {nodeType === 'action.google_drive' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="gdrive-credential-label">Credential preset</InputLabel>
            <Select
              labelId="gdrive-credential-label"
              label="Credential preset"
              value={params.credential_id ?? ''}
              onChange={(e) => setParam('credential_id', e.target.value)}
              onOpen={() => loadWorkflowCredentials()} disabled={readOnly || credentialsLoading}
            >
              <MenuItem value="">None (use inline access token)</MenuItem>
              {googleDriveCredentials.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Connect via "Connect with Google" → Google Drive in the Workflow Credentials page,
            then select the preset here. The OAuth token auto-refreshes — no need to paste an access
            token below.
          </Typography>
          <TextField
            label="Access Token (optional, overrides preset)"
            size="small"
            type="password"
            fullWidth
            value={params.access_token ?? ''}
            onChange={(e) => setParam('access_token', e.target.value)}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="drive-operation-label">Operation</InputLabel>
            <Select
              labelId="drive-operation-label"
              label="Operation"
              value={params.operation ?? 'list'}
              onChange={(e) => setParam('operation', e.target.value)}
            >
              {['list', 'download', 'upload'].map((op) => (
                <MenuItem key={op} value={op}>
                  {op}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Query"
            size="small"
            fullWidth
            value={params.query ?? ''}
            onChange={(e) => setParam('query', e.target.value)}
            sx={{ mb: 2 }}
          />
          <Button size="small" variant="outlined" onClick={fetchDriveFiles} disabled={driveLoading} sx={{ mb: 2 }}>
            {driveLoading ? <CircularProgress size={16} /> : 'Load files'}
          </Button>
          {driveFiles.length > 0 && (
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel id="drive-file-label">Select file</InputLabel>
              <Select
                labelId="drive-file-label"
                label="Select file"
                value={params.file_id ?? ''}
                onChange={(e) => {
                  const id = String(e.target.value);
                  const file = driveFiles.find((f) => f.id === id);
                  setParams(file ? { file_id: id, filename: file.name } : { file_id: id });
                }}
              >
                {driveFiles.map((f) => (
                  <MenuItem key={f.id} value={f.id}>
                    {f.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField
            label="File ID"
            size="small"
            fullWidth
            value={params.file_id ?? ''}
            onChange={(e) => setParam('file_id', e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Filename"
            size="small"
            fullWidth
            value={params.filename ?? ''}
            onChange={(e) => setParam('filename', e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="MIME Type"
            size="small"
            fullWidth
            value={params.mime_type ?? 'application/octet-stream'}
            onChange={(e) => setParam('mime_type', e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Content (base64)"
            fullWidth
            multiline
            minRows={3}
            value={params.content_base64 ?? ''}
            onChange={(e) => setParam('content_base64', e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Fields"
            size="small"
            fullWidth
            value={params.fields ?? ''}
            onChange={(e) => setParam('fields', e.target.value)}
            sx={{ mb: 2 }}
          />
        </>
      )}

      {/* Action: SMTP */}
      {nodeType === 'action.smtp' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="smtp-credential-label">Credential preset</InputLabel>
            <Select
              labelId="smtp-credential-label"
              label="Credential preset"
              value={params.credential_id ?? ''}
              onChange={(e) => setParam('credential_id', e.target.value)}
              onOpen={() => loadWorkflowCredentials()} disabled={readOnly || credentialsLoading}
            >
              <MenuItem value="">None (use inline fields)</MenuItem>
              {smtpCredentials.map((cred) => (
                <MenuItem key={cred.id} value={cred.id}>
                  {cred.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Create/manage credentials in Workflow Credentials page, then select it here.
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            SMTP default sender now prefers the current user email when From is empty.
          </Typography>
          <TextField
            label="From"
            size="small"
            fullWidth
            value={params.from_email ?? ''}
            onChange={(e) => setParam('from_email', e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="To"
            size="small"
            fullWidth
            value={params.to_email ?? ''}
            onChange={(e) => setParam('to_email', e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Subject"
            size="small"
            fullWidth
            value={params.subject ?? ''}
            onChange={(e) => setParam('subject', e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Body"
            fullWidth
            multiline
            minRows={4}
            value={params.body ?? ''}
            onChange={(e) => setParam('body', e.target.value)}
            sx={{ mb: 2 }}
          />
          {renderAttachmentsEditor()}
        </>
      )}

      {/* Action: IMAP */}
      {nodeType === 'action.imap' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="imap-credential-label">Credential preset</InputLabel>
            <Select
              labelId="imap-credential-label"
              label="Credential preset"
              value={params.credential_id ?? ''}
              onChange={(e) => setParam('credential_id', e.target.value)}
              onOpen={() => loadWorkflowCredentials()} disabled={readOnly || credentialsLoading}
            >
              <MenuItem value="">None (use inline fields)</MenuItem>
              {imapCredentials.map((cred) => (
                <MenuItem key={cred.id} value={cred.id}>
                  {cred.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Create/manage credentials in Workflow Credentials page, then select it here.
          </Typography>
          <TextField
            label="Mailbox"
            size="small"
            fullWidth
            value={params.mailbox ?? 'INBOX'}
            onChange={(e) => setParam('mailbox', e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Search"
            size="small"
            fullWidth
            value={params.search ?? 'ALL'}
            onChange={(e) => setParam('search', e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Limit"
            size="small"
            type="number"
            fullWidth
            value={params.limit ?? 10}
            onChange={(e) => setParam('limit', Number(e.target.value))}
            sx={{ mb: 2 }}
          />
        </>
      )}

      {/* Trigger: MQTT Subscribe (Node-RED `mqtt in` equivalent) */}
      {nodeType === 'trigger.mqtt_subscribe' && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Fires the workflow every time a message arrives on the subscribed topic.
            The long-running listener picks this trigger up automatically within
            ~30 seconds of activating the workflow.
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="mqtt-sub-credential-label">Broker credential</InputLabel>
            <Select
              labelId="mqtt-sub-credential-label"
              label="Broker credential"
              value={params.credential_id ?? ''}
              onChange={(e) => setParam('credential_id', e.target.value)}
              onOpen={() => loadWorkflowCredentials()}
              disabled={readOnly || credentialsLoading}
              required
            >
              <MenuItem value="">— select a broker credential —</MenuItem>
              {mqttCredentials.map((cred) => (
                <MenuItem key={cred.id} value={cred.id}>
                  {cred.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Pick an MQTT credential (Workflow Credentials page). The listener
            uses its host/port/auth to open a subscription on your behalf — your
            workflow doesn&apos;t need to stay open in the editor.
          </Typography>
          <TextField
            label="Topic"
            size="small"
            fullWidth
            value={params.topic ?? ''}
            onChange={(e) => setParam('topic', e.target.value)}
            placeholder="e.g. devices/+/events or rfid/scans or sensors/#"
            helperText="MQTT wildcards: + matches one level, # matches all remaining levels."
            sx={{ mb: 2 }}
            disabled={readOnly}
            required
          />
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="mqtt-sub-qos-label">QoS</InputLabel>
            <Select
              labelId="mqtt-sub-qos-label"
              label="QoS"
              value={String(params.qos ?? 0)}
              onChange={(e) => setParam('qos', Number(e.target.value))}
              disabled={readOnly}
            >
              <MenuItem value="0">0 — at most once</MenuItem>
              <MenuItem value="1">1 — at least once</MenuItem>
              <MenuItem value="2">2 — exactly once</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="mqtt-sub-payload-type-label">Payload format</InputLabel>
            <Select
              labelId="mqtt-sub-payload-type-label"
              label="Payload format"
              value={params.payload_type ?? 'auto'}
              onChange={(e) => setParam('payload_type', e.target.value)}
              disabled={readOnly}
            >
              <MenuItem value="auto">Auto (JSON, else UTF-8, else binary)</MenuItem>
              <MenuItem value="json">JSON (parse strictly)</MenuItem>
              <MenuItem value="utf8">UTF-8 string</MenuItem>
              <MenuItem value="buffer">Raw bytes (base64)</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Incoming messages arrive as <code>trigger_data</code> with
            {' '}<code>{'{ source: "mqtt", topic, payload, body }'}</code>{' '}— use
            <code>{'{{ body.* }}'}</code> in downstream nodes just like a webhook trigger.
          </Typography>
        </>
      )}

      {/* Action: MQTT Publish (Node-RED `mqtt out` equivalent) */}
      {nodeType === 'action.mqtt_publish' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="mqtt-credential-label">Broker credential</InputLabel>
            <Select
              labelId="mqtt-credential-label"
              label="Broker credential"
              value={params.credential_id ?? ''}
              onChange={(e) => setParam('credential_id', e.target.value)}
              onOpen={() => loadWorkflowCredentials()}
              disabled={readOnly || credentialsLoading}
            >
              <MenuItem value="">None (use inline broker fields below)</MenuItem>
              {mqttCredentials.map((cred) => (
                <MenuItem key={cred.id} value={cred.id}>
                  {cred.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Imported from Node-RED `mqtt out`. The original `mqtt-broker` config
            node is stored as a workflow credential — pick it above, or fill in
            the broker fields directly.
          </Typography>
          <TextField
            label="Broker host"
            size="small"
            fullWidth
            value={params.broker_host ?? ''}
            onChange={(e) => setParam('broker_host', e.target.value)}
            placeholder="e.g. mqtt.example.com"
            sx={{ mb: 2 }}
            disabled={readOnly}
          />
          <TextField
            label="Broker port"
            size="small"
            type="number"
            fullWidth
            value={params.broker_port ?? 1883}
            onChange={(e) => setParam('broker_port', Number(e.target.value) || 1883)}
            sx={{ mb: 2 }}
            disabled={readOnly}
          />
          <TextField
            label="Username"
            size="small"
            fullWidth
            value={params.username ?? ''}
            onChange={(e) => setParam('username', e.target.value)}
            sx={{ mb: 2 }}
            disabled={readOnly}
          />
          <TextField
            label="Password"
            size="small"
            type="password"
            fullWidth
            value={params.password ?? ''}
            onChange={(e) => setParam('password', e.target.value)}
            sx={{ mb: 2 }}
            disabled={readOnly}
          />
          <TextField
            label="Client ID (optional)"
            size="small"
            fullWidth
            value={params.client_id ?? ''}
            onChange={(e) => setParam('client_id', e.target.value)}
            placeholder="auto-generated if blank"
            sx={{ mb: 2 }}
            disabled={readOnly}
          />
          <FormControlLabel
            sx={{ mb: 2, display: 'block' }}
            control={
              <Switch
                checked={Boolean(params.use_tls)}
                onChange={(e) => setParam('use_tls', e.target.checked)}
                disabled={readOnly}
              />
            }
            label="Use TLS"
          />
          <Divider sx={{ my: 2 }} />
          <TextField
            label="Topic"
            size="small"
            fullWidth
            value={params.topic ?? ''}
            onChange={(e) => setParam('topic', e.target.value)}
            placeholder="e.g. sensors/rfid/granted — supports {{ msg.topic }}"
            sx={{ mb: 2 }}
            disabled={readOnly}
            required
          />
          <TextField
            label="Payload"
            fullWidth
            multiline
            minRows={3}
            value={
              typeof params.payload === 'object' && params.payload !== null
                ? JSON.stringify(params.payload, null, 2)
                : (params.payload ?? '')
            }
            onChange={(e) => {
              const raw = e.target.value;
              const trimmed = raw.trim();
              if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
                try {
                  setParam('payload', JSON.parse(trimmed));
                  return;
                } catch {
                  // fall through and store as string so user can keep typing
                }
              }
              setParam('payload', raw);
            }}
            placeholder="Leave blank to forward msg.payload from upstream — supports {{ }} templates"
            sx={{ mb: 2 }}
            disabled={readOnly}
          />
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="mqtt-qos-label">QoS</InputLabel>
            <Select
              labelId="mqtt-qos-label"
              label="QoS"
              value={String(params.qos ?? 0)}
              onChange={(e) => setParam('qos', Number(e.target.value))}
              disabled={readOnly}
            >
              <MenuItem value="0">0 — at most once</MenuItem>
              <MenuItem value="1">1 — at least once</MenuItem>
              <MenuItem value="2">2 — exactly once</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            sx={{ mb: 2, display: 'block' }}
            control={
              <Switch
                checked={Boolean(params.retain)}
                onChange={(e) => setParam('retain', e.target.checked)}
                disabled={readOnly}
              />
            }
            label="Retain on broker"
          />
        </>
      )}

      {/* Action: Pipedrive */}
      {/* Action: Respond to Webhook */}
      {nodeType === 'action.webhook.respond' && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Controls exactly what the webhook caller receives. Place this node at the end of your workflow.
          </Typography>

          {/* Respond-with mode */}
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="wh-respond-with-label">Respond with</InputLabel>
            <Select
              labelId="wh-respond-with-label"
              label="Respond with"
              value={params.respond_with ?? 'last_node'}
              onChange={(e) => setParam('respond_with', e.target.value)}
              disabled={readOnly}
            >
              <MenuItem value="last_node">Last node output (auto)</MenuItem>
              <MenuItem value="json">Custom JSON body</MenuItem>
              <MenuItem value="text">Custom text body</MenuItem>
              <MenuItem value="nothing">No body (empty response)</MenuItem>
            </Select>
          </FormControl>

          {/* HTTP Status code */}
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="wh-status-label">HTTP Status Code</InputLabel>
            <Select
              labelId="wh-status-label"
              label="HTTP Status Code"
              value={params.status_code ?? 200}
              onChange={(e) => setParam('status_code', Number(e.target.value))}
              disabled={readOnly}
            >
              {[200, 201, 202, 204, 400, 401, 403, 404, 409, 422, 500].map((code) => (
                <MenuItem key={code} value={code}>{code}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Custom body — shown only for json/text modes */}
          {(params.respond_with === 'json' || params.respond_with === 'text') && (
            <TextField
              label={params.respond_with === 'json' ? 'Response Body (JSON)' : 'Response Body (text)'}
              fullWidth
              multiline
              minRows={4}
              value={params.body ?? (params.respond_with === 'json' ? '{}' : '')}
              onChange={(e) => setParam('body', e.target.value)}
              sx={{ mb: 2, fontFamily: 'monospace' }}
              disabled={readOnly}
              helperText={
                params.respond_with === 'json'
                  ? 'Enter valid JSON. Use {{ node_name.field }} to reference previous node output.'
                  : 'Plain text returned to the caller. Use {{ node_name.field }} for dynamic values.'
              }
            />
          )}

          {/* Content-Type header shortcut */}
          {params.respond_with === 'json' && (
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              Content-Type will be set to <code>application/json</code> automatically.
            </Typography>
          )}

          {/* Optional extra response headers */}
          <TextField
            label="Extra Response Headers (JSON)"
            fullWidth
            multiline
            minRows={2}
            value={typeof params.headers === 'string' ? params.headers : JSON.stringify(params.headers ?? {}, null, 2)}
            onChange={(e) => {
              try { setParam('headers', JSON.parse(e.target.value)); } catch { setParam('headers', e.target.value); }
            }}
            sx={{ mb: 2, fontFamily: 'monospace' }}
            disabled={readOnly}
            helperText='e.g. {"X-My-Header": "value"}'
          />
        </>
      )}

      {nodeType === 'action.pipedrive' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="pipedrive-cred-label">Credential preset</InputLabel>
            <Select labelId="pipedrive-cred-label" label="Credential preset" value={params.credential_id ?? ''} onChange={(e) => setParam('credential_id', e.target.value)} onOpen={() => loadWorkflowCredentials()} disabled={readOnly || credentialsLoading}>
              <MenuItem value="">None</MenuItem>
              {pipedriveCredentials.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="pipedrive-op-label">Operation</InputLabel>
            <Select labelId="pipedrive-op-label" label="Operation" value={params.operation ?? 'list_deals'} onChange={(e) => setParam('operation', e.target.value)} disabled={readOnly}>
              {['list_persons','get_person','create_person','update_person','delete_person','list_deals','get_deal','create_deal','update_deal','delete_deal','list_organizations','create_organization','list_activities','create_activity','create_note','search'].map((op) => <MenuItem key={op} value={op}>{op}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="pipedrive-res-label">Resource</InputLabel>
            <Select labelId="pipedrive-res-label" label="Resource" value={params.resource ?? 'deal'} onChange={(e) => setParam('resource', e.target.value)} disabled={readOnly}>
              {['deal','person','organization','activity','note','lead','product','pipeline'].map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Resource ID" size="small" fullWidth value={params.resource_id ?? ''} onChange={(e) => setParam('resource_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          {params.operation === 'search' && <TextField label="Search Term" size="small" fullWidth value={params.search_term ?? ''} onChange={(e) => setParam('search_term', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />}
          <TextField label="Limit" size="small" type="number" fullWidth value={params.limit ?? 100} onChange={(e) => setParam('limit', Number(e.target.value))} sx={{ mb: 2 }} disabled={readOnly} />
        </>
      )}

      {/* Action: Microsoft 365 */}
      {nodeType === 'action.microsoft365' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="m365-cred-label">Credential preset</InputLabel>
            <Select labelId="m365-cred-label" label="Credential preset" value={params.credential_id ?? ''} onChange={(e) => setParam('credential_id', e.target.value)} onOpen={() => loadWorkflowCredentials()} disabled={readOnly || credentialsLoading}>
              <MenuItem value="">None</MenuItem>
              {microsoft365Credentials.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="m365-op-label">Operation</InputLabel>
            <Select labelId="m365-op-label" label="Operation" value={params.operation ?? 'send_email'} onChange={(e) => setParam('operation', e.target.value)} disabled={readOnly}>
              {['send_email','list_emails','get_email','list_calendar_events','create_calendar_event','list_teams','list_team_channels','send_teams_message','list_drive_items','raw'].map((op) => <MenuItem key={op} value={op}>{op}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="User ID (default: me)" size="small" fullWidth value={params.user_id ?? 'me'} onChange={(e) => setParam('user_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          {params.operation === 'send_email' && <>
            <TextField label="To" size="small" fullWidth value={params.to ?? ''} onChange={(e) => setParam('to', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
            <TextField label="Subject" size="small" fullWidth value={params.subject ?? ''} onChange={(e) => setParam('subject', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
            <TextField label="Body" fullWidth multiline minRows={3} value={params.body ?? ''} onChange={(e) => setParam('body', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          </>}
          {params.operation === 'send_teams_message' && <>
            <TextField label="Team ID" size="small" fullWidth value={params.team_id ?? ''} onChange={(e) => setParam('team_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
            <TextField label="Channel ID" size="small" fullWidth value={params.channel_id ?? ''} onChange={(e) => setParam('channel_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
            <TextField label="Content" fullWidth multiline minRows={2} value={params.content ?? ''} onChange={(e) => setParam('content', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          </>}
          {['create_calendar_event'].includes(params.operation ?? '') && <>
            <TextField label="Subject" size="small" fullWidth value={params.subject ?? ''} onChange={(e) => setParam('subject', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
            <TextField label="Start (ISO 8601)" size="small" fullWidth value={params.start ?? ''} onChange={(e) => setParam('start', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
            <TextField label="End (ISO 8601)" size="small" fullWidth value={params.end ?? ''} onChange={(e) => setParam('end', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          </>}
          <TextField label="Limit" size="small" type="number" fullWidth value={params.limit ?? 25} onChange={(e) => setParam('limit', Number(e.target.value))} sx={{ mb: 2 }} disabled={readOnly} />
        </>
      )}

      {/* Action: Asana */}
      {nodeType === 'action.asana' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="asana-cred-label">Credential preset</InputLabel>
            <Select labelId="asana-cred-label" label="Credential preset" value={params.credential_id ?? ''} onChange={(e) => setParam('credential_id', e.target.value)} onOpen={() => loadWorkflowCredentials()} disabled={readOnly || credentialsLoading}>
              <MenuItem value="">None</MenuItem>
              {asanaCredentials.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="asana-op-label">Operation</InputLabel>
            <Select labelId="asana-op-label" label="Operation" value={params.operation ?? 'list_tasks'} onChange={(e) => setParam('operation', e.target.value)} disabled={readOnly}>
              {['list_workspaces','list_projects','get_project','create_project','list_tasks','get_task','create_task','update_task','complete_task','delete_task','list_sections'].map((op) => <MenuItem key={op} value={op}>{op}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Workspace ID" size="small" fullWidth value={params.workspace ?? ''} onChange={(e) => setParam('workspace', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          <TextField label="Project ID" size="small" fullWidth value={params.project_id ?? ''} onChange={(e) => setParam('project_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          <TextField label="Task ID" size="small" fullWidth value={params.task_id ?? ''} onChange={(e) => setParam('task_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          {['create_task','update_task'].includes(params.operation ?? '') && <>
            <TextField label="Task Name" size="small" fullWidth value={params.name ?? ''} onChange={(e) => setParam('name', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
            <TextField label="Notes" fullWidth multiline minRows={2} value={params.notes ?? ''} onChange={(e) => setParam('notes', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
            <TextField label="Due On (YYYY-MM-DD)" size="small" fullWidth value={params.due_on ?? ''} onChange={(e) => setParam('due_on', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
            <TextField label="Assignee (GID)" size="small" fullWidth value={params.assignee ?? ''} onChange={(e) => setParam('assignee', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          </>}
          <TextField label="Limit" size="small" type="number" fullWidth value={params.limit ?? 100} onChange={(e) => setParam('limit', Number(e.target.value))} sx={{ mb: 2 }} disabled={readOnly} />
        </>
      )}

      {/* Action: Trello */}
      {nodeType === 'action.trello' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="trello-cred-label">Credential preset</InputLabel>
            <Select labelId="trello-cred-label" label="Credential preset" value={params.credential_id ?? ''} onChange={(e) => setParam('credential_id', e.target.value)} onOpen={() => loadWorkflowCredentials()} disabled={readOnly || credentialsLoading}>
              <MenuItem value="">None</MenuItem>
              {trelloCredentials.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="trello-op-label">Operation</InputLabel>
            <Select labelId="trello-op-label" label="Operation" value={params.operation ?? 'list_boards'} onChange={(e) => setParam('operation', e.target.value)} disabled={readOnly}>
              {['list_boards','get_board','list_lists','create_list','list_cards','get_card','create_card','update_card','archive_card','move_card','list_members'].map((op) => <MenuItem key={op} value={op}>{op}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Board ID" size="small" fullWidth value={params.board_id ?? ''} onChange={(e) => setParam('board_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          <TextField label="List ID" size="small" fullWidth value={params.list_id ?? ''} onChange={(e) => setParam('list_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          <TextField label="Card ID" size="small" fullWidth value={params.card_id ?? ''} onChange={(e) => setParam('card_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          {['create_card','create_list'].includes(params.operation ?? '') && <>
            <TextField label="Name" size="small" fullWidth value={params.name ?? ''} onChange={(e) => setParam('name', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
            {params.operation === 'create_card' && <TextField label="Description" fullWidth multiline minRows={2} value={params.desc ?? ''} onChange={(e) => setParam('desc', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />}
            {params.operation === 'create_card' && <TextField label="Due Date (ISO)" size="small" fullWidth value={params.due ?? ''} onChange={(e) => setParam('due', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />}
          </>}
        </>
      )}

      {/* Action: NetSuite */}
      {nodeType === 'action.netsuite' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="netsuite-cred-label">Credential preset</InputLabel>
            <Select labelId="netsuite-cred-label" label="Credential preset" value={params.credential_id ?? ''} onChange={(e) => setParam('credential_id', e.target.value)} onOpen={() => loadWorkflowCredentials()} disabled={readOnly || credentialsLoading}>
              <MenuItem value="">None</MenuItem>
              {netsuiteCredentials.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="netsuite-op-label">Operation</InputLabel>
            <Select labelId="netsuite-op-label" label="Operation" value={params.operation ?? 'run_suiteql'} onChange={(e) => setParam('operation', e.target.value)} disabled={readOnly}>
              {['run_suiteql','list_records','get_record','create_record','update_record','delete_record'].map((op) => <MenuItem key={op} value={op}>{op}</MenuItem>)}
            </Select>
          </FormControl>
          {params.operation === 'run_suiteql' && <TextField label="SuiteQL Query" fullWidth multiline minRows={3} value={params.suiteql ?? ''} onChange={(e) => setParam('suiteql', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />}
          {params.operation !== 'run_suiteql' && <TextField label="Record Type (e.g. customer, invoice)" size="small" fullWidth value={params.record_type ?? ''} onChange={(e) => setParam('record_type', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />}
          {['get_record','update_record','delete_record'].includes(params.operation ?? '') && <TextField label="Record ID" size="small" fullWidth value={params.record_id ?? ''} onChange={(e) => setParam('record_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />}
          <TextField label="Limit" size="small" type="number" fullWidth value={params.limit ?? 100} onChange={(e) => setParam('limit', Number(e.target.value))} sx={{ mb: 2 }} disabled={readOnly} />
          <TextField label="Offset" size="small" type="number" fullWidth value={params.offset ?? 0} onChange={(e) => setParam('offset', Number(e.target.value))} sx={{ mb: 2 }} disabled={readOnly} />
        </>
      )}

      {/* Action: QuickBooks */}
      {nodeType === 'action.quickbooks' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="qbo-cred-label">Credential preset</InputLabel>
            <Select labelId="qbo-cred-label" label="Credential preset" value={params.credential_id ?? ''} onChange={(e) => setParam('credential_id', e.target.value)} onOpen={() => loadWorkflowCredentials()} disabled={readOnly || credentialsLoading}>
              <MenuItem value="">None</MenuItem>
              {quickbooksCredentials.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="qbo-op-label">Operation</InputLabel>
            <Select labelId="qbo-op-label" label="Operation" value={params.operation ?? 'query'} onChange={(e) => setParam('operation', e.target.value)} disabled={readOnly}>
              {['query','get','create','update','delete','read_report'].map((op) => <MenuItem key={op} value={op}>{op}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Entity (Customer, Invoice, Payment, Item…)" size="small" fullWidth value={params.entity ?? 'Customer'} onChange={(e) => setParam('entity', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          {params.operation === 'query' && <TextField label="SQL Query" fullWidth multiline minRows={2} value={params.query ?? 'SELECT * FROM Customer MAXRESULTS 100'} onChange={(e) => setParam('query', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />}
          {['get','update','delete'].includes(params.operation ?? '') && <TextField label="Entity ID" size="small" fullWidth value={params.entity_id ?? ''} onChange={(e) => setParam('entity_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />}
          {params.operation === 'read_report' && <TextField label="Report Name (e.g. BalanceSheet)" size="small" fullWidth value={params.report_name ?? 'BalanceSheet'} onChange={(e) => setParam('report_name', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />}
        </>
      )}

      {/* Action: SAP */}
      {nodeType === 'action.sap' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="sap-cred-label">Credential preset</InputLabel>
            <Select labelId="sap-cred-label" label="Credential preset" value={params.credential_id ?? ''} onChange={(e) => setParam('credential_id', e.target.value)} onOpen={() => loadWorkflowCredentials()} disabled={readOnly || credentialsLoading}>
              <MenuItem value="">None</MenuItem>
              {sapCredentials.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="sap-op-label">Operation</InputLabel>
            <Select labelId="sap-op-label" label="Operation" value={params.operation ?? 'query'} onChange={(e) => setParam('operation', e.target.value)} disabled={readOnly}>
              {['query','get','create','update','delete','call_function','raw'].map((op) => <MenuItem key={op} value={op}>{op}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Service Path (e.g. sap/opu/odata/sap/API_BUSINESS_PARTNER)" size="small" fullWidth value={params.service_path ?? ''} onChange={(e) => setParam('service_path', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          <TextField label="Entity Set (e.g. A_BusinessPartner)" size="small" fullWidth value={params.entity_set ?? ''} onChange={(e) => setParam('entity_set', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          {['get','update','delete'].includes(params.operation ?? '') && <TextField label="Entity Key (e.g. BusinessPartner='1000')" size="small" fullWidth value={params.entity_key ?? ''} onChange={(e) => setParam('entity_key', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />}
          {params.operation === 'query' && <>
            <TextField label="$filter" size="small" fullWidth value={params.filter ?? ''} onChange={(e) => setParam('filter', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
            <TextField label="$select" size="small" fullWidth value={params.select ?? ''} onChange={(e) => setParam('select', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
            <TextField label="$top" size="small" type="number" fullWidth value={params.top ?? 100} onChange={(e) => setParam('top', Number(e.target.value))} sx={{ mb: 2 }} disabled={readOnly} />
            <TextField label="$skip" size="small" type="number" fullWidth value={params.skip ?? 0} onChange={(e) => setParam('skip', Number(e.target.value))} sx={{ mb: 2 }} disabled={readOnly} />
          </>}
          {params.operation === 'call_function' && <TextField label="Function Name" size="small" fullWidth value={params.function_name ?? ''} onChange={(e) => setParam('function_name', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />}
          {params.operation === 'raw' && <>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel id="sap-method-label">HTTP Method</InputLabel>
              <Select labelId="sap-method-label" label="HTTP Method" value={params.method ?? 'GET'} onChange={(e) => setParam('method', e.target.value)} disabled={readOnly}>
                {['GET','POST','PUT','PATCH','DELETE'].map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label="Path" size="small" fullWidth value={params.path ?? ''} onChange={(e) => setParam('path', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          </>}
        </>
      )}

      {/* Action: Slack */}
      {nodeType === 'action.slack' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="slack-credential-label">Credential preset</InputLabel>
            <Select
              labelId="slack-credential-label"
              label="Credential preset"
              value={params.credential_id ?? ''}
              onChange={(e) => setParam('credential_id', e.target.value)}
              onOpen={() => loadWorkflowCredentials()} disabled={readOnly || credentialsLoading}
            >
              <MenuItem value="">None (use inline fields)</MenuItem>
              {slackCredentials.map((cred) => (
                <MenuItem key={cred.id} value={cred.id}>
                  {cred.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Create/manage credentials in Workflow Credentials page, then select it here.
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="slack-op-label">Operation</InputLabel>
            <Select
              labelId="slack-op-label"
              label="Operation"
              value={params.operation ?? 'send_message'}
              onChange={(e) => setParam('operation', e.target.value)}
              disabled={readOnly}
            >
              <MenuItem value="send_message">Send Message</MenuItem>
              <MenuItem value="update_message">Update Message</MenuItem>
              <MenuItem value="delete_message">Delete Message</MenuItem>
              <MenuItem value="get_history">Get Channel History</MenuItem>
              <MenuItem value="list_channels">List Channels</MenuItem>
              <MenuItem value="get_channel">Get Channel</MenuItem>
              <MenuItem value="list_members">List Channel Members</MenuItem>
              <MenuItem value="list_users">List Users</MenuItem>
              <MenuItem value="get_user">Get User</MenuItem>
              <MenuItem value="lookup_user_by_email">Lookup User by Email</MenuItem>
              <MenuItem value="add_reaction">Add Reaction</MenuItem>
              <MenuItem value="upload_file">Upload File</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Channel (ID or #name)"
            size="small"
            fullWidth
            value={params.channel ?? ''}
            onChange={(e) => setParam('channel', e.target.value)}
            sx={{ mb: 2 }}
            disabled={readOnly}
          />
          {['send_message', 'update_message'].includes(params.operation ?? 'send_message') && (
            <TextField
              label="Text"
              fullWidth
              multiline
              minRows={3}
              value={params.text ?? ''}
              onChange={(e) => setParam('text', e.target.value)}
              sx={{ mb: 2 }}
              disabled={readOnly}
            />
          )}
          {params.operation === 'send_message' && (
            <TextField
              label="Thread TS (optional, for replies)"
              size="small"
              fullWidth
              value={params.thread_ts ?? ''}
              onChange={(e) => setParam('thread_ts', e.target.value)}
              sx={{ mb: 2 }}
              disabled={readOnly}
            />
          )}
          {['update_message', 'delete_message', 'add_reaction'].includes(params.operation ?? '') && (
            <TextField
              label="Message Timestamp (ts)"
              size="small"
              fullWidth
              value={params.ts ?? ''}
              onChange={(e) => setParam('ts', e.target.value)}
              sx={{ mb: 2 }}
              disabled={readOnly}
            />
          )}
          {params.operation === 'add_reaction' && (
            <TextField
              label="Reaction (emoji name, e.g. thumbsup)"
              size="small"
              fullWidth
              value={params.reaction ?? 'thumbsup'}
              onChange={(e) => setParam('reaction', e.target.value)}
              sx={{ mb: 2 }}
              disabled={readOnly}
            />
          )}
          {params.operation === 'get_user' && (
            <TextField
              label="User ID"
              size="small"
              fullWidth
              value={params.user_id ?? ''}
              onChange={(e) => setParam('user_id', e.target.value)}
              sx={{ mb: 2 }}
              disabled={readOnly}
            />
          )}
          {params.operation === 'lookup_user_by_email' && (
            <TextField
              label="Email"
              size="small"
              fullWidth
              value={params.email ?? ''}
              onChange={(e) => setParam('email', e.target.value)}
              sx={{ mb: 2 }}
              disabled={readOnly}
            />
          )}
          {params.operation === 'upload_file' && (
            <>
              <TextField
                label="File content"
                fullWidth
                multiline
                minRows={3}
                value={params.content ?? ''}
                onChange={(e) => setParam('content', e.target.value)}
                sx={{ mb: 2 }}
                disabled={readOnly}
              />
              <TextField
                label="Filename"
                size="small"
                fullWidth
                value={params.filename ?? 'file.txt'}
                onChange={(e) => setParam('filename', e.target.value)}
                sx={{ mb: 2 }}
                disabled={readOnly}
              />
              <TextField
                label="Title"
                size="small"
                fullWidth
                value={params.title ?? ''}
                onChange={(e) => setParam('title', e.target.value)}
                sx={{ mb: 2 }}
                disabled={readOnly}
              />
            </>
          )}
          {['list_channels', 'list_users', 'list_members', 'get_history'].includes(params.operation ?? '') && (
            <TextField
              label="Limit"
              size="small"
              type="number"
              fullWidth
              value={params.limit ?? 100}
              onChange={(e) => setParam('limit', Number(e.target.value))}
              sx={{ mb: 2 }}
              disabled={readOnly}
            />
          )}
        </>
      )}

      {/* Action: Shopify */}
      {nodeType === 'action.shopify' && (() => {
        const op: string = params.operation ?? 'list_products';
        const verb = op.split('_')[0]; // list | get | create | update | delete | graphql
        const body: Record<string, any> = (params.body && typeof params.body === 'object') ? params.body : {};

        const setBodyField = (key: string, value: any) => {
          const next = { ...body };
          if (value === '' || value === null || value === undefined) {
            delete next[key];
          } else {
            next[key] = value;
          }
          setParam('body', next);
        };

        // Per-operation field schemas. These render labeled inputs that
        // populate `params.body` directly so the user never has to write JSON.
        type ShopifyField = {
          key: string;
          label: string;
          type?: 'text' | 'number' | 'email' | 'textarea' | 'select';
          placeholder?: string;
          required?: boolean;
          options?: Array<{ value: string; label: string }>;
          helper?: string;
        };

        // --- Customer fields ---
        const customerFields: ShopifyField[] = [
          { key: 'first_name', label: 'First name', placeholder: 'Jane' },
          { key: 'last_name',  label: 'Last name',  placeholder: 'Doe' },
          { key: 'email',      label: 'Email',      type: 'email', placeholder: 'jane@example.com' },
          { key: 'phone',      label: 'Phone',      placeholder: '+1 555 123 4567' },
          { key: 'tags',       label: 'Tags (comma separated)', placeholder: 'vip,newsletter' },
          { key: 'note',       label: 'Note',       type: 'textarea' },
          { key: 'accepts_marketing', label: 'Accepts marketing', type: 'select', options: [
            { value: '', label: '— leave unchanged —' },
            { value: 'true',  label: 'Yes' },
            { value: 'false', label: 'No' },
          ]},
          { key: 'verified_email', label: 'Email verified', type: 'select', options: [
            { value: '', label: '— leave unchanged —' },
            { value: 'true',  label: 'Yes' },
            { value: 'false', label: 'No' },
          ]},
        ];

        // --- Product fields ---
        const productFields: ShopifyField[] = [
          { key: 'title',        label: 'Title', placeholder: 'My Product' },
          { key: 'body_html',    label: 'Description (HTML allowed)', type: 'textarea', placeholder: '<p>Description</p>' },
          { key: 'vendor',       label: 'Vendor', placeholder: 'Brand name' },
          { key: 'product_type', label: 'Product type', placeholder: 'T-Shirt' },
          { key: 'tags',         label: 'Tags (comma separated)', placeholder: 'sale,summer' },
          { key: 'handle',       label: 'Handle (URL slug)', placeholder: 'my-product' },
          { key: 'status',       label: 'Status', type: 'select', options: [
            { value: '', label: '— leave unchanged —' },
            { value: 'active',   label: 'Active' },
            { value: 'draft',    label: 'Draft' },
            { value: 'archived', label: 'Archived' },
          ]},
          { key: 'published_scope', label: 'Published to', type: 'select', options: [
            { value: '', label: '— leave unchanged —' },
            { value: 'web',    label: 'Online store only' },
            { value: 'global', label: 'All sales channels' },
          ]},
        ];

        // --- Order fields ---
        const orderFields: ShopifyField[] = [
          { key: 'email',            label: 'Customer email', type: 'email' },
          { key: 'phone',            label: 'Customer phone' },
          { key: 'note',             label: 'Order note', type: 'textarea' },
          { key: 'tags',             label: 'Tags (comma separated)' },
          { key: 'financial_status', label: 'Financial status', type: 'select', options: [
            { value: '', label: '— leave unchanged —' },
            { value: 'pending',           label: 'Pending' },
            { value: 'authorized',        label: 'Authorized' },
            { value: 'partially_paid',    label: 'Partially paid' },
            { value: 'paid',              label: 'Paid' },
            { value: 'partially_refunded',label: 'Partially refunded' },
            { value: 'refunded',          label: 'Refunded' },
            { value: 'voided',            label: 'Voided' },
          ]},
          { key: 'fulfillment_status', label: 'Fulfillment status', type: 'select', options: [
            { value: '', label: '— leave unchanged —' },
            { value: 'fulfilled',           label: 'Fulfilled' },
            { value: 'partial',             label: 'Partial' },
            { value: 'restocked',           label: 'Restocked' },
            { value: 'unfulfilled',         label: 'Unfulfilled' },
          ]},
        ];

        // --- Collection fields (custom_collections endpoint) ---
        const collectionFields: ShopifyField[] = [
          { key: 'title',     label: 'Title', placeholder: 'Summer sale' },
          { key: 'body_html', label: 'Description (HTML allowed)', type: 'textarea' },
          { key: 'handle',    label: 'Handle (URL slug)', placeholder: 'summer-sale' },
          { key: 'sort_order', label: 'Sort order', type: 'select', options: [
            { value: '', label: '— leave unchanged —' },
            { value: 'manual',         label: 'Manual' },
            { value: 'best-selling',   label: 'Best selling' },
            { value: 'alpha-asc',      label: 'Alphabetical A → Z' },
            { value: 'alpha-desc',     label: 'Alphabetical Z → A' },
            { value: 'price-asc',      label: 'Price low → high' },
            { value: 'price-desc',     label: 'Price high → low' },
            { value: 'created',        label: 'Date: oldest first' },
            { value: 'created-desc',   label: 'Date: newest first' },
          ]},
          { key: 'published', label: 'Published', type: 'select', options: [
            { value: '', label: '— leave unchanged —' },
            { value: 'true',  label: 'Yes' },
            { value: 'false', label: 'No' },
          ]},
        ];

        // --- Draft order fields ---
        const draftOrderFields: ShopifyField[] = [
          { key: 'email',    label: 'Customer email', type: 'email' },
          { key: 'note',     label: 'Note', type: 'textarea' },
          { key: 'tags',     label: 'Tags (comma separated)' },
          { key: 'currency', label: 'Currency code (ISO)', placeholder: 'USD' },
        ];

        // --- Variant fields (Shopify only supports update/get/delete on
        // top-level variants — to create variants you create/update a product). ---
        const variantFields: ShopifyField[] = [
          { key: 'option1', label: 'Option 1 (e.g. size)' },
          { key: 'option2', label: 'Option 2 (e.g. color)' },
          { key: 'option3', label: 'Option 3' },
          { key: 'price',   label: 'Price', type: 'number', placeholder: '19.99' },
          { key: 'compare_at_price', label: 'Compare at price', type: 'number' },
          { key: 'sku',     label: 'SKU' },
          { key: 'barcode', label: 'Barcode (ISBN, UPC, GTIN…)' },
          { key: 'inventory_quantity', label: 'Inventory quantity', type: 'number' },
          { key: 'weight',  label: 'Weight', type: 'number' },
          { key: 'weight_unit', label: 'Weight unit', type: 'select', options: [
            { value: '', label: '— leave unchanged —' },
            { value: 'g',  label: 'grams (g)' },
            { value: 'kg', label: 'kilograms (kg)' },
            { value: 'oz', label: 'ounces (oz)' },
            { value: 'lb', label: 'pounds (lb)' },
          ]},
          { key: 'taxable', label: 'Taxable', type: 'select', options: [
            { value: '', label: '— leave unchanged —' },
            { value: 'true',  label: 'Yes' },
            { value: 'false', label: 'No' },
          ]},
          { key: 'requires_shipping', label: 'Requires shipping', type: 'select', options: [
            { value: '', label: '— leave unchanged —' },
            { value: 'true',  label: 'Yes' },
            { value: 'false', label: 'No' },
          ]},
        ];

        // create_X variants: enforce the one truly required field per resource.
        const withRequired = (fields: ShopifyField[], requiredKey: string): ShopifyField[] =>
          fields.map((f) => f.key === requiredKey ? { ...f, required: true } : f);

        const FIELDS: Record<string, ShopifyField[]> = {
          // Customer
          create_customer: withRequired(customerFields, 'email'),
          update_customer: customerFields,
          // Product
          create_product: withRequired(productFields, 'title'),
          update_product: productFields,
          // Order
          create_order: orderFields,
          update_order: orderFields,
          // Collection
          create_collection: withRequired(collectionFields, 'title'),
          update_collection: collectionFields,
          // Draft order
          create_draft_order: draftOrderFields,
          update_draft_order: draftOrderFields,
          // Variant (no create — only update)
          update_variant: variantFields,
        };
        const opFields: ShopifyField[] = FIELDS[op] ?? [];

        // Per-list filter schemas — these populate `params.query_params` and
        // are sent as URL query string filters by the backend executor.
        const LIST_FILTERS: Record<string, ShopifyField[]> = {
          list_products: [
            { key: 'status', label: 'Status', type: 'select', options: [
              { value: '', label: '— any —' },
              { value: 'active',   label: 'Active' },
              { value: 'draft',    label: 'Draft' },
              { value: 'archived', label: 'Archived' },
            ]},
            { key: 'vendor',       label: 'Vendor (exact match)' },
            { key: 'product_type', label: 'Product type (exact match)' },
            { key: 'collection_id',label: 'Collection ID' },
            { key: 'ids',          label: 'IDs (comma separated)', placeholder: '12345,67890' },
            { key: 'created_at_min', label: 'Created after', placeholder: '2026-01-01T00:00:00Z' },
            { key: 'created_at_max', label: 'Created before', placeholder: '2026-12-31T23:59:59Z' },
            { key: 'updated_at_min', label: 'Updated after', placeholder: '2026-01-01T00:00:00Z' },
            { key: 'updated_at_max', label: 'Updated before' },
          ],
          list_orders: [
            { key: 'status', label: 'Status', type: 'select', options: [
              { value: '', label: '— default (open) —' },
              { value: 'any',       label: 'Any' },
              { value: 'open',      label: 'Open' },
              { value: 'closed',    label: 'Closed' },
              { value: 'cancelled', label: 'Cancelled' },
            ]},
            { key: 'financial_status', label: 'Financial status', type: 'select', options: [
              { value: '', label: '— any —' },
              { value: 'paid',              label: 'Paid' },
              { value: 'pending',           label: 'Pending' },
              { value: 'authorized',        label: 'Authorized' },
              { value: 'partially_paid',    label: 'Partially paid' },
              { value: 'partially_refunded',label: 'Partially refunded' },
              { value: 'refunded',          label: 'Refunded' },
              { value: 'voided',            label: 'Voided' },
            ]},
            { key: 'fulfillment_status', label: 'Fulfillment status', type: 'select', options: [
              { value: '', label: '— any —' },
              { value: 'shipped',     label: 'Shipped' },
              { value: 'partial',     label: 'Partial' },
              { value: 'unshipped',   label: 'Unshipped' },
              { value: 'unfulfilled', label: 'Unfulfilled' },
            ]},
            { key: 'created_at_min', label: 'Created after', placeholder: '2026-01-01T00:00:00Z' },
            { key: 'created_at_max', label: 'Created before' },
            { key: 'ids',            label: 'IDs (comma separated)' },
          ],
          list_customers: [
            { key: 'ids',            label: 'IDs (comma separated)' },
            { key: 'created_at_min', label: 'Created after', placeholder: '2026-01-01T00:00:00Z' },
            { key: 'created_at_max', label: 'Created before' },
            { key: 'updated_at_min', label: 'Updated after' },
            { key: 'updated_at_max', label: 'Updated before' },
          ],
          list_collections: [
            { key: 'title',  label: 'Title (exact match)' },
            { key: 'handle', label: 'Handle (exact match)' },
            { key: 'ids',    label: 'IDs (comma separated)' },
            { key: 'published_status', label: 'Published status', type: 'select', options: [
              { value: '', label: '— any —' },
              { value: 'published',   label: 'Published' },
              { value: 'unpublished', label: 'Unpublished' },
            ]},
          ],
          list_draft_orders: [
            { key: 'status', label: 'Status', type: 'select', options: [
              { value: '', label: '— any —' },
              { value: 'open',         label: 'Open' },
              { value: 'invoice_sent', label: 'Invoice sent' },
              { value: 'completed',    label: 'Completed' },
            ]},
            { key: 'ids',            label: 'IDs (comma separated)' },
            { key: 'updated_at_min', label: 'Updated after' },
            { key: 'updated_at_max', label: 'Updated before' },
          ],
        };
        const listFilters: ShopifyField[] = LIST_FILTERS[op] ?? [];

        const queryParams: Record<string, any> = (params.query_params && typeof params.query_params === 'object')
          ? params.query_params
          : {};

        const setFilter = (key: string, value: any) => {
          const next = { ...queryParams };
          if (value === '' || value === null || value === undefined) {
            delete next[key];
          } else {
            next[key] = value;
          }
          setParam('query_params', next);
        };

        // Free-form extra key/value pairs for fields not covered above.
        // Stored as `body_extras` so the user can keep typing a key without
        // it being mirrored into `body` until both key + value are set.
        const extras: Array<{ key: string; value: string }> = Array.isArray(params.body_extras)
          ? params.body_extras
          : [];

        const coerce = (v: string): any => {
          if (v === 'true') return true;
          if (v === 'false') return false;
          if (v !== '' && /^-?\d+(\.\d+)?$/.test(v)) return Number(v);
          return v;
        };

        // Update extras AND rebuild body in a single onChange so the backend
        // always receives a merged `body` without needing a useEffect.
        const setExtras = (next: Array<{ key: string; value: string }>) => {
          if (readOnly) return;
          const newBody: Record<string, any> = { ...body };
          extras.forEach((r) => { if (r.key) delete newBody[r.key]; });
          next.forEach((r) => {
            const k = (r.key || '').trim();
            if (!k) return;
            newBody[k] = coerce(r.value);
          });
          onChange({ parameters: { ...params, body: newBody, body_extras: next } });
        };

        const showResourceId = ['get', 'update', 'delete'].includes(verb);
        const showBody       = ['create', 'update'].includes(verb);
        const showLimit      = verb === 'list';

        return (
          <>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel id="shopify-credential-label">Credential preset</InputLabel>
              <Select
                labelId="shopify-credential-label"
                label="Credential preset"
                value={params.credential_id ?? ''}
                onChange={(e) => setParam('credential_id', e.target.value)}
                onOpen={() => loadWorkflowCredentials()} disabled={readOnly || credentialsLoading}
              >
                <MenuItem value="">None (use inline fields)</MenuItem>
                {shopifyCredentials.map((cred) => (
                  <MenuItem key={cred.id} value={cred.id}>
                    {cred.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              Create/manage credentials in Workflow Credentials page, then select it here.
            </Typography>

            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel id="shopify-op-label">Operation</InputLabel>
              <Select
                labelId="shopify-op-label"
                label="Operation"
                value={op}
                onChange={(e) => setParam('operation', e.target.value)}
                disabled={readOnly}
              >
                <ListSubheader>Products</ListSubheader>
                <MenuItem value="list_products">List Products</MenuItem>
                <MenuItem value="get_product">Get Product</MenuItem>
                <MenuItem value="create_product">Create Product</MenuItem>
                <MenuItem value="update_product">Update Product</MenuItem>
                <MenuItem value="delete_product">Delete Product</MenuItem>

                <ListSubheader>Variants</ListSubheader>
                <MenuItem value="get_variant">Get Variant</MenuItem>
                <MenuItem value="update_variant">Update Variant</MenuItem>
                <MenuItem value="delete_variant">Delete Variant</MenuItem>

                <ListSubheader>Orders</ListSubheader>
                <MenuItem value="list_orders">List Orders</MenuItem>
                <MenuItem value="get_order">Get Order</MenuItem>
                <MenuItem value="create_order">Create Order</MenuItem>
                <MenuItem value="update_order">Update Order</MenuItem>
                <MenuItem value="delete_order">Delete Order</MenuItem>

                <ListSubheader>Draft Orders</ListSubheader>
                <MenuItem value="list_draft_orders">List Draft Orders</MenuItem>
                <MenuItem value="get_draft_order">Get Draft Order</MenuItem>
                <MenuItem value="create_draft_order">Create Draft Order</MenuItem>
                <MenuItem value="update_draft_order">Update Draft Order</MenuItem>
                <MenuItem value="delete_draft_order">Delete Draft Order</MenuItem>

                <ListSubheader>Customers</ListSubheader>
                <MenuItem value="list_customers">List Customers</MenuItem>
                <MenuItem value="get_customer">Get Customer</MenuItem>
                <MenuItem value="create_customer">Create Customer</MenuItem>
                <MenuItem value="update_customer">Update Customer</MenuItem>
                <MenuItem value="delete_customer">Delete Customer</MenuItem>

                <ListSubheader>Collections</ListSubheader>
                <MenuItem value="list_collections">List Collections</MenuItem>
                <MenuItem value="get_collection">Get Collection</MenuItem>
                <MenuItem value="create_collection">Create Collection</MenuItem>
                <MenuItem value="update_collection">Update Collection</MenuItem>
                <MenuItem value="delete_collection">Delete Collection</MenuItem>

                <ListSubheader>Advanced</ListSubheader>
                <MenuItem value="graphql">GraphQL Query</MenuItem>
              </Select>
            </FormControl>

            {showResourceId && (
              <TextField
                label={`${verb === 'get' ? 'Get' : verb === 'update' ? 'Update' : 'Delete'} ID`}
                placeholder="e.g. 7234567890123"
                size="small"
                fullWidth
                value={params.resource_id ?? ''}
                onChange={(e) => setParam('resource_id', e.target.value)}
                helperText="The numeric Shopify ID of the item. You can copy one from a successful List operation."
                sx={{ mb: 2 }}
              />
            )}

            {showBody && (
              <>
                <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                  {verb === 'create' ? 'New record fields' : 'Fields to update'}
                </Typography>
                {opFields.length === 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    No curated fields exist for this operation yet — use the custom fields below
                    to send any field documented in the Shopify Admin API.
                  </Typography>
                )}
                {opFields.map((f) => {
                  if (f.type === 'select') {
                    return (
                      <FormControl key={f.key} fullWidth size="small" sx={{ mb: 2 }}>
                        <InputLabel id={`shopify-field-${f.key}`}>
                          {f.label + (f.required ? ' *' : '')}
                        </InputLabel>
                        <Select
                          labelId={`shopify-field-${f.key}`}
                          label={f.label + (f.required ? ' *' : '')}
                          value={
                            body[f.key] === true
                              ? 'true'
                              : body[f.key] === false
                                ? 'false'
                                : (body[f.key] ?? '')
                          }
                          onChange={(e) => {
                            const v = e.target.value;
                            // Coerce 'true'/'false' strings to real booleans
                            // for boolean-like fields so Shopify accepts them.
                            const coerced = v === 'true' ? true : v === 'false' ? false : v;
                            setBodyField(f.key, coerced);
                          }}
                          disabled={readOnly}
                        >
                          {(f.options ?? []).map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    );
                  }
                  return (
                    <TextField
                      key={f.key}
                      label={f.label + (f.required ? ' *' : '')}
                      placeholder={f.placeholder}
                      helperText={f.helper}
                      size="small"
                      fullWidth
                      multiline={f.type === 'textarea'}
                      minRows={f.type === 'textarea' ? 3 : undefined}
                      type={f.type === 'number' ? 'number' : f.type === 'email' ? 'email' : 'text'}
                      value={body[f.key] ?? ''}
                      onChange={(e) => setBodyField(
                        f.key,
                        f.type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value,
                      )}
                      sx={{ mb: 2 }}
                    />
                  );
                })}

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Need a field that isn&apos;t listed? Add it as key/value pairs below — Shopify accepts any
                  field documented for this resource.
                </Typography>
                {extras.map((row, idx) => (
                  <Box key={`shopify-extra-${idx}`} display="flex" gap={1} sx={{ mb: 1 }}>
                    <TextField
                      label="Field name"
                      size="small"
                      fullWidth
                      value={row.key}
                      onChange={(e) => {
                        const next = [...extras];
                        next[idx] = { ...row, key: e.target.value };
                        setExtras(next);
                      }}
                    />
                    <TextField
                      label="Value"
                      size="small"
                      fullWidth
                      value={row.value}
                      onChange={(e) => {
                        const next = [...extras];
                        next[idx] = { ...row, value: e.target.value };
                        setExtras(next);
                      }}
                    />
                    <Button
                      color="error"
                      size="small"
                      onClick={() => setExtras(extras.filter((_, i) => i !== idx))}
                    >
                      Remove
                    </Button>
                  </Box>
                ))}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setExtras([...extras, { key: '', value: '' }])}
                  sx={{ mb: 2 }}
                >
                  Add custom field
                </Button>
              </>
            )}

            {showLimit && (
              <>
                {listFilters.length > 0 && (
                  <>
                    <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                      Filters (optional)
                    </Typography>
                    {listFilters.map((f) => {
                      if (f.type === 'select') {
                        return (
                          <FormControl key={f.key} fullWidth size="small" sx={{ mb: 2 }}>
                            <InputLabel id={`shopify-filter-${f.key}`}>{f.label}</InputLabel>
                            <Select
                              labelId={`shopify-filter-${f.key}`}
                              label={f.label}
                              value={queryParams[f.key] ?? ''}
                              onChange={(e) => setFilter(f.key, e.target.value)}
                              disabled={readOnly}
                            >
                              {(f.options ?? []).map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        );
                      }
                      return (
                        <TextField
                          key={f.key}
                          label={f.label}
                          placeholder={f.placeholder}
                          size="small"
                          fullWidth
                          value={queryParams[f.key] ?? ''}
                          onChange={(e) => setFilter(f.key, e.target.value)}
                          sx={{ mb: 2 }}
                        />
                      );
                    })}
                  </>
                )}
                <TextField
                  label="Limit"
                  size="small"
                  type="number"
                  fullWidth
                  value={params.limit ?? 50}
                  onChange={(e) => setParam('limit', Number(e.target.value))}
                  helperText="Maximum number of records to return (Shopify allows up to 250)."
                  sx={{ mb: 2 }}
                />
              </>
            )}

            <TextField
              label="API Version"
              size="small"
              fullWidth
              value={params.api_version ?? '2024-01'}
              onChange={(e) => setParam('api_version', e.target.value)}
              sx={{ mb: 2 }}
            />

            {op === 'graphql' && (
              <>
                <TextField
                  label="GraphQL Query *"
                  placeholder={'query { shop { name email } }'}
                  fullWidth
                  multiline
                  minRows={5}
                  value={params.graphql_query ?? ''}
                  onChange={(e) => setParam('graphql_query', e.target.value)}
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="GraphQL Variables (JSON, optional)"
                  placeholder={'{"first": 10}'}
                  fullWidth
                  multiline
                  minRows={3}
                  value={
                    typeof params.graphql_variables === 'string'
                      ? params.graphql_variables
                      : JSON.stringify(params.graphql_variables ?? {}, null, 2)
                  }
                  onChange={(e) => {
                    const raw = e.target.value;
                    try {
                      const parsed = raw.trim() === '' ? {} : JSON.parse(raw);
                      setParam('graphql_variables', parsed);
                    } catch {
                      // Keep the raw text in state so the user can keep typing
                      // even if it's not valid JSON yet; we'll re-parse on next edit.
                      setParam('graphql_variables', raw);
                    }
                  }}
                  helperText={'Must be a valid JSON object (e.g. {"id": "gid://shopify/Product/12345"}).'}
                  sx={{ mb: 2 }}
                />
              </>
            )}
          </>
        );
      })()}

      {/* Action: HubSpot */}
      {nodeType === 'action.hubspot' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="hubspot-credential-label">Credential preset</InputLabel>
            <Select
              labelId="hubspot-credential-label"
              label="Credential preset"
              value={params.credential_id ?? ''}
              onChange={(e) => setParam('credential_id', e.target.value)}
              onOpen={() => loadWorkflowCredentials()} disabled={readOnly || credentialsLoading}
            >
              <MenuItem value="">None (use inline fields)</MenuItem>
              {hubspotCredentials.map((cred) => (
                <MenuItem key={cred.id} value={cred.id}>
                  {cred.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Create/manage credentials in Workflow Credentials page, then select it here.
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="hubspot-op-label">Operation</InputLabel>
            <Select
              labelId="hubspot-op-label"
              label="Operation"
              value={params.operation ?? 'list_contacts'}
              onChange={(e) => setParam('operation', e.target.value)}
              disabled={readOnly}
            >
              <MenuItem value="list_contacts">List Contacts</MenuItem>
              <MenuItem value="get_contact">Get Contact</MenuItem>
              <MenuItem value="create_contact">Create Contact</MenuItem>
              <MenuItem value="update_contact">Update Contact</MenuItem>
              <MenuItem value="delete_contact">Delete Contact</MenuItem>
              <MenuItem value="list_deals">List Deals</MenuItem>
              <MenuItem value="get_deal">Get Deal</MenuItem>
              <MenuItem value="create_deal">Create Deal</MenuItem>
              <MenuItem value="update_deal">Update Deal</MenuItem>
              <MenuItem value="delete_deal">Delete Deal</MenuItem>
              <MenuItem value="list_companies">List Companies</MenuItem>
              <MenuItem value="get_company">Get Company</MenuItem>
              <MenuItem value="create_company">Create Company</MenuItem>
              <MenuItem value="update_company">Update Company</MenuItem>
              <MenuItem value="list_tickets">List Tickets</MenuItem>
              <MenuItem value="search">Search</MenuItem>
              <MenuItem value="send_email">Send Transactional Email</MenuItem>
              <MenuItem value="raw">Raw API Call</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="hubspot-object-label">Object Type</InputLabel>
            <Select
              labelId="hubspot-object-label"
              label="Object Type"
              value={params.object_type ?? 'contact'}
              onChange={(e) => setParam('object_type', e.target.value)}
              disabled={readOnly}
            >
              <MenuItem value="contact">Contact</MenuItem>
              <MenuItem value="deal">Deal</MenuItem>
              <MenuItem value="company">Company</MenuItem>
              <MenuItem value="ticket">Ticket</MenuItem>
              <MenuItem value="product">Product</MenuItem>
              <MenuItem value="line_item">Line Item</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Object ID (for get/update/delete)"
            size="small"
            fullWidth
            value={params.object_id ?? ''}
            onChange={(e) => setParam('object_id', e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Limit"
            size="small"
            type="number"
            fullWidth
            value={params.limit ?? 100}
            onChange={(e) => setParam('limit', Number(e.target.value))}
            sx={{ mb: 2 }}
          />
          {params.operation === 'send_email' && (
            <>
              <TextField
                label="Email ID (HubSpot template ID)"
                size="small"
                fullWidth
                value={params.email_id ?? ''}
                onChange={(e) => setParam('email_id', e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                label="To (email address)"
                size="small"
                fullWidth
                value={params.to ?? ''}
                onChange={(e) => setParam('to', e.target.value)}
                sx={{ mb: 2 }}
              />
            </>
          )}
          {params.operation === 'raw' && (
            <>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel id="hubspot-method-label">HTTP Method</InputLabel>
                <Select
                  labelId="hubspot-method-label"
                  label="HTTP Method"
                  value={params.method ?? 'GET'}
                  onChange={(e) => setParam('method', e.target.value)}
                  disabled={readOnly}
                >
                  {['GET', 'POST', 'PATCH', 'PUT', 'DELETE'].map((m) => (
                    <MenuItem key={m} value={m}>{m}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Path (e.g. crm/v3/objects/contacts)"
                size="small"
                fullWidth
                value={params.path ?? ''}
                onChange={(e) => setParam('path', e.target.value)}
                sx={{ mb: 2 }}
              />
            </>
          )}
        </>
      )}

      {/* Action: Xero */}
      {nodeType === 'action.xero' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="xero-credential-label">Credential preset</InputLabel>
            <Select
              labelId="xero-credential-label"
              label="Credential preset"
              value={params.credential_id ?? ''}
              onChange={(e) => setParam('credential_id', e.target.value)}
              onOpen={() => loadWorkflowCredentials()}
              disabled={readOnly || credentialsLoading}
            >
              <MenuItem value="">None (use inline fields)</MenuItem>
              {xeroCredentials.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Create your Xero app at developer.xero.com, then store access_token + tenant_id (and
            refresh_token + client_id/secret to enable auto-refresh) in Workflow Credentials.
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="xero-op-label">Operation</InputLabel>
            <Select
              labelId="xero-op-label"
              label="Operation"
              value={params.operation ?? 'list_invoices'}
              onChange={(e) => setParam('operation', e.target.value)}
              disabled={readOnly}
            >
              <MenuItem value="list_tenants">List Tenants (connections)</MenuItem>
              <MenuItem value="get_organisation">Get Organisation</MenuItem>
              <MenuItem value="list_invoices">List Invoices</MenuItem>
              <MenuItem value="get_invoice">Get Invoice</MenuItem>
              <MenuItem value="create_invoice">Create Invoice</MenuItem>
              <MenuItem value="update_invoice">Update Invoice</MenuItem>
              <MenuItem value="delete_invoice">Delete (void) Invoice</MenuItem>
              <MenuItem value="download_invoice_pdf">Download Invoice PDF</MenuItem>
              <MenuItem value="list_contacts">List Contacts</MenuItem>
              <MenuItem value="get_contact">Get Contact</MenuItem>
              <MenuItem value="create_contact">Create Contact</MenuItem>
              <MenuItem value="update_contact">Update Contact</MenuItem>
              <MenuItem value="list_items">List Items</MenuItem>
              <MenuItem value="create_item">Create Item</MenuItem>
              <MenuItem value="update_item">Update Item</MenuItem>
              <MenuItem value="list_accounts">List Accounts</MenuItem>
              <MenuItem value="list_banktransactions">List Bank Transactions</MenuItem>
              <MenuItem value="create_banktransaction">Create Bank Transaction</MenuItem>
              <MenuItem value="list_payments">List Payments</MenuItem>
              <MenuItem value="create_payment">Create Payment</MenuItem>
              <MenuItem value="list_creditnotes">List Credit Notes</MenuItem>
              <MenuItem value="list_purchaseorders">List Purchase Orders</MenuItem>
              <MenuItem value="list_quotes">List Quotes</MenuItem>
              <MenuItem value="list_taxrates">List Tax Rates</MenuItem>
              <MenuItem value="raw">Raw API Call</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Tenant ID (Xero organisation UUID)"
            size="small"
            fullWidth
            value={params.tenant_id ?? ''}
            onChange={(e) => setParam('tenant_id', e.target.value)}
            sx={{ mb: 2 }}
            helperText="Leave empty to use the value from the credential preset."
          />
          {['get_invoice', 'update_invoice', 'delete_invoice', 'download_invoice_pdf',
            'get_contact', 'update_contact', 'update_item'].includes(params.operation ?? '') && (
            <TextField
              label="Resource ID (Invoice / Contact / Item GUID)"
              size="small"
              fullWidth
              value={params.resource_id ?? ''}
              onChange={(e) => setParam('resource_id', e.target.value)}
              sx={{ mb: 2 }}
            />
          )}
          {(params.operation ?? '').startsWith('list_') && params.operation !== 'list_tenants' && (
            <>
              <TextField
                label='Where (e.g. Status=="AUTHORISED")'
                size="small"
                fullWidth
                value={params.where ?? ''}
                onChange={(e) => setParam('where', e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                label="Order (e.g. UpdatedDateUTC DESC)"
                size="small"
                fullWidth
                value={params.order ?? ''}
                onChange={(e) => setParam('order', e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                label="Page (Xero pagination, optional)"
                size="small"
                type="number"
                fullWidth
                value={params.page ?? ''}
                onChange={(e) => setParam('page', e.target.value === '' ? '' : Number(e.target.value))}
                sx={{ mb: 2 }}
              />
            </>
          )}
          {['create_invoice', 'update_invoice', 'create_contact', 'update_contact',
            'create_item', 'update_item', 'create_banktransaction', 'create_payment',
            'raw'].includes(params.operation ?? '') && (
            <TextField
              label="Body (JSON, supports {{templates}})"
              size="small"
              fullWidth
              multiline
              minRows={4}
              value={typeof params.body === 'string' ? params.body : JSON.stringify(params.body ?? {}, null, 2)}
              onChange={(e) => {
                try { setParam('body', JSON.parse(e.target.value || '{}')); }
                catch { setParam('body', e.target.value); }
              }}
              sx={{ mb: 2 }}
              helperText='Example: { "Type":"ACCREC", "Contact":{"ContactID":"..."}, "LineItems":[{"Description":"Widget","Quantity":1,"UnitAmount":99}] }'
            />
          )}
          {params.operation === 'raw' && (
            <>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel id="xero-method-label">HTTP Method</InputLabel>
                <Select
                  labelId="xero-method-label"
                  label="HTTP Method"
                  value={params.method ?? 'GET'}
                  onChange={(e) => setParam('method', e.target.value)}
                  disabled={readOnly}
                >
                  {['GET', 'POST', 'PUT', 'DELETE'].map((m) => (
                    <MenuItem key={m} value={m}>{m}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Path (e.g. Reports/ProfitAndLoss)"
                size="small"
                fullWidth
                value={params.path ?? ''}
                onChange={(e) => setParam('path', e.target.value)}
                sx={{ mb: 2 }}
              />
            </>
          )}
        </>
      )}

      {/* Action: MYOB */}
      {nodeType === 'action.myob' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="myob-credential-label">Credential preset</InputLabel>
            <Select
              labelId="myob-credential-label"
              label="Credential preset"
              value={params.credential_id ?? ''}
              onChange={(e) => setParam('credential_id', e.target.value)}
              onOpen={() => loadWorkflowCredentials()}
              disabled={readOnly || credentialsLoading}
            >
              <MenuItem value="">None (use inline fields)</MenuItem>
              {myobCredentials.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Register a developer app at developer.myob.com to get an api_key (x-myobapi-key) and
            obtain an access_token via OAuth2. Run "List Company Files" first to discover the
            company file ID needed for everything else.
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="myob-op-label">Operation</InputLabel>
            <Select
              labelId="myob-op-label"
              label="Operation"
              value={params.operation ?? 'list_company_files'}
              onChange={(e) => setParam('operation', e.target.value)}
              disabled={readOnly}
            >
              <MenuItem value="list_company_files">List Company Files</MenuItem>
              <MenuItem value="list_customer">List Customers</MenuItem>
              <MenuItem value="get_customer">Get Customer</MenuItem>
              <MenuItem value="create_customer">Create Customer</MenuItem>
              <MenuItem value="update_customer">Update Customer</MenuItem>
              <MenuItem value="list_supplier">List Suppliers</MenuItem>
              <MenuItem value="create_supplier">Create Supplier</MenuItem>
              <MenuItem value="list_item_invoice">List Item Invoices</MenuItem>
              <MenuItem value="get_item_invoice">Get Item Invoice</MenuItem>
              <MenuItem value="create_item_invoice">Create Item Invoice</MenuItem>
              <MenuItem value="update_item_invoice">Update Item Invoice</MenuItem>
              <MenuItem value="list_service_invoice">List Service Invoices</MenuItem>
              <MenuItem value="create_service_invoice">Create Service Invoice</MenuItem>
              <MenuItem value="list_customer_payment">List Customer Payments</MenuItem>
              <MenuItem value="create_customer_payment">Create Customer Payment</MenuItem>
              <MenuItem value="list_item_purchase">List Item Bills</MenuItem>
              <MenuItem value="create_item_purchase">Create Item Bill</MenuItem>
              <MenuItem value="list_supplier_payment">List Supplier Payments</MenuItem>
              <MenuItem value="list_account">List Accounts</MenuItem>
              <MenuItem value="list_tax_code">List Tax Codes</MenuItem>
              <MenuItem value="list_item">List Inventory Items</MenuItem>
              <MenuItem value="create_item">Create Inventory Item</MenuItem>
              <MenuItem value="list_spend_money">List Spend Money</MenuItem>
              <MenuItem value="create_spend_money">Create Spend Money</MenuItem>
              <MenuItem value="list_receive_money">List Receive Money</MenuItem>
              <MenuItem value="list_general_journal">List General Journals</MenuItem>
              <MenuItem value="raw">Raw API Call</MenuItem>
            </Select>
          </FormControl>
          {params.operation !== 'list_company_files' && (
            <TextField
              label="Company File ID (UID)"
              size="small"
              fullWidth
              value={params.company_file_id ?? ''}
              onChange={(e) => setParam('company_file_id', e.target.value)}
              sx={{ mb: 2 }}
              helperText="Run 'List Company Files' once to discover this. Leave empty to use the credential preset value."
            />
          )}
          {(params.operation ?? '').startsWith('get_') ||
           (params.operation ?? '').startsWith('update_') ||
           (params.operation ?? '').startsWith('delete_') ? (
            <TextField
              label="Resource UID"
              size="small"
              fullWidth
              value={params.resource_id ?? ''}
              onChange={(e) => setParam('resource_id', e.target.value)}
              sx={{ mb: 2 }}
            />
          ) : null}
          {(params.operation ?? '').startsWith('list_') && params.operation !== 'list_company_files' && (
            <>
              <TextField
                label='Filter ($filter, e.g. IsActive eq true)'
                size="small"
                fullWidth
                value={params.filter ?? ''}
                onChange={(e) => setParam('filter', e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                label='Order By ($orderby, e.g. Name asc)'
                size="small"
                fullWidth
                value={params.orderby ?? ''}
                onChange={(e) => setParam('orderby', e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                label="Top ($top, default 100)"
                size="small"
                type="number"
                fullWidth
                value={params.top ?? 100}
                onChange={(e) => setParam('top', Number(e.target.value))}
                sx={{ mb: 2 }}
              />
              <TextField
                label="Skip ($skip, default 0)"
                size="small"
                type="number"
                fullWidth
                value={params.skip ?? 0}
                onChange={(e) => setParam('skip', Number(e.target.value))}
                sx={{ mb: 2 }}
              />
            </>
          )}
          {((params.operation ?? '').startsWith('create_') ||
            (params.operation ?? '').startsWith('update_') ||
            params.operation === 'raw') && params.operation !== 'list_company_files' && (
            <TextField
              label="Body (JSON, supports {{templates}})"
              size="small"
              fullWidth
              multiline
              minRows={4}
              value={typeof params.body === 'string' ? params.body : JSON.stringify(params.body ?? {}, null, 2)}
              onChange={(e) => {
                try { setParam('body', JSON.parse(e.target.value || '{}')); }
                catch { setParam('body', e.target.value); }
              }}
              sx={{ mb: 2 }}
              helperText='Example (item invoice): { "Customer":{"UID":"..."}, "Date":"2026-04-20", "Lines":[{"Type":"Transaction","Item":{"UID":"..."},"Total":100,"TaxCode":{"UID":"..."}}] }'
            />
          )}
          {params.operation === 'raw' && (
            <>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel id="myob-method-label">HTTP Method</InputLabel>
                <Select
                  labelId="myob-method-label"
                  label="HTTP Method"
                  value={params.method ?? 'GET'}
                  onChange={(e) => setParam('method', e.target.value)}
                  disabled={readOnly}
                >
                  {['GET', 'POST', 'PUT', 'DELETE'].map((m) => (
                    <MenuItem key={m} value={m}>{m}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Path (e.g. {company_file_id}/Sale/Invoice/Item)"
                size="small"
                fullWidth
                value={params.path ?? ''}
                onChange={(e) => setParam('path', e.target.value)}
                sx={{ mb: 2 }}
              />
            </>
          )}
        </>
      )}

      {/* Action: Zoom */}
      {nodeType === 'action.zoom' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="zoom-credential-label">Credential preset</InputLabel>
            <Select labelId="zoom-credential-label" label="Credential preset" value={params.credential_id ?? ''} onChange={(e) => setParam('credential_id', e.target.value)} onOpen={() => loadWorkflowCredentials()} disabled={readOnly || credentialsLoading}>
              <MenuItem value="">None (use inline fields)</MenuItem>
              {zoomCredentials.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>Create/manage credentials in Workflow Credentials page, then select it here.</Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="zoom-op-label">Operation</InputLabel>
            <Select labelId="zoom-op-label" label="Operation" value={params.operation ?? 'create_meeting'} onChange={(e) => setParam('operation', e.target.value)} disabled={readOnly}>
              <MenuItem value="create_meeting">Create Meeting</MenuItem>
              <MenuItem value="get_meeting">Get Meeting</MenuItem>
              <MenuItem value="list_meetings">List Meetings</MenuItem>
              <MenuItem value="update_meeting">Update Meeting</MenuItem>
              <MenuItem value="delete_meeting">Delete Meeting</MenuItem>
            </Select>
          </FormControl>
          {['create_meeting', 'update_meeting'].includes(params.operation ?? 'create_meeting') && (
            <TextField label="Topic" size="small" fullWidth value={params.topic ?? ''} onChange={(e) => setParam('topic', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
          {params.operation === 'create_meeting' && (
            <>
              <TextField label="Start Time (ISO 8601, e.g. 2026-04-01T10:00:00Z)" size="small" fullWidth value={params.start_time ?? ''} onChange={(e) => setParam('start_time', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
              <TextField label="Duration (minutes)" size="small" type="number" fullWidth value={params.duration ?? 60} onChange={(e) => setParam('duration', Number(e.target.value))} sx={{ mb: 2 }} disabled={readOnly} />
            </>
          )}
          {['get_meeting', 'update_meeting', 'delete_meeting'].includes(params.operation ?? '') && (
            <TextField label="Meeting ID" size="small" fullWidth value={params.meeting_id ?? ''} onChange={(e) => setParam('meeting_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
        </>
      )}

      {/* Action: Discord */}
      {nodeType === 'action.discord' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="discord-credential-label">Credential preset</InputLabel>
            <Select labelId="discord-credential-label" label="Credential preset" value={params.credential_id ?? ''} onChange={(e) => setParam('credential_id', e.target.value)} onOpen={() => loadWorkflowCredentials()} disabled={readOnly || credentialsLoading}>
              <MenuItem value="">None (use inline fields)</MenuItem>
              {discordCredentials.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>Create/manage credentials in Workflow Credentials page, then select it here.</Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="discord-op-label">Operation</InputLabel>
            <Select labelId="discord-op-label" label="Operation" value={params.operation ?? 'send_message'} onChange={(e) => setParam('operation', e.target.value)} disabled={readOnly}>
              <MenuItem value="send_message">Send Message</MenuItem>
              <MenuItem value="send_embed">Send Embed</MenuItem>
            </Select>
          </FormControl>
          <TextField label="Content / Message" fullWidth multiline minRows={2} value={params.content ?? ''} onChange={(e) => setParam('content', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          {params.operation === 'send_embed' && (
            <>
              <TextField label="Embed Title" size="small" fullWidth value={params.title ?? ''} onChange={(e) => setParam('title', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
              <TextField label="Embed Description" fullWidth multiline minRows={2} value={params.description ?? ''} onChange={(e) => setParam('description', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
            </>
          )}
          <TextField label="Username (optional override)" size="small" fullWidth value={params.username ?? ''} onChange={(e) => setParam('username', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          <TextField label="Channel ID (bot token only)" size="small" fullWidth value={params.channel_id ?? ''} onChange={(e) => setParam('channel_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
        </>
      )}

      {/* Action: Microsoft Teams */}
      {nodeType === 'action.microsoft_teams' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="msteams-credential-label">Credential preset</InputLabel>
            <Select labelId="msteams-credential-label" label="Credential preset" value={params.credential_id ?? ''} onChange={(e) => setParam('credential_id', e.target.value)} onOpen={() => loadWorkflowCredentials()} disabled={readOnly || credentialsLoading}>
              <MenuItem value="">None (use inline fields)</MenuItem>
              {microsoftTeamsCredentials.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>Create/manage credentials in Workflow Credentials page, then select it here.</Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="msteams-op-label">Operation</InputLabel>
            <Select labelId="msteams-op-label" label="Operation" value={params.operation ?? 'send_message'} onChange={(e) => setParam('operation', e.target.value)} disabled={readOnly}>
              <MenuItem value="send_message">Send Message</MenuItem>
              <MenuItem value="send_card">Send Adaptive Card</MenuItem>
            </Select>
          </FormControl>
          <TextField label="Title (optional)" size="small" fullWidth value={params.title ?? ''} onChange={(e) => setParam('title', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          <TextField label="Text / Body" fullWidth multiline minRows={3} value={params.text ?? ''} onChange={(e) => setParam('text', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
        </>
      )}

      {/* Action: Jira */}
      {nodeType === 'action.jira' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="jira-credential-label">Credential preset</InputLabel>
            <Select labelId="jira-credential-label" label="Credential preset" value={params.credential_id ?? ''} onChange={(e) => setParam('credential_id', e.target.value)} onOpen={() => loadWorkflowCredentials()} disabled={readOnly || credentialsLoading}>
              <MenuItem value="">None (use inline fields)</MenuItem>
              {jiraCredentials.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>Create/manage credentials in Workflow Credentials page, then select it here.</Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="jira-op-label">Operation</InputLabel>
            <Select labelId="jira-op-label" label="Operation" value={params.operation ?? 'create_issue'} onChange={(e) => setParam('operation', e.target.value)} disabled={readOnly}>
              <MenuItem value="create_issue">Create Issue</MenuItem>
              <MenuItem value="get_issue">Get Issue</MenuItem>
              <MenuItem value="update_issue">Update Issue</MenuItem>
              <MenuItem value="search_issues">Search Issues (JQL)</MenuItem>
              <MenuItem value="list_projects">List Projects</MenuItem>
            </Select>
          </FormControl>
          {['create_issue', 'update_issue'].includes(params.operation ?? 'create_issue') && (
            <>
              <TextField label="Project Key" size="small" fullWidth value={params.project_key ?? ''} onChange={(e) => setParam('project_key', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
              <TextField label="Summary" size="small" fullWidth value={params.summary ?? ''} onChange={(e) => setParam('summary', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
              <TextField label="Description" fullWidth multiline minRows={2} value={params.description ?? ''} onChange={(e) => setParam('description', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
              <TextField label="Issue Type (e.g. Task, Bug)" size="small" fullWidth value={params.issue_type ?? 'Task'} onChange={(e) => setParam('issue_type', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
            </>
          )}
          {['get_issue', 'update_issue'].includes(params.operation ?? '') && (
            <TextField label="Issue Key (e.g. PROJ-123)" size="small" fullWidth value={params.issue_key ?? ''} onChange={(e) => setParam('issue_key', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
          {params.operation === 'search_issues' && (
            <TextField label="JQL Query" size="small" fullWidth value={params.jql ?? ''} onChange={(e) => setParam('jql', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
        </>
      )}

      {/* Action: Confluence */}
      {nodeType === 'action.confluence' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="confluence-credential-label">Credential preset</InputLabel>
            <Select labelId="confluence-credential-label" label="Credential preset" value={params.credential_id ?? ''} onChange={(e) => setParam('credential_id', e.target.value)} onOpen={() => loadWorkflowCredentials()} disabled={readOnly || credentialsLoading}>
              <MenuItem value="">None (use inline fields)</MenuItem>
              {confluenceCredentials.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>Create/manage credentials in Workflow Credentials page, then select it here.</Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="confluence-op-label">Operation</InputLabel>
            <Select labelId="confluence-op-label" label="Operation" value={params.operation ?? 'create_page'} onChange={(e) => setParam('operation', e.target.value)} disabled={readOnly}>
              <MenuItem value="create_page">Create Page</MenuItem>
              <MenuItem value="get_page">Get Page</MenuItem>
              <MenuItem value="update_page">Update Page</MenuItem>
              <MenuItem value="search_pages">Search Pages</MenuItem>
            </Select>
          </FormControl>
          {params.operation === 'create_page' && (
            <>
              <TextField label="Space Key" size="small" fullWidth value={params.space_key ?? ''} onChange={(e) => setParam('space_key', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
              <TextField label="Title" size="small" fullWidth value={params.title ?? ''} onChange={(e) => setParam('title', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
              <TextField label="Body (HTML/storage format)" fullWidth multiline minRows={3} value={params.body ?? ''} onChange={(e) => setParam('body', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
              <TextField label="Parent Page ID (optional)" size="small" fullWidth value={params.parent_id ?? ''} onChange={(e) => setParam('parent_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
            </>
          )}
          {['get_page', 'update_page'].includes(params.operation ?? '') && (
            <TextField label="Page ID" size="small" fullWidth value={params.page_id ?? ''} onChange={(e) => setParam('page_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
          {params.operation === 'update_page' && (
            <>
              <TextField label="Title" size="small" fullWidth value={params.title ?? ''} onChange={(e) => setParam('title', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
              <TextField label="Body (HTML/storage format)" fullWidth multiline minRows={3} value={params.body ?? ''} onChange={(e) => setParam('body', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
              <TextField label="Current Version Number" size="small" type="number" fullWidth value={params.version ?? 1} onChange={(e) => setParam('version', Number(e.target.value))} sx={{ mb: 2 }} disabled={readOnly} />
            </>
          )}
          {params.operation === 'search_pages' && (
            <TextField label="Search Query" size="small" fullWidth value={params.query ?? ''} onChange={(e) => setParam('query', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
        </>
      )}

      {/* Action: Google Calendar */}
      {nodeType === 'action.google_calendar' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="gcal-credential-label">Credential preset</InputLabel>
            <Select labelId="gcal-credential-label" label="Credential preset" value={params.credential_id ?? ''} onChange={(e) => setParam('credential_id', e.target.value)} onOpen={() => loadWorkflowCredentials()} disabled={readOnly || credentialsLoading}>
              <MenuItem value="">None (use inline fields)</MenuItem>
              {googleCalendarCredentials.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>Create/manage credentials in Workflow Credentials page, then select it here.</Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="gcal-op-label">Operation</InputLabel>
            <Select labelId="gcal-op-label" label="Operation" value={params.operation ?? 'list_events'} onChange={(e) => setParam('operation', e.target.value)} disabled={readOnly}>
              <MenuItem value="list_events">List Events</MenuItem>
              <MenuItem value="create_event">Create Event</MenuItem>
              <MenuItem value="get_event">Get Event</MenuItem>
              <MenuItem value="update_event">Update Event</MenuItem>
              <MenuItem value="delete_event">Delete Event</MenuItem>
            </Select>
          </FormControl>
          <Box display="flex" gap={1} sx={{ mb: 2 }}>
            <Button size="small" variant="outlined" onClick={fetchCalendarList} disabled={calendarsLoading || readOnly}>
              {calendarsLoading ? <CircularProgress size={16} /> : 'Load calendars'}
            </Button>
            {calendarList.length > 0 && (
              <FormControl fullWidth size="small">
                <InputLabel id="gcal-id-label">Select calendar</InputLabel>
                <Select
                  labelId="gcal-id-label"
                  label="Select calendar"
                  value={params.calendar_id ?? 'primary'}
                  onChange={(e) => setParam('calendar_id', e.target.value)}
                >
                  {calendarList.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.summary}{c.primary ? ' (primary)' : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
          <TextField label="Calendar ID (default: primary)" size="small" fullWidth value={params.calendar_id ?? 'primary'} onChange={(e) => setParam('calendar_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          {params.operation === 'list_events' && (
            <>
              <TextField label="Time Min (ISO 8601)" size="small" fullWidth value={params.time_min ?? ''} onChange={(e) => setParam('time_min', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
              <TextField label="Time Max (ISO 8601)" size="small" fullWidth value={params.time_max ?? ''} onChange={(e) => setParam('time_max', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
            </>
          )}
          {params.operation === 'create_event' && (
            <>
              <TextField label="Summary (title)" size="small" fullWidth value={params.summary ?? ''} onChange={(e) => setParam('summary', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
              <TextField label="Start (ISO 8601)" size="small" fullWidth value={params.start ?? ''} onChange={(e) => setParam('start', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
              <TextField label="End (ISO 8601)" size="small" fullWidth value={params.end ?? ''} onChange={(e) => setParam('end', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
              <TextField label="Description" fullWidth multiline minRows={2} value={params.description ?? ''} onChange={(e) => setParam('description', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
            </>
          )}
          {['get_event', 'update_event', 'delete_event'].includes(params.operation ?? '') && (
            <TextField label="Event ID" size="small" fullWidth value={params.event_id ?? ''} onChange={(e) => setParam('event_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
          {params.operation === 'update_event' && (
            <TextField label="Summary (title)" size="small" fullWidth value={params.summary ?? ''} onChange={(e) => setParam('summary', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
        </>
      )}

      {/* Action: Notion */}
      {nodeType === 'action.notion' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="notion-credential-label">Credential preset</InputLabel>
            <Select labelId="notion-credential-label" label="Credential preset" value={params.credential_id ?? ''} onChange={(e) => setParam('credential_id', e.target.value)} onOpen={() => loadWorkflowCredentials()} disabled={readOnly || credentialsLoading}>
              <MenuItem value="">None (use inline fields)</MenuItem>
              {notionCredentials.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>Create/manage credentials in Workflow Credentials page, then select it here.</Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="notion-op-label">Operation</InputLabel>
            <Select labelId="notion-op-label" label="Operation" value={params.operation ?? 'create_page'} onChange={(e) => setParam('operation', e.target.value)} disabled={readOnly}>
              <MenuItem value="create_page">Create Page</MenuItem>
              <MenuItem value="get_page">Get Page</MenuItem>
              <MenuItem value="update_page">Update Page</MenuItem>
              <MenuItem value="query_database">Query Database</MenuItem>
              <MenuItem value="create_database_item">Create Database Item</MenuItem>
            </Select>
          </FormControl>
          {['create_page'].includes(params.operation ?? 'create_page') && (
            <>
              <TextField label="Parent ID (page or database ID)" size="small" fullWidth value={params.parent_id ?? ''} onChange={(e) => setParam('parent_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel id="notion-parent-type-label">Parent Type</InputLabel>
                <Select labelId="notion-parent-type-label" label="Parent Type" value={params.parent_type ?? 'database_id'} onChange={(e) => setParam('parent_type', e.target.value)} disabled={readOnly}>
                  <MenuItem value="database_id">Database</MenuItem>
                  <MenuItem value="page_id">Page</MenuItem>
                </Select>
              </FormControl>
              <TextField label="Title" size="small" fullWidth value={params.title ?? ''} onChange={(e) => setParam('title', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
              <TextField label="Content (paragraph)" fullWidth multiline minRows={2} value={params.content ?? ''} onChange={(e) => setParam('content', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
            </>
          )}
          {['get_page', 'update_page'].includes(params.operation ?? '') && (
            <TextField label="Page ID" size="small" fullWidth value={params.page_id ?? ''} onChange={(e) => setParam('page_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
          {params.operation === 'update_page' && (
            <TextField label="Title" size="small" fullWidth value={params.title ?? ''} onChange={(e) => setParam('title', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
          {['query_database', 'create_database_item'].includes(params.operation ?? '') && (
            <TextField label="Database ID" size="small" fullWidth value={params.database_id ?? ''} onChange={(e) => setParam('database_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
          {params.operation === 'create_database_item' && (
            <TextField label="Title (Name property)" size="small" fullWidth value={params.title ?? ''} onChange={(e) => setParam('title', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
          {params.operation === 'query_database' && (
            <TextField label="Filter (JSON, optional)" fullWidth multiline minRows={2} value={typeof params.filter === 'string' ? params.filter : JSON.stringify(params.filter ?? {})} onChange={(e) => setParam('filter', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
        </>
      )}

      {/* Action: Airtable */}
      {nodeType === 'action.airtable' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="airtable-credential-label">Credential preset</InputLabel>
            <Select labelId="airtable-credential-label" label="Credential preset" value={params.credential_id ?? ''} onChange={(e) => setParam('credential_id', e.target.value)} onOpen={() => loadWorkflowCredentials()} disabled={readOnly || credentialsLoading}>
              <MenuItem value="">None (use inline fields)</MenuItem>
              {airtableCredentials.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>Create/manage credentials in Workflow Credentials page, then select it here.</Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="airtable-op-label">Operation</InputLabel>
            <Select labelId="airtable-op-label" label="Operation" value={params.operation ?? 'list_records'} onChange={(e) => setParam('operation', e.target.value)} disabled={readOnly}>
              <MenuItem value="list_records">List Records</MenuItem>
              <MenuItem value="create_record">Create Record</MenuItem>
              <MenuItem value="get_record">Get Record</MenuItem>
              <MenuItem value="update_record">Update Record</MenuItem>
              <MenuItem value="delete_record">Delete Record</MenuItem>
            </Select>
          </FormControl>
          <TextField label="Base ID" size="small" fullWidth value={params.base_id ?? ''} onChange={(e) => setParam('base_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          <TextField label="Table Name" size="small" fullWidth value={params.table ?? ''} onChange={(e) => setParam('table', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          {['get_record', 'update_record', 'delete_record'].includes(params.operation ?? '') && (
            <TextField label="Record ID" size="small" fullWidth value={params.record_id ?? ''} onChange={(e) => setParam('record_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
          {['create_record', 'update_record'].includes(params.operation ?? '') && (
            <TextField label="Fields (JSON)" fullWidth multiline minRows={3} value={typeof params.fields === 'string' ? params.fields : JSON.stringify(params.fields ?? {})} onChange={(e) => setParam('fields', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
          {params.operation === 'list_records' && (
            <TextField label="Max Records" size="small" type="number" fullWidth value={params.max_records ?? 100} onChange={(e) => setParam('max_records', Number(e.target.value))} sx={{ mb: 2 }} disabled={readOnly} />
          )}
        </>
      )}

      {/* Action: Dropbox */}
      {nodeType === 'action.dropbox' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="dropbox-credential-label">Credential preset</InputLabel>
            <Select labelId="dropbox-credential-label" label="Credential preset" value={params.credential_id ?? ''} onChange={(e) => setParam('credential_id', e.target.value)} onOpen={() => loadWorkflowCredentials()} disabled={readOnly || credentialsLoading}>
              <MenuItem value="">None (use inline fields)</MenuItem>
              {dropboxCredentials.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>Create/manage credentials in Workflow Credentials page, then select it here.</Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="dropbox-op-label">Operation</InputLabel>
            <Select labelId="dropbox-op-label" label="Operation" value={params.operation ?? 'list_folder'} onChange={(e) => setParam('operation', e.target.value)} disabled={readOnly}>
              <MenuItem value="list_folder">List Folder</MenuItem>
              <MenuItem value="upload_file">Upload File</MenuItem>
              <MenuItem value="download_file">Download File</MenuItem>
              <MenuItem value="create_folder">Create Folder</MenuItem>
              <MenuItem value="delete_file">Delete File / Folder</MenuItem>
            </Select>
          </FormControl>
          <TextField label="Path (e.g. /folder or /folder/file.txt)" size="small" fullWidth value={params.path ?? ''} onChange={(e) => setParam('path', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          {params.operation === 'upload_file' && (
            <TextField label="File Content" fullWidth multiline minRows={3} value={params.content ?? ''} onChange={(e) => setParam('content', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
        </>
      )}

      {/* Action: Zoho CRM */}
      {nodeType === 'action.zoho_crm' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="zohocrm-credential-label">Credential preset</InputLabel>
            <Select labelId="zohocrm-credential-label" label="Credential preset" value={params.credential_id ?? ''} onChange={(e) => setParam('credential_id', e.target.value)} onOpen={() => loadWorkflowCredentials()} disabled={readOnly || credentialsLoading}>
              <MenuItem value="">None (use inline fields)</MenuItem>
              {zohoCrmCredentials.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>Create/manage credentials in Workflow Credentials page, then select it here.</Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="zohocrm-op-label">Operation</InputLabel>
            <Select labelId="zohocrm-op-label" label="Operation" value={params.operation ?? 'create_record'} onChange={(e) => setParam('operation', e.target.value)} disabled={readOnly}>
              <MenuItem value="create_record">Create Record</MenuItem>
              <MenuItem value="get_record">Get Record</MenuItem>
              <MenuItem value="search_records">Search Records</MenuItem>
              <MenuItem value="update_record">Update Record</MenuItem>
              <MenuItem value="delete_record">Delete Record</MenuItem>
            </Select>
          </FormControl>
          <TextField label="Module (e.g. Leads, Contacts, Deals)" size="small" fullWidth value={params.module ?? 'Leads'} onChange={(e) => setParam('module', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          <TextField label="Region (e.g. com, eu, in)" size="small" fullWidth value={params.region ?? 'com'} onChange={(e) => setParam('region', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          {['create_record', 'update_record'].includes(params.operation ?? 'create_record') && (
            <TextField label="Data (JSON)" fullWidth multiline minRows={3} value={typeof params.data === 'string' ? params.data : JSON.stringify(params.data ?? {})} onChange={(e) => setParam('data', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
          {['get_record', 'update_record', 'delete_record'].includes(params.operation ?? '') && (
            <TextField label="Record ID" size="small" fullWidth value={params.record_id ?? ''} onChange={(e) => setParam('record_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
          {params.operation === 'search_records' && (
            <TextField label="Criteria (e.g. (Last_Name:equals:Smith))" size="small" fullWidth value={params.criteria ?? ''} onChange={(e) => setParam('criteria', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
        </>
      )}

      {/* Action: Insightly */}
      {nodeType === 'action.insightly' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="insightly-credential-label">Credential preset</InputLabel>
            <Select labelId="insightly-credential-label" label="Credential preset" value={params.credential_id ?? ''} onChange={(e) => setParam('credential_id', e.target.value)} onOpen={() => loadWorkflowCredentials()} disabled={readOnly || credentialsLoading}>
              <MenuItem value="">None (use inline fields)</MenuItem>
              {insightlyCredentials.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>Create/manage credentials in Workflow Credentials page, then select it here.</Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="insightly-op-label">Operation</InputLabel>
            <Select labelId="insightly-op-label" label="Operation" value={params.operation ?? 'create_contact'} onChange={(e) => setParam('operation', e.target.value)} disabled={readOnly}>
              <MenuItem value="create_contact">Create Contact</MenuItem>
              <MenuItem value="get_contact">Get Contact</MenuItem>
              <MenuItem value="update_contact">Update Contact</MenuItem>
              <MenuItem value="search_contacts">Search Contacts</MenuItem>
              <MenuItem value="create_lead">Create Lead</MenuItem>
            </Select>
          </FormControl>
          {['create_contact', 'update_contact', 'create_lead'].includes(params.operation ?? 'create_contact') && (
            <>
              <TextField label="First Name" size="small" fullWidth value={params.first_name ?? ''} onChange={(e) => setParam('first_name', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
              <TextField label="Last Name" size="small" fullWidth value={params.last_name ?? ''} onChange={(e) => setParam('last_name', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
              <TextField label="Email" size="small" fullWidth value={params.email ?? ''} onChange={(e) => setParam('email', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
            </>
          )}
          {['get_contact', 'update_contact'].includes(params.operation ?? '') && (
            <TextField label="Contact ID" size="small" fullWidth value={params.contact_id ?? ''} onChange={(e) => setParam('contact_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
          {params.operation === 'search_contacts' && (
            <TextField label="Email to search" size="small" fullWidth value={params.query ?? ''} onChange={(e) => setParam('query', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
        </>
      )}

      {/* Action: Cal.com */}
      {nodeType === 'action.cal_com' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="calcom-credential-label">Credential preset</InputLabel>
            <Select labelId="calcom-credential-label" label="Credential preset" value={params.credential_id ?? ''} onChange={(e) => setParam('credential_id', e.target.value)} onOpen={() => loadWorkflowCredentials()} disabled={readOnly || credentialsLoading}>
              <MenuItem value="">None (use inline fields)</MenuItem>
              {calComCredentials.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>Create/manage credentials in Workflow Credentials page, then select it here.</Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="calcom-op-label">Operation</InputLabel>
            <Select labelId="calcom-op-label" label="Operation" value={params.operation ?? 'list_bookings'} onChange={(e) => setParam('operation', e.target.value)} disabled={readOnly}>
              <MenuItem value="list_bookings">List Bookings</MenuItem>
              <MenuItem value="get_booking">Get Booking</MenuItem>
              <MenuItem value="create_booking">Create Booking</MenuItem>
              <MenuItem value="cancel_booking">Cancel Booking</MenuItem>
            </Select>
          </FormControl>
          {['get_booking', 'cancel_booking'].includes(params.operation ?? '') && (
            <TextField label="Booking ID" size="small" fullWidth value={params.booking_id ?? ''} onChange={(e) => setParam('booking_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
          {params.operation === 'cancel_booking' && (
            <TextField label="Cancellation Reason" size="small" fullWidth value={params.reason ?? ''} onChange={(e) => setParam('reason', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
          {params.operation === 'create_booking' && (
            <>
              <TextField label="Event Type ID" size="small" type="number" fullWidth value={params.event_type_id ?? ''} onChange={(e) => setParam('event_type_id', Number(e.target.value))} sx={{ mb: 2 }} disabled={readOnly} />
              <TextField label="Start (ISO 8601)" size="small" fullWidth value={params.start ?? ''} onChange={(e) => setParam('start', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
              <TextField label="Name" size="small" fullWidth value={params.name ?? ''} onChange={(e) => setParam('name', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
              <TextField label="Email" size="small" fullWidth value={params.email ?? ''} onChange={(e) => setParam('email', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
              <TextField label="Timezone (e.g. UTC)" size="small" fullWidth value={params.timezone ?? 'UTC'} onChange={(e) => setParam('timezone', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
            </>
          )}
        </>
      )}

      {/* Action: ERPNext */}
      {nodeType === 'action.erpnext' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="erpnext-credential-label">Credential preset</InputLabel>
            <Select labelId="erpnext-credential-label" label="Credential preset" value={params.credential_id ?? ''} onChange={(e) => setParam('credential_id', e.target.value)} onOpen={() => loadWorkflowCredentials()} disabled={readOnly || credentialsLoading}>
              <MenuItem value="">None (use inline fields)</MenuItem>
              {erpnextCredentials.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>Create/manage credentials in Workflow Credentials page, then select it here.</Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="erpnext-op-label">Operation</InputLabel>
            <Select labelId="erpnext-op-label" label="Operation" value={params.operation ?? 'create_document'} onChange={(e) => setParam('operation', e.target.value)} disabled={readOnly}>
              <MenuItem value="create_document">Create Document</MenuItem>
              <MenuItem value="get_document">Get Document</MenuItem>
              <MenuItem value="update_document">Update Document</MenuItem>
              <MenuItem value="search_documents">Search Documents</MenuItem>
            </Select>
          </FormControl>
          <TextField label="DocType (e.g. Customer, Sales Order)" size="small" fullWidth value={params.doctype ?? ''} onChange={(e) => setParam('doctype', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          {['get_document', 'update_document'].includes(params.operation ?? '') && (
            <TextField label="Document Name" size="small" fullWidth value={params.doc_name ?? ''} onChange={(e) => setParam('doc_name', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
          {['create_document', 'update_document'].includes(params.operation ?? 'create_document') && (
            <TextField label="Data (JSON)" fullWidth multiline minRows={3} value={typeof params.data === 'string' ? params.data : JSON.stringify(params.data ?? {})} onChange={(e) => setParam('data', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
          {params.operation === 'search_documents' && (
            <TextField label={'Filters (JSON array, e.g. [["status","=","Active"]])'} fullWidth multiline minRows={2} value={params.filters ?? '[]'} onChange={(e) => setParam('filters', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
        </>
      )}

      {/* Action: Mailchimp */}
      {nodeType === 'action.mailchimp' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="mailchimp-credential-label">Credential preset</InputLabel>
            <Select labelId="mailchimp-credential-label" label="Credential preset" value={params.credential_id ?? ''} onChange={(e) => setParam('credential_id', e.target.value)} onOpen={() => loadWorkflowCredentials()} disabled={readOnly || credentialsLoading}>
              <MenuItem value="">None (use inline fields)</MenuItem>
              {mailchimpCredentials.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>Create/manage credentials in Workflow Credentials page, then select it here.</Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="mailchimp-op-label">Operation</InputLabel>
            <Select labelId="mailchimp-op-label" label="Operation" value={params.operation ?? 'add_subscriber'} onChange={(e) => setParam('operation', e.target.value)} disabled={readOnly}>
              <MenuItem value="add_subscriber">Add Subscriber</MenuItem>
              <MenuItem value="update_subscriber">Update Subscriber</MenuItem>
              <MenuItem value="get_subscriber">Get Subscriber</MenuItem>
              <MenuItem value="add_tag">Add Tag</MenuItem>
              <MenuItem value="send_campaign">Send Campaign</MenuItem>
            </Select>
          </FormControl>
          <TextField label="Audience / List ID" size="small" fullWidth value={params.list_id ?? ''} onChange={(e) => setParam('list_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          {['add_subscriber', 'update_subscriber', 'get_subscriber', 'add_tag'].includes(params.operation ?? 'add_subscriber') && (
            <TextField label="Email" size="small" fullWidth value={params.email ?? ''} onChange={(e) => setParam('email', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
          {['add_subscriber', 'update_subscriber'].includes(params.operation ?? 'add_subscriber') && (
            <>
              <TextField label="First Name" size="small" fullWidth value={params.first_name ?? ''} onChange={(e) => setParam('first_name', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
              <TextField label="Last Name" size="small" fullWidth value={params.last_name ?? ''} onChange={(e) => setParam('last_name', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
            </>
          )}
          {params.operation === 'add_subscriber' && (
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel id="mailchimp-status-label">Status</InputLabel>
              <Select labelId="mailchimp-status-label" label="Status" value={params.status ?? 'subscribed'} onChange={(e) => setParam('status', e.target.value)} disabled={readOnly}>
                <MenuItem value="subscribed">Subscribed</MenuItem>
                <MenuItem value="pending">Pending (double opt-in)</MenuItem>
                <MenuItem value="unsubscribed">Unsubscribed</MenuItem>
              </Select>
            </FormControl>
          )}
          {params.operation === 'add_tag' && (
            <TextField label="Tag Name" size="small" fullWidth value={params.tag_name ?? ''} onChange={(e) => setParam('tag_name', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
          {params.operation === 'send_campaign' && (
            <TextField label="Campaign ID" size="small" fullWidth value={params.campaign_id ?? ''} onChange={(e) => setParam('campaign_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
        </>
      )}

      {/* Action: Qdrant */}
      {nodeType === 'action.qdrant' && (
        <>
          <TextField
            label="Base URL"
            size="small"
            fullWidth
            value={params.base_url ?? ''}
            onChange={(e) => setParam('base_url', e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="API Key"
            size="small"
            type="password"
            fullWidth
            value={params.api_key ?? ''}
            onChange={(e) => setParam('api_key', e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Collection"
            size="small"
            fullWidth
            value={params.collection ?? ''}
            onChange={(e) => setParam('collection', e.target.value)}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="qdrant-operation-label">Operation</InputLabel>
            <Select
              labelId="qdrant-operation-label"
              label="Operation"
              value={params.operation ?? 'search'}
              onChange={(e) => setParam('operation', e.target.value)}
            >
              {['search', 'upsert', 'delete'].map((op) => (
                <MenuItem key={op} value={op}>
                  {op}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Vector (comma-separated)"
            fullWidth
            value={qdrantVectorText}
            onChange={(e) => setQdrantVectorText(e.target.value)}
            onBlur={() => {
              const vec = qdrantVectorText
                .split(',')
                .map((v) => v.trim())
                .filter((v) => v.length)
                .map((v) => Number(v));
              setParam('vector', vec);
            }}
            sx={{ mb: 2 }}
          />
          {renderQdrantPointsEditor()}
          {renderKeyValueEditor('qdrant.filter', 'Filter')}
          <TextField
            label="Top K"
            size="small"
            type="number"
            fullWidth
            value={params.top_k ?? 5}
            onChange={(e) => setParam('top_k', Number(e.target.value))}
            sx={{ mb: 2 }}
          />
        </>
      )}

      {/* n8n Manual Trigger */}
      {nodeType === 'n8n-nodes-base.manualTrigger' && (
        <Typography variant="body2" color="text.secondary">
          Manual trigger — click "Execute" in the workflow editor to run this workflow.
          No configuration needed.
        </Typography>
      )}

      {/* n8n Set / Edit Fields */}
      {nodeType === 'n8n-nodes-base.set' && (
        <>
          {(() => {
            const rows: Array<{ id?: string; name: string; value: string; type: string }> =
              Array.isArray(params.assignments?.assignments)
                ? params.assignments.assignments.map((a: any) => ({
                    id: a.id,
                    name: String(a.name ?? ''),
                    value: String(a.value ?? ''),
                    type: String(a.type ?? 'string'),
                  }))
                : [];
            const setRows = (next: typeof rows) => {
              setParam('assignments', { ...params.assignments, assignments: next });
            };
            return (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Field Assignments</Typography>
                <Stack spacing={1} sx={{ mb: 1 }}>
                  {rows.map((row, idx) => (
                    <Box key={`set-${idx}`} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
                      <Box display="flex" gap={1} alignItems="center" sx={{ mb: 1 }}>
                        <TextField
                          label="Field Name"
                          size="small"
                          fullWidth
                          value={row.name}
                          onChange={(e) => {
                            const next = [...rows];
                            next[idx] = { ...row, name: e.target.value };
                            setRows(next);
                          }}
                          disabled={readOnly}
                        />
                        <FormControl size="small" sx={{ minWidth: 100 }}>
                          <InputLabel id={`set-type-${idx}`}>Type</InputLabel>
                          <Select
                            labelId={`set-type-${idx}`}
                            label="Type"
                            value={row.type}
                            onChange={(e) => {
                              const next = [...rows];
                              next[idx] = { ...row, type: e.target.value };
                              setRows(next);
                            }}
                            disabled={readOnly}
                          >
                            {['string', 'number', 'boolean', 'object', 'array'].map((t) => (
                              <MenuItem key={t} value={t}>{t}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <IconButton
                          aria-label="remove"
                          onClick={() => setRows(rows.filter((_, i) => i !== idx))}
                          disabled={readOnly}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                      <TextField
                        label="Value / Expression"
                        size="small"
                        fullWidth
                        value={row.value}
                        onChange={(e) => {
                          const next = [...rows];
                          next[idx] = { ...row, value: e.target.value };
                          setRows(next);
                        }}
                        helperText="Supports n8n expressions like ={{ $json.fieldName }}"
                        disabled={readOnly}
                      />
                    </Box>
                  ))}
                </Stack>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setRows([...rows, { name: '', value: '', type: 'string' }])}
                  disabled={readOnly}
                >
                  Add field
                </Button>
              </Box>
            );
          })()}
          <Divider sx={{ my: 2 }} />
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="set-include-label">Include other fields</InputLabel>
            <Select
              labelId="set-include-label"
              label="Include other fields"
              value={params.include ?? 'none'}
              onChange={(e) => setParam('include', e.target.value)}
              disabled={readOnly}
            >
              <MenuItem value="none">No other fields</MenuItem>
              <MenuItem value="all">All fields</MenuItem>
              <MenuItem value="selected">Selected fields</MenuItem>
              <MenuItem value="except">All except selected</MenuItem>
            </Select>
          </FormControl>
          {(params.include === 'selected' || params.include === 'except') && (
            <TextField
              label="Fields (comma-separated)"
              size="small"
              fullWidth
              value={params.includeFields ?? ''}
              onChange={(e) => setParam('includeFields', e.target.value)}
              sx={{ mb: 2 }}
              helperText="e.g. fieldA, fieldB, fieldC"
              disabled={readOnly}
            />
          )}
        </>
      )}

      {/* n8n Evaluation Trigger */}
      {nodeType === 'n8n-nodes-base.evaluationTrigger' && (
        <>
          <TextField
            label="Data Table ID"
            size="small"
            fullWidth
            value={getRlValue(params.dataTableId)}
            onChange={(e) => setParam('dataTableId', setRlValue(params.dataTableId, e.target.value))}
            sx={{ mb: 1 }}
            helperText={params.dataTableId?.cachedResultName ? `Table: ${params.dataTableId.cachedResultName}` : 'n8n data table ID'}
            disabled={readOnly}
          />
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(params.filterRows ?? false)}
                onChange={(e) => setParam('filterRows', e.target.checked)}
                disabled={readOnly}
              />
            }
            label="Filter rows"
            sx={{ mb: 2 }}
          />
          {params.filterRows && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Filter Conditions</Typography>
              {(() => {
                const conditions: Array<{ keyName: string; keyValue: string }> =
                  Array.isArray(params.filters?.conditions)
                    ? params.filters.conditions.map((c: any) => ({
                        keyName: String(c.keyName ?? ''),
                        keyValue: String(c.keyValue ?? ''),
                      }))
                    : [];
                const setConds = (next: typeof conditions) =>
                  setParam('filters', { ...params.filters, conditions: next });
                return (
                  <Stack spacing={1}>
                    {conditions.map((cond, idx) => (
                      <Box key={`cond-${idx}`} display="flex" gap={1} alignItems="center">
                        <TextField
                          label="Column"
                          size="small"
                          fullWidth
                          value={cond.keyName}
                          onChange={(e) => {
                            const next = [...conditions];
                            next[idx] = { ...cond, keyName: e.target.value };
                            setConds(next);
                          }}
                          disabled={readOnly}
                        />
                        <TextField
                          label="Value"
                          size="small"
                          fullWidth
                          value={cond.keyValue}
                          onChange={(e) => {
                            const next = [...conditions];
                            next[idx] = { ...cond, keyValue: e.target.value };
                            setConds(next);
                          }}
                          disabled={readOnly}
                        />
                        <IconButton
                          aria-label="remove"
                          onClick={() => setConds(conditions.filter((_, i) => i !== idx))}
                          disabled={readOnly}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    ))}
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => setConds([...conditions, { keyName: '', keyValue: '' }])}
                      disabled={readOnly}
                    >
                      Add condition
                    </Button>
                  </Stack>
                );
              })()}
            </Box>
          )}
        </>
      )}

      {/* n8n Evaluation (Set Outputs / Set Metrics) */}
      {nodeType === 'n8n-nodes-base.evaluation' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="eval-op-label">Operation</InputLabel>
            <Select
              labelId="eval-op-label"
              label="Operation"
              value={params.operation ?? 'setOutputs'}
              onChange={(e) => setParam('operation', e.target.value)}
              disabled={readOnly}
            >
              <MenuItem value="setOutputs">Set Evaluation Outputs</MenuItem>
              <MenuItem value="setMetrics">Set Metrics (LLM-judged)</MenuItem>
            </Select>
          </FormControl>

          {/* setOutputs mode */}
          {(!params.operation || params.operation === 'setOutputs') && (
            <>
              <TextField
                label="Data Table ID"
                size="small"
                fullWidth
                value={getRlValue(params.dataTableId)}
                onChange={(e) => setParam('dataTableId', setRlValue(params.dataTableId, e.target.value))}
                sx={{ mb: 1 }}
                helperText={params.dataTableId?.cachedResultName ? `Table: ${params.dataTableId.cachedResultName}` : 'n8n data table ID'}
                disabled={readOnly}
              />
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Output Mappings</Typography>
                {(() => {
                  const outputs: Array<{ outputName: string; outputValue: string }> =
                    Array.isArray(params.outputs?.values)
                      ? params.outputs.values.map((o: any) => ({
                          outputName: String(o.outputName ?? ''),
                          outputValue: String(o.outputValue ?? ''),
                        }))
                      : [];
                  const setOutputs = (next: typeof outputs) =>
                    setParam('outputs', { ...params.outputs, values: next });
                  return (
                    <Stack spacing={1}>
                      {outputs.map((out, idx) => (
                        <Box key={`out-${idx}`} display="flex" gap={1} alignItems="center">
                          <TextField
                            label="Output Field"
                            size="small"
                            fullWidth
                            value={out.outputName}
                            onChange={(e) => {
                              const next = [...outputs];
                              next[idx] = { ...out, outputName: e.target.value };
                              setOutputs(next);
                            }}
                            disabled={readOnly}
                          />
                          <TextField
                            label="Value / Expression"
                            size="small"
                            fullWidth
                            value={out.outputValue}
                            onChange={(e) => {
                              const next = [...outputs];
                              next[idx] = { ...out, outputValue: e.target.value };
                              setOutputs(next);
                            }}
                            helperText="e.g. ={{ $json.output }}"
                            disabled={readOnly}
                          />
                          <IconButton
                            aria-label="remove"
                            onClick={() => setOutputs(outputs.filter((_, i) => i !== idx))}
                            disabled={readOnly}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      ))}
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => setOutputs([...outputs, { outputName: '', outputValue: '' }])}
                        disabled={readOnly}
                      >
                        Add output
                      </Button>
                    </Stack>
                  );
                })()}
              </Box>
            </>
          )}

          {/* setMetrics mode */}
          {params.operation === 'setMetrics' && (
            <>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel id="eval-metric-label">Metric Type</InputLabel>
                <Select
                  labelId="eval-metric-label"
                  label="Metric Type"
                  value={params.metric ?? 'correctness'}
                  onChange={(e) => setParam('metric', e.target.value)}
                  disabled={readOnly}
                >
                  <MenuItem value="correctness">Correctness (vs expected answer)</MenuItem>
                  <MenuItem value="helpfulness">Helpfulness (vs user query)</MenuItem>
                  <MenuItem value="custom">Custom</MenuItem>
                </Select>
              </FormControl>
              {(params.metric === 'correctness' || !params.metric) && (
                <TextField
                  label="Expected Answer"
                  size="small"
                  fullWidth
                  value={params.expectedAnswer ?? ''}
                  onChange={(e) => setParam('expectedAnswer', e.target.value)}
                  sx={{ mb: 2 }}
                  helperText="Expression pointing to ground truth, e.g. ={{ $('Evaluation').item.json.expected_answer }}"
                  disabled={readOnly}
                />
              )}
              {params.metric === 'helpfulness' && (
                <TextField
                  label="User Query"
                  size="small"
                  fullWidth
                  value={params.userQuery ?? ''}
                  onChange={(e) => setParam('userQuery', e.target.value)}
                  sx={{ mb: 2 }}
                  helperText="Expression pointing to the user's question, e.g. ={{ $('Evaluation').item.json.question }}"
                  disabled={readOnly}
                />
              )}
              <TextField
                label="Actual Answer"
                size="small"
                fullWidth
                value={params.actualAnswer ?? ''}
                onChange={(e) => setParam('actualAnswer', e.target.value)}
                sx={{ mb: 2 }}
                helperText="Expression pointing to the AI's answer, e.g. ={{ $json.output }}"
                disabled={readOnly}
              />
              <TextField
                label="Evaluation Prompt"
                fullWidth
                multiline
                minRows={6}
                value={params.prompt ?? ''}
                onChange={(e) => setParam('prompt', e.target.value)}
                sx={{ mb: 2 }}
                helperText="The LLM judge prompt (leave as-is to use n8n's default scoring rubric)"
                disabled={readOnly}
              />
            </>
          )}
        </>
      )}

      {/* n8n Data Table */}
      {nodeType === 'n8n-nodes-base.dataTable' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="dt-op-label">Operation</InputLabel>
            <Select
              labelId="dt-op-label"
              label="Operation"
              value={params.operation ?? 'get'}
              onChange={(e) => setParam('operation', e.target.value)}
              disabled={readOnly}
            >
              <MenuItem value="get">Get Rows</MenuItem>
              <MenuItem value="create">Create Row</MenuItem>
              <MenuItem value="update">Update Row</MenuItem>
              <MenuItem value="delete">Delete Row</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Data Table ID"
            size="small"
            fullWidth
            value={getRlValue(params.dataTableId)}
            onChange={(e) => setParam('dataTableId', setRlValue(params.dataTableId, e.target.value))}
            sx={{ mb: 1 }}
            helperText={params.dataTableId?.cachedResultName ? `Table: ${params.dataTableId.cachedResultName}` : 'n8n data table ID'}
            disabled={readOnly}
          />
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(params.returnAll ?? false)}
                onChange={(e) => setParam('returnAll', e.target.checked)}
                disabled={readOnly}
              />
            }
            label="Return all rows"
            sx={{ mb: 2 }}
          />
          {!params.returnAll && (
            <TextField
              label="Limit"
              size="small"
              fullWidth
              type="number"
              value={params.limit ?? 50}
              onChange={(e) => setParam('limit', Number(e.target.value))}
              sx={{ mb: 2 }}
              disabled={readOnly}
            />
          )}
        </>
      )}

      {/* Core: Delay */}
      {nodeType === 'core.delay' && (
        <TextField
          label="Seconds"
          size="small"
          type="number"
          fullWidth
          value={params.seconds ?? 5}
          onChange={(e) => setParam('seconds', Number(e.target.value))}
          sx={{ mb: 2 }}
        />
      )}

      {/* Core: If */}
      {nodeType === 'core.if' && (() => {
        const OPERATORS: Array<{ value: string; label: string }> = [
          { value: 'equals', label: 'Equals (==)' },
          { value: 'not_equals', label: 'Not Equals (!=)' },
          { value: 'gt', label: 'Greater Than (>)' },
          { value: 'gte', label: 'Greater Than or Equal (>=)' },
          { value: 'lt', label: 'Less Than (<)' },
          { value: 'lte', label: 'Less Than or Equal (<=)' },
          { value: 'contains', label: 'Contains' },
          { value: 'not_contains', label: 'Not Contains' },
          { value: 'is_empty', label: 'Is Empty' },
          { value: 'is_not_empty', label: 'Is Not Empty' },
          { value: 'starts_with', label: 'Starts With' },
          { value: 'ends_with', label: 'Ends With' },
          { value: 'regex', label: 'Regex Match' },
        ];

        // Read conditions from the new shape first, falling back to the
        // legacy single-condition shape so existing nodes still open.
        const rawList = (params as any)?.conditions;
        let initial: Array<{ value1: string; operator: string; value2: string }> = [];
        let nestedCombinator: string | undefined;
        if (Array.isArray(rawList)) {
          initial = rawList.map((c: any) => ({
            value1: c?.value1 ?? c?.leftValue ?? '',
            operator: c?.operator ?? c?.operation ?? 'equals',
            value2: c?.value2 ?? c?.rightValue ?? '',
          }));
        } else if (rawList && typeof rawList === 'object') {
          // n8n-style nested {conditions:[...], combinator}
          nestedCombinator = rawList.combinator;
          const inner = Array.isArray(rawList.conditions) ? rawList.conditions : [];
          initial = inner.map((c: any) => ({
            value1: c?.value1 ?? c?.leftValue ?? '',
            operator: c?.operator ?? c?.operation ?? 'equals',
            value2: c?.value2 ?? c?.rightValue ?? '',
          }));
        }
        if (!initial.length && (params as any)?.condition) {
          const c = (params as any).condition;
          initial = [{
            value1: c?.value1 ?? '',
            operator: c?.operator ?? 'equals',
            value2: c?.value2 ?? '',
          }];
        }
        if (!initial.length) {
          initial = [{ value1: '', operator: 'equals', value2: '' }];
        }

        const combinator = String(
          (params as any)?.combinator ?? (params as any)?.match ?? nestedCombinator ?? 'and'
        ).toLowerCase() === 'or' ? 'or' : 'and';

        const writeBack = (
          next: Array<{ value1: string; operator: string; value2: string }>,
          nextCombinator: 'and' | 'or' = combinator,
        ) => {
          setParam('conditions', next);
          setParam('combinator', nextCombinator);
          // Keep legacy `condition` aligned with the first row for back-compat.
          setParam('condition', next[0] ?? { value1: '', operator: 'equals', value2: '' });
        };

        return (
          <>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel id="if-combinator-label">Match</InputLabel>
              <Select
                labelId="if-combinator-label"
                label="Match"
                value={combinator}
                onChange={(e) => writeBack(initial, e.target.value as 'and' | 'or')}
              >
                <MenuItem value="and">ALL conditions (AND)</MenuItem>
                <MenuItem value="or">ANY condition (OR)</MenuItem>
              </Select>
            </FormControl>

            {initial.map((cond, idx) => (
              <Box
                key={idx}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2,
                  mb: 2,
                  position: 'relative',
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Condition {idx + 1}
                  </Typography>
                  {initial.length > 1 && (
                    <IconButton
                      size="small"
                      onClick={() => writeBack(initial.filter((_, i) => i !== idx))}
                      aria-label="Remove condition"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Stack>
                <TextField
                  label="Value 1"
                  size="small"
                  fullWidth
                  value={cond.value1}
                  onChange={(e) => {
                    const next = [...initial];
                    next[idx] = { ...next[idx], value1: e.target.value };
                    writeBack(next);
                  }}
                  sx={{ mb: 2 }}
                />
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel id={`if-operator-label-${idx}`}>Operator</InputLabel>
                  <Select
                    labelId={`if-operator-label-${idx}`}
                    label="Operator"
                    value={cond.operator}
                    onChange={(e) => {
                      const next = [...initial];
                      next[idx] = { ...next[idx], operator: String(e.target.value) };
                      writeBack(next);
                    }}
                  >
                    {OPERATORS.map((op) => (
                      <MenuItem key={op.value} value={op.value}>{op.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Value 2"
                  size="small"
                  fullWidth
                  value={cond.value2}
                  onChange={(e) => {
                    const next = [...initial];
                    next[idx] = { ...next[idx], value2: e.target.value };
                    writeBack(next);
                  }}
                />
              </Box>
            ))}

            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => writeBack([...initial, { value1: '', operator: 'equals', value2: '' }])}
              sx={{ mb: 2 }}
            >
              Add condition
            </Button>
          </>
        );
      })()}

      {/* Core: Switch */}
      {nodeType === 'core.switch' && (
        <>
          <TextField
            label="Expression"
            size="small"
            fullWidth
            value={params.expression ?? ''}
            onChange={(e) => setParam('expression', e.target.value)}
            sx={{ mb: 2 }}
          />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Cases
          </Typography>
          <Stack spacing={1} sx={{ mb: 2 }}>
            {switchCases.map((c, idx) => (
              <Box key={`case-${idx}`} display="flex" gap={1} alignItems="center">
                <TextField
                  label={`Value ${idx + 1}`}
                  size="small"
                  fullWidth
                  value={c.value}
                  onChange={(e) => {
                    const next = [...switchCases];
                    next[idx] = { value: e.target.value };
                    setSwitchCases(next);
                    setParam('cases', next.map((item) => ({ value: item.value })));
                  }}
                  disabled={readOnly}
                />
                <IconButton
                  aria-label="remove"
                  onClick={() => {
                    const next = switchCases.filter((_, i) => i !== idx);
                    setSwitchCases(next);
                    setParam('cases', next.map((item) => ({ value: item.value })));
                  }}
                  disabled={readOnly}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                const next = [...switchCases, { value: '' }];
                setSwitchCases(next);
                setParam('cases', next.map((item) => ({ value: item.value })));
              }}
              disabled={readOnly}
            >
              Add case
            </Button>
          </Stack>
          <TextField
            label="Default"
            size="small"
            fullWidth
            value={params.default ?? ''}
            onChange={(e) => setParam('default', e.target.value)}
            sx={{ mb: 2 }}
          />
        </>
      )}

      {/* Core: Code */}
      {nodeType === 'core.code' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="code-language-label">Language</InputLabel>
            <Select
              labelId="code-language-label"
              label="Language"
              value={params.language ?? 'python'}
              onChange={(e) => {
                const next = e.target.value as string;
                const prev = (params.language ?? 'python') as string;
                setParam('language', next);
                // If the user is just switching languages and the body is
                // still the boilerplate from the OTHER language, swap it for
                // the equivalent boilerplate so they aren't staring at code
                // that won't compile. We only auto-swap when we recognise
                // the previous default; bespoke code is left untouched.
                const PY_DEFAULT = 'output = {"result": input.get("value")}';
                const JS_DEFAULT = 'msg.payload = msg.payload;\nreturn msg;';
                const current = (params.code ?? '').toString().trim();
                if (next === 'javascript' && (current === '' || current === PY_DEFAULT)) {
                  setParam('code', JS_DEFAULT);
                } else if (next === 'python' && (current === '' || current === JS_DEFAULT)) {
                  setParam('code', PY_DEFAULT);
                }
                void prev;
              }}
            >
              <MenuItem value="python">Python</MenuItem>
              <MenuItem value="javascript">JavaScript (Node-RED compatible)</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Code"
            fullWidth
            multiline
            minRows={6}
            value={params.code ?? ''}
            onChange={(e) => setParam('code', e.target.value)}
            sx={{ mb: 2, '& textarea': { fontFamily: 'monospace', fontSize: 13 } }}
            helperText={
              (params.language ?? 'python') === 'javascript'
                ? 'Node-RED-style: `msg` is the input object. Mutate fields and `return msg;` (or return null to drop). Runs in a sandboxed `node` subprocess with a 10s timeout.'
                : 'Use `input` (or `data`) for the incoming dict and assign your result to `output`. Allowed imports: base64, email, json, re, html, urllib.parse, hashlib, datetime, etc.'
            }
          />
        </>
      )}

      {/* Tool: Parse Email */}
      {nodeType === 'tool.parse_email' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="parse-email-source-label">Source format</InputLabel>
            <Select
              labelId="parse-email-source-label"
              label="Source format"
              value={params.source ?? 'auto'}
              onChange={(e) => setParam('source', e.target.value)}
            >
              <MenuItem value="auto">Auto-detect (recommended)</MenuItem>
              <MenuItem value="gmail">Gmail API message (JSON)</MenuItem>
              <MenuItem value="raw">Raw RFC-822 (string or base64url)</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Source path (optional)"
            size="small"
            fullWidth
            value={params.source_path ?? ''}
            onChange={(e) => setParam('source_path', e.target.value)}
            sx={{ mb: 2 }}
            helperText="Leave empty to auto-detect. The node looks one level deep into common wrapper keys (body, data, response, result, message, output, email) and picks the nested email automatically."
          />
          <FormControlLabel
            control={
              <Switch
                checked={params.include_text !== false}
                onChange={(e) => setParam('include_text', e.target.checked)}
              />
            }
            label="Include plain-text body"
            sx={{ mb: 1, display: 'block' }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={params.include_html !== false}
                onChange={(e) => setParam('include_html', e.target.checked)}
              />
            }
            label="Include HTML body"
            sx={{ mb: 1, display: 'block' }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={!!params.decode_attachments}
                onChange={(e) => setParam('decode_attachments', e.target.checked)}
              />
            }
            label="Inline attachment bytes (base64) — usually leave OFF and use attachment_id"
            sx={{ mb: 1, display: 'block' }}
          />
          <TextField
            label="Max text body chars (0 = unlimited)"
            size="small"
            type="number"
            fullWidth
            value={params.max_text_chars ?? 0}
            onChange={(e) => setParam('max_text_chars', Number(e.target.value))}
            sx={{ mb: 2 }}
          />
        </>
      )}

      {/* Core: Split */}
      {nodeType === 'core.split' && (
        <>
          <TextField
            label="Source Path"
            size="small"
            fullWidth
            value={params.source_path ?? ''}
            onChange={(e) => setParam('source_path', e.target.value)}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="split-mode-label">Mode</InputLabel>
            <Select
              labelId="split-mode-label"
              label="Mode"
              value={params.mode ?? 'by_items'}
              onChange={(e) => setParam('mode', e.target.value)}
            >
              <MenuItem value="by_items">by_items</MenuItem>
              <MenuItem value="by_delimiter">by_delimiter</MenuItem>
              <MenuItem value="by_chunk">by_chunk</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Delimiter"
            size="small"
            fullWidth
            value={params.delimiter ?? ','}
            onChange={(e) => setParam('delimiter', e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Chunk Size"
            size="small"
            type="number"
            fullWidth
            value={params.chunk_size ?? 0}
            onChange={(e) => setParam('chunk_size', Number(e.target.value))}
            sx={{ mb: 2 }}
          />
        </>
      )}

      {/* Core: Aggregate */}
      {nodeType === 'core.aggregate' && (
        <>
          <TextField
            label="Source Path"
            size="small"
            fullWidth
            value={params.source_path ?? ''}
            onChange={(e) => setParam('source_path', e.target.value)}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="aggregate-operation-label">Operation</InputLabel>
            <Select
              labelId="aggregate-operation-label"
              label="Operation"
              value={params.operation ?? 'count'}
              onChange={(e) => setParam('operation', e.target.value)}
            >
              {['count', 'sum', 'avg', 'min', 'max'].map((op) => (
                <MenuItem key={op} value={op}>
                  {op}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Field (optional)"
            size="small"
            fullWidth
            value={params.field ?? ''}
            onChange={(e) => setParam('field', e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Group By (optional)"
            size="small"
            fullWidth
            value={params.group_by ?? ''}
            onChange={(e) => setParam('group_by', e.target.value)}
            sx={{ mb: 2 }}
          />
        </>
      )}

      {/* Core: Global Constants */}
      {nodeType === 'core.constants' && (
        <>
          {renderKeyValueEditor('constants.values', 'Values')}
        </>
      )}

      {/* Core: Set Variable */}
      {nodeType === 'core.set' && (
        <>
          {renderKeyValueEditor('set.values', 'Values')}
        </>
      )}

      {/* Core: Unsupported (imported) */}
      {nodeType === 'core.unsupported' && (
        <TextField
          label="Imported node data (read-only)"
          fullWidth
          multiline
          minRows={10}
          value={unsupportedText}
          InputProps={{ readOnly: true }}
        />
      )}

      {/*
        Generic fallback for imported n8n / langchain nodes that don't have a
        bespoke UI panel above. This catches things like n8n-nodes-base.code,
        .httpRequest, .if, .switch, .merge, .splitOut, .limit, .aggregate,
        .scheduleTrigger, .respondToWebhook (raw), and any langchain node we
        haven't given a hand-rolled inspector. The compatibility executor on
        the backend keeps reading the params by their original n8n names, so
        editing here is fully round-trip-safe.
      */}
      {!isKnownEditorType &&
        (nodeType.startsWith('n8n-nodes-base.') || nodeType.startsWith('@n8n/')) && (
          <GenericParamsEditor
            nodeType={nodeType}
            params={params}
            setParam={setParam}
            readOnly={readOnly}
          />
        )}
      </Box>
    </Box>
  );
};


